import { state } from "../state";
import type { ScreenContext } from "../app";
import { inLocationTopSpeed } from "../../inLocationSpeed";
import {
  GATE_RADIUS_M,
  MINING_LASER_RANGE_M,
  PLANET_RADIUS_M,
  SHIP_RADIUS_M,
  SHIP_TO_ASTEROID_CLEARANCE_M,
  SHIP_TO_SHIP_CLEARANCE_M,
  STATION_RADIUS_M,
  drawRadiusPx,
  fixedObjectRadiusM
} from "../../worldScale";
import {
  checkDocking,
  guardDisposition,
  guardNpcFaction,
  guardsAttackOnSight,
  stationGuardFaction,
  stationGuardCount,
  type GalaxyRegion
} from "../../stationGuards";
import {
  orbitPointPosition,
  stepBrake,
  stepCruise,
  stepFlee,
  stepOrbitPoint,
  stepTowardTarget,
  type FleeStage,
  type Kinematics,
  type OrbitPoint,
  type Point
} from "../../shipMovement";
import {
  IDLE_SELF_MODULE,
  IDLE_WEAPON,
  lockArmedWeapon,
  tapSelfModule,
  tapWeapon,
  tickSelfModuleCycle,
  tickWeaponCycle,
  type SelfModuleCycleState,
  type WeaponCycleState
} from "../../moduleCycle";
import { rangeHitChance, rollDamage, rollDamageWithHitChance } from "../../combatResolutionLoop";
import { HIT_CHANCE_CURVES, WAVE_GAP_SECONDS, type HitChanceCurve } from "../../combatConstants";
import { loadEncounters, loadUnits } from "../../data/loadEncounters";
import { parseCompositionUnits, parseStageSegments } from "../../encounterComposition";
import { dangerTierRichnessMultiplier, effectiveYieldRate, nodePoolM3 } from "../../miningYield";
import { volumePerUnitM3 } from "../../resourceCatalog";
import { formatEndurance, fuelHeadline, fuelSummary, provisionsSummary } from "../../endurance";
import { travelCostLabel, travelCostPreview, type TravelCostPreview, type TravelKind } from "../../travelCost";
import { createLabelWriter } from "../labelWriter";
import { REPAIR_ORE, drawRepair, ferritePerCycle } from "../../repairEconomy";
import { damageFor, recordCurrentHp } from "../../shipDamage";
import { HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER, guardianName, recordKill, recordSighting } from "../../bestiary";
import { HIDDEN_SYSTEM_LORE, loreForSystem } from "../../loreCodex";
import { HIDDEN_SYSTEM_IDS, hiddenNeighborsOf } from "../../galaxyVisibility";
import { playSound } from "../../audio";
import { navIconsHtml, wireNavIcons } from "../navIcons";
import { COMBAT_SNAPSHOT_VERSION, canRestoreCombat, survivingHostiles, type HostileSnapshot } from "../../combatSnapshot";
import { ASTEROID_CLEARANCE_M, SHIP_SEPARATION_M, resolveSeparation, type SeparationBody } from "../../shipSeparation";
import { beginScreenTransition, endScreenTransition, isScreenTransitioning } from "../transitionGate";
import { addEngagementSeconds } from "../../captainSkills";
import { dockKey, podIsImmune, POD_IN_LOCATION_MPS, STATION_LOCATION_ID } from "../../shipStorage";
// drawHullShip moved to shipCanvasIcon.ts as drawShipShape (16.82) so the
// Trophy Room draws the same real silhouettes this view does; aliased on
// import rather than renamed at 3 call sites for a no-op diff.
import { drawShipShape as drawHullShip, npcShipShape, playerShipShape, npcSubtypeKey } from "../shipCanvasIcon";
import { openBlueprint, type BlueprintSpec } from "../shipBlueprint";
import { canvasPointFromEvent } from "../responsiveStage";
import {
  loanerHullAtRisk,
  loanerHullWarningTier,
  loanerWarningMessage,
  runLoanerHullMutiny
} from "../../mutiny";
import {
  nextRandomAttackDelayMs,
  planetIsDefended,
  PLANETARY_DEFENSE_LOCK_MS,
  PLANETARY_DEFENSE_RANGE_M,
  systemAllowsRandomAttack
} from "../../planetaryDefenses";
import { settings } from "../../settings";
import { seededUnit } from "../../seededRandom";
import { triggerSave } from "../persistence";
import { fastTravelFuelCost, warpFuelCost } from "../../fuel";
import { effectiveNow } from "../../gameClock";
import { marketPricePerUnit } from "../../data/marketItems";
import {
  activateDistressSignal,
  cancelDistressSignal,
  tickDistressSignal
} from "../../distressSignal";
import { nodeRegenKey, readNodePool, upsertNodeRegen } from "../../resourceNodeRegen";
import {
  adjustStanding,
  applyMissionCompletionStanding,
  shipKillStandingPenalty,
  type FactionName
} from "../../factionStanding";
import { DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT, DRONE_HEAL_SECONDS, DRONE_STATS, droneBayCapacity, droneComplement, droneEntryForHull, loadableDroneCount, shipHasDefaultDroneBay } from "../../droneSystem";
import { getSystem, loadSystems } from "../../data/loadSystems";
import { shortestRoute } from "../../haulingMissions";
import { itemsByCategory } from "../../data/marketItems";
import {
  depositMining,
  freeCargoM3,
  freeOreHoldM3,
  type AcceptedCombatMission,
  type AcceptedMiningMission
} from "../../acceptedMissions";
import type { NpcFaction, NpcHullTier, StarSystem } from "../../types";
import { createBase } from "../../baseStorage";
import { WEAPON_LEVEL_MULTIPLIER, resetShipUpgrades, weaponLevel, weaponMultiplier, type WeaponKey } from "../../weaponUpgrades";

const allSystems = loadSystems();

// Expedition View -- design doc Sections 2a/4a/4b (LOCKED wireframe),
// ported directly from the tested interactive prototype
// (mockups/starforge_combat_mockup_v2.html) rather than re-derived from
// prose -- per CLAUDE.md, that mockup's visual/interaction language is the
// source of truth for exact values. Movement, camera/zoom, the Fast
// Travel sequence, module tap-to-target/cycle behavior, and now a real
// combat tick (Section 4a/16.30) are live. The combat tick reuses the
// same real, tested pieces the rest of the game already relies on --
// combatResolutionLoop.ts's rollDamage() (so hit chance/damage variance
// are the actual locked HIT_CHANCE/DAMAGE_VARIANCE_MIN/MAX, not
// reinvented), and a real Wave encounter (PT1-013, src/data/encounters.json,
// parsed with the same encounterComposition.ts functions initCombat()
// uses) instead of the mockup's fake single "Micro Pirate 420 HP, 38 DPS"
// placeholder -- multi-wave, with the real 15s recovery gap
// (WAVE_GAP_SECONDS) and its passive shield-regen exception between
// waves. combatResolutionLoop.ts's own tick() function itself still isn't
// reused directly, though -- it models one global per-second tick for the
// whole ship (Section 16.30's pre-revision shape), not per-module
// independent cycles, so damage here is instead triggered per-weapon at
// the exact moment each one's own cycle completes (see moduleCycle.ts).
// Mining Laser doesn't deal damage at all -- it's "explicitly not a
// weapon" (Section 9), so it extracts real m3/s (miningYield.ts,
// effectiveYieldRate) from a locked asteroid straight into state.cargo,
// the actual persistent save-state cargo, capped by real remaining ship
// cargo room and the node's own finite pool (16.25) -- saved on exit.
//
// There is still no per-ship module fitting system in the game data
// (PlayerShip only has aggregate dps/hullHp/shieldHp), so the Docked
// Module Rack below assumes an illustrative "everything fitted" loadout
// (all four Hardpoint types) -- not real fitted data, and per-weapon DPS
// is therefore illustrative too (no way to split a ship's one real `dps`
// stat across four illustrative weapon types). Per-module cycle durations
// are likewise illustrative (mockup values for Shield/Laser/Auto;
// invented, flagged, reasonable-feeling values for Hull/Mining/Missile,
// since no cycle-duration numbers are locked anywhere in the design doc).
// Weapon RANGES, however, are real (Section 2a's locked range-band
// table), and the player's max HP/regen rates are the real selected
// ship's own stats where non-zero (falling back to an illustrative value
// for ships like the Digger that genuinely have no shield/repair fitted
// in the real data -- otherwise the "everything fitted" rack would show
// a shield module that can never do anything).
//
// Fleet-in-combat (16.43, replaces the old single hardcoded "Escort --
// example" test entity): every fleet ship (state.fleet) with stance
// "Active", other than whichever one is being flown, joins the scene as a
// real ship -- its own real hull/shield/dps/repair/cargo from PlayerShip,
// not illustrative placeholders. Both hostiles and non-flown fleet ships
// now have real pursuit AI: a detection range and weapon range by hull
// tier (reusing the real player weapon ranges from WEAPON_SHELLS below as
// anchors, since no NPC range numbers are locked anywhere), aggro onto
// the nearest in-range candidate, close the distance, and auto-fire once
// actually in weapon range. NPC top speed is 90% of the comparable
// player-ship class's own real top speed (inLocationSpeed.ts) -- reusing
// the real player speed table rather than inventing a separate NPC one.
// Fleet ships auto-flee at 10% hull per Section 10, same as before, and
// once fled are gone for the rest of this session, not just off-camera.
// Fast Travel to a same-System Location (Planet/Station/Belt/Mining/Combat
// rows) just resets this same scene (fresh undiscovered signatures,
// refilled asteroid pool, and every fleet ship's position/aggro -- but
// NOT its HP or fled status) -- there's still no per-System Location
// roster in the data model (Section 6 only gives qualitative rules), so
// those rows don't lead to genuinely different content yet. A Jump Gate
// row is different: it's a real
// inter-System jump (getSystem(), src/data/systems.json's 107 real
// systems), and does change state.currentSystemId (+ re-derives the
// belt's resource/pool from the new System's own data) and save on
// arrival.
//
// Drone System (Section 16.23) is real (capacity/EHP/DPS by hull tier,
// the 90/10 ship-vs-drone targeting weight, 45s gap-only heal), gated on
// shipHasDefaultDroneBay() so it only shows for a Digger/Packmule. There
// is no UI for it specified anywhere in the doc at all (no rack circle,
// no panel, no per-drone health bar) -- it's added as a 3rd Fleet Health
// Box row with an aggregate bay-wide HP bar, and correspondingly drones
// aren't drawn on the canvas either (no visual sprite is specified any
// more than a health bar is) -- purely a stat/targeting mechanic here,
// not a rendered entity.
//
// Also newly real this pass: the Scanner UI's "In This Location" list
// used to be a static array completely disconnected from the fixedObjects
// the canvas/mini-map actually used (a real bug) -- it's now derived live
// from fixedObjects, and SCAN actually rolls each hidden object
// independently (Section 8's locked shape: probabilistic per-object
// reveal, not a flat cooldown). Section 8's other locked consequence -- a
// scan attempt risking triggering a hostile contact -- is now modeled too
// (see the "Ambient Hostile Contacts" paragraph further down).
// maxManualTargets (Section 4a: 1/2/5 by Small/Medium/Large
// hull) is now enforced when assigning a genuinely new target; Capital
// hull is left uncapped since the doc never defines a value for it.
// Module expand/minimize (Section 4a) is implemented too, with the
// trigger moved off the circle itself -- see rackModuleHtml's comment for
// why, and the doc's own supersession note next to Section 4a's
// Expand/minimize paragraph.
//
// Also newly real this pass: the Scanning UI large popup (Section 2a/8,
// distinct from the small always-visible Scanner UI panel) -- opened by
// tapping the small panel's own title label (no trigger element is
// specified in the doc; see the design doc's implementation-clarification
// note next to Section 4b's SCAN button bullet, which also covers why
// BOTH panels get a real, independently-working SCAN button rather than
// only one). The popup shows the same live Fast Travel rows as the small
// panel plus fuller per-object detail for discovered asteroids (real
// resource name + remaining pool %). The Scanner UI's "Fast Travel --
// This System" list itself is also real now: Planet/Station/Belt/Jump
// Gate rows are generated from the actual current System
// (getSystem(state.currentSystemId), src/data/systems.json) instead of
// an invented single "Kel-Var" scene, and the belt's own resource/pool
// size are similarly derived from that real System instead of a
// hardcoded "Ferrite Ore"/Danger Tier 0 stand-in. Mining/Combat encounter
// rows are still illustrative placeholders (no per-System encounter
// generator exists), just flavored with the real System's resource list.
//
// Two bugs fixed this pass, both real regressions rather than
// unimplemented spec: (1) the Mini-map and its full-screen popup drew
// nothing beyond the player dot -- their canvases used `inset: 0` alone
// in CSS, which doesn't stretch a replaced element like <canvas> the way
// it does a block element, so they silently stayed at the browser's
// intrinsic 300x150 default instead of their real 118x118/circular size
// (fixed by adding explicit width/height: 100% alongside inset, matching
// the main viewport canvas's own CSS). (2) The Scanner UI's "In This
// Location" rows were display-only -- clicking a discovered object did
// nothing, only Fast Travel rows had a click handler. Both direct-tap
// selection and Scanner UI row selection now route through one shared
// selectTarget() function, so clicking an object row selects it (Move
// To/Orbit enable, same as tapping it directly) with identical weapon
// lock-on behavior either way.
//
// Four more Section 8/16.1 mechanics added this pass:
//   1. **Wrecks from destroyed hostiles** -- a destroyed hostile leaves a
//      real Wreck fixedObject at its position ("aftermath of a triggered
//      encounter"), already discovered, selectable/Move-To-able like any
//      other object. No real per-wreck loot table exists yet, so it
//      carries no resource/pool the way an asteroid does.
//   2. **Ambient Hostile Contacts** (Section 8/16.1) -- every SCAN attempt
//      and every completed mining cycle now rolls the real per-Danger-Tier
//      trigger chance (Tier 1-4: 10/15/20/25%; Tier 0/Safe never triggers,
//      consistent with hub systems being genuinely safe) and, on a hit,
//      plays the doc's own staged warning sequence ("Hostiles detected in
//      the system" -> "We've been located" -> "They're closing in" ->
//      "Contact!", 3s apart) before spawning a real group (16.1's Ambient
//      group-size table) into the same hostiles array the scripted wave
//      uses. Unit stats reuse the real PT1-013 Pirate unit pool, with
//      Danger Tier mapped onto the closest hullTier (T1..T4) as an
//      illustrative proxy -- ship-size hullTier and System dangerTier are
//      two different axes nowhere unified in the real data model, so this
//      is not 16.1's literal HP/DPS curve.
//   3. **Selection ring on both mini-maps** -- previously only hostiles
//      got a highlight ring, and only on the full-screen popup. Now
//      generic to whatever's selected (hostile, object, or marker) and
//      shown on the small always-visible mini-map too, matching the main
//      viewport's own selection ring.
//   4. **Cargo-full banner** -- Section 11a explicitly leaves the exact
//      near-full/full cargo display treatment "not yet designed," but
//      silently stopping extraction with zero feedback was a real UX gap;
//      a one-time "Cargo hold full" banner (reset once there's room again)
//      is a reasonable, clearly-flagged stopgap rather than waiting on
//      that undecided spec.
//
// Extensions beyond the original mockup/design doc, added directly per
// Don's direction rather than derived from Section 4a/4b:
//   1. Tapping empty space (main viewport or the Mini-map popup) drops a
//      marker the captain can Move To/Orbit, so travel isn't limited to
//      objects that happen to be visible -- and it's cleaned up (removed)
//      the instant the ship actually arrives at it, rather than sitting
//      on the viewport/mini-map forever.
//   2. Tapping the Engine circle itself cruises in whatever heading the
//      ship already has, with no target needed at all -- and, if the ship
//      already has any speed (cruise, Move To, Orbit, or just coasting),
//      tapping it instead actively brakes to a stop at the same rate it
//      would normally accelerate, rather than switching into cruise. The
//      circle also now shows a solid green ring for as long as the ship
//      has any speed at all, independent of which mode got it moving.
//   3. The Mini-map is now live (real dots, matching the mockup's radar)
//      with a working full-screen popup, not the earlier static 3-dot
//      placeholder. Both mini-maps also show a selection ring around
//      whatever's currently selected (any object or a marker, not just
//      hostiles), matching the main viewport's own ring.
//   4. Move To/Orbit Near/Orbit Far all toggle off on a second tap while
//      already active, the same "tapping an active module deactivates
//      it" convention every rack circle already follows -- previously
//      these three buttons only ever turned a mode on.
//   5. Double-clicking a real object (main viewport or Mini-map popup)
//      selects it, Move To's it, and immediately activates whatever's
//      appropriate for what it is: every non-Mining weapon module for an
//      NPC ship, or the Mining Laser for an asteroid (same
//      maxManualTargets cap as the manual tap-module-then-target
//      gesture). Jump Gates are a new physical object in this pass too
//      (see buildGateObjects) -- one real one per the current System's
//      actual `connections`, always visible; double-clicking one Move
//      To's it and auto-jumps through once actually in range. This sits
//      alongside the Scanner UI's existing Fast Travel route to the same
//      destination, not replacing it -- Section 2a's own text frames Fast
//      Travel as System-scope and explicitly "not a Move To input," so a
//      literal in-Location gate object to fly up to is a deliberate
//      extension of that, not a literal reading of it.
// The module cycle's "tap-bonus" (tapping a completed cycle to restart it
// early) has been removed entirely, in the design doc and here -- it kept
// colliding with the tap-to-assign/deactivate gesture. See moduleCycle.ts.
//
// Shield now repairs immediately on activation (t=0), then again at each
// subsequent cycle completion as before -- since cycles run back-to-back,
// that's the same cadence as "the next repair occurs at the start of the
// next cycle," per Don's direction. Hull deliberately keeps its original
// end-of-cycle-only timing; only Shield gets the immediate first tick.
// Fixed a real adjacent bug while touching this: repairs previously only
// ever applied when targetId was "player" -- Shield/Hull targeting the
// escort (Section 4a: "can target the flagship or any other fleet ship")
// cycled visually but healed nobody. Both paths now share one
// applySelfModuleRepair() so player/escort and immediate/per-cycle timing
// can't drift apart again.

type ModuleType = "shield" | "hull" | "weapon" | "engine";

interface ModuleShell {
  key: string;
  type: ModuleType;
  letter: string;
  label: string;
  range?: number; // meters -- weapon modules only (Section 2a, LOCKED)
}

// Fixed order per Section 4a: Shield -- Hull -- [Mining Laser -> Laser
// Turret -> Auto-Turret -> Missile Turret, present ones only] -- Engine.
const SELF_MODULE_SHELLS: ModuleShell[] = [
  { key: "shield", type: "shield", letter: "S", label: "Shield" },
  { key: "hull", type: "hull", letter: "H", label: "Hull/Repair" }
];
const WEAPON_SHELLS: ModuleShell[] = [
  // 16.101 (Don): 100m -> 1400m, matching the Laser Turret. At 100m the Mining
  // Laser could not reach an asteroid the ship had flown to at all -- Move To
  // stopped at 150m and Orbit Near at 450m, both outside it -- so the module was
  // unusable by any normal approach.
  { key: "mining", type: "weapon", letter: "M", label: "Mining Laser", range: MINING_LASER_RANGE_M },
  { key: "laser", type: "weapon", letter: "L", label: "Laser Turret", range: 1400 },
  { key: "auto", type: "weapon", letter: "A", label: "Auto-Turret", range: 600 },
  { key: "missile", type: "weapon", letter: "MT", label: "Missile Turret", range: 3000 }
];
const ENGINE_SHELL: ModuleShell = { key: "engine", type: "engine", letter: "E", label: "Engine" };
const RACK_SHELLS: ModuleShell[] = [...SELF_MODULE_SHELLS, ...WEAPON_SHELLS];

// Illustrative cycle durations -- no per-module cycle-duration numbers are
// locked anywhere in the design doc. Shield/Laser/Auto match the tested
// mockup exactly; Hull/Mining/Missile are reasonable invented values
// (mockup never included those three) flagged here rather than presented
// as real.
// Post-fire cooldown per self-module (16.101, Don's ruling on 16.100's
// analysis). The shield module restored shieldRegenPerS x 4 every 4s, i.e.
// unlimited free healing at shieldRegenPerS HP/s -- 1.9x median incoming DPS at
// T1-T3 and up to 7.5x at T4. A 12s cooldown makes that one heal per 16s, a 4x
// cut, delivered as a lump the captain chooses when to spend rather than a drip
// that cannot be out-damaged.
//
// Hull repair gets none: it is already rate-limited by something real -- every
// cycle costs Ferrite out of the hold (16.80).
const SELF_MODULE_COOLDOWN_MS: Record<string, number> = {
  shield: 12000,
  hull: 0
};

const CYCLE_DURATION_MS: Record<string, number> = {
  shield: 4000,
  hull: 5000,
  mining: 3000,
  laser: 2200,
  auto: 1400,
  missile: 4500
};

// Illustrative per-weapon DPS -- there's no real per-Hardpoint-type
// breakdown anywhere (PlayerShip only has one aggregate `dps`), so these
// are flavor numbers only. Laser/Auto match the tested mockup exactly;
// Missile is invented (mockup never included it). Mining Laser has no
// entry here -- it uses a real m3/s extraction rate (miningYield.ts)
// instead of a damage figure; see extractFromAsteroid.
const WEAPON_DPS: Record<string, number> = {
  laser: 70,
  auto: 34,
  missile: 50
};

// Fallbacks for ships whose real data genuinely has no shield/repair
// fitted (e.g. the Digger, a pure mining hull with shieldHp: 0,
// repairPerS: 0) -- the rack's illustrative "everything fitted" loadout
// assumes a Shield/Hull module exists, so a real 0 would show a module
// that's on-screen but can never do anything. Any non-zero real value is
// used as-is.
const FALLBACK_SHIELD_MAX_HP = 1200;
const FALLBACK_SHIELD_REGEN_PER_S = 30;
const FALLBACK_HULL_REPAIR_PER_S = 40;

// Illustrative hostile combat cadence -- no real per-NPC cycle-duration
// number exists either (same gap as the player's own modules).
const HOSTILE_CYCLE_DURATION_MS = 3000;

/**
 * Bridge between the nav row and the live fight (16.99).
 *
 * The Save & Exit button is wired in renderExpeditionScreen, but the hostiles,
 * the banner and the arm flag all live inside setUpSpaceCanvas -- a separate
 * module-level function. Rather than hoist the whole combat scope, the canvas
 * publishes one predicate here: "I handled this press, do not exit yet."
 *
 * Reset on every mount, so a guard left over from a previous Expedition View
 * can never speak for a fight that is no longer happening.
 */
let combatExitGuard: (() => boolean) | null = null;

/**
 * The Fleet Health Box provisions line (16.104).
 *
 * Module scope rather than inside setUpSpaceCanvas, because the markup is built
 * in renderExpeditionScreen -- before the canvas scope exists -- and both need
 * the same sentence. One function, so the first paint and every frame after it
 * cannot disagree.
 */
function provisionsLineText(): string {
  if (!state.currentShip || state.isLoanerShip) return "";
  const prov = provisionsSummary(state.currentShip.crew, state.provisionsOnBoard, state.provisionsCapacity);
  return `Provisions: ${formatEndurance(prov.hoursLeft)} left`;
}

/** The warning state, seeded at first paint and re-applied every frame. */
function provisionsLineClass(): string {
  if (!state.currentShip || state.isLoanerShip) return "";
  const prov = provisionsSummary(state.currentShip.crew, state.provisionsOnBoard, state.provisionsCapacity);
  if (prov.onBoard <= 0) return "empty";
  return prov.low ? "low" : "";
}
export function renderExpeditionScreen(container: HTMLElement, ctx: ScreenContext): void {
  combatExitGuard = null;
  const shipLabel = state.currentShip
    ? `${state.currentShip.name} (you)`
    : "No Ship Selected";

  // Section 16.23, LOCKED: only Miner (Digger) and Hauler (Packmule) fit a
  // Drone Module in their default loadout. No rack circle, panel, or
  // per-drone health bar is specified anywhere in the doc for this --
  // added here as a 3rd Fleet Health Box row (the doc's only UI text is
  // "works like weapon-module targeting," which this mirrors: tap the row
  // to assign the current selection, same target-then-module gesture).
  const hasDroneBay = shipHasDefaultDroneBay(state.currentShip?.role ?? "");

  // Real fleet ships (16.43) -- replaces the single hardcoded "Escort —
  // example" test entity that used to sit here unconditionally. Built
  // once here (module-level buildFleetShips(), shared with
  // setUpSpaceCanvas below which does the actual movement/combat/render
  // work against this exact same array) and used here only to generate
  // the right number of HUD rows.
  const fleetShips: LiveFleetShip[] = buildFleetShips();

  // One HUD row per active fleet ship (16.43) -- replaces the single
  // hardcoded "Escort — example" row. `data-fleet-row`/`data-fleet-id`
  // (matching the drone row's own `data-drone-row` convention) let
  // updateHealthBars() zip live values back into the right row by id,
  // rather than the old positional [playerRow, escortRow] destructure.
  function fleetShipRowHtml(f: LiveFleetShip): string {
    return `
      <div class="xv-ship-row" data-fleet-row data-fleet-id="${f.id}">
        <div class="xv-ship-name-line"><b>${f.name}</b><span>NO TARGET</span></div>
        <div class="xv-bar-track"><div class="xv-bar-fill shield" style="width:100%"></div></div>
        <div class="xv-bar-track" style="position:relative;">
          <div class="xv-bar-fill hull-healthy" style="width:100%"></div>
          <div class="sf-flee-marker"><span class="xv-flee-marker-text">FLEE</span></div>
        </div>
      </div>
    `;
  }

  const wrap = document.createElement("div");
  wrap.className = "xv-wrap";
  wrap.innerHTML = `
    <canvas class="xv-space-canvas"></canvas>

    ${navIconsHtml("expedition", false, { inEscapePod: state.inEscapePod, isLoanerShip: state.isLoanerShip })}


    <div class="xv-fleet-box sf-panel">
      <div class="xv-ship-row">
        <div class="xv-ship-name-line"><b>${shipLabel}</b><span>NO TARGET</span></div>
        <div class="xv-bar-track"><div class="xv-bar-fill shield" style="width:100%"></div></div>
        <div class="xv-bar-track"><div class="xv-bar-fill hull-healthy" style="width:100%"></div></div>
      </div>
      ${fleetShips.map((f) => fleetShipRowHtml(f)).join("")}
      ${
        hasDroneBay
          ? `<div class="xv-ship-row" data-drone-row>
              <div class="xv-ship-name-line" data-drone-trigger><b>Drone Bay</b><span data-drone-target>NO TARGET</span></div>
              <div class="xv-bar-track"><div class="xv-bar-fill hull-healthy" style="width:100%" data-drone-hp></div></div>
              <div class="xv-drone-controls">
                <button type="button" class="xv-drone-btn" data-drone-mode>AUTO</button>
                <button type="button" class="xv-drone-btn" data-drone-recall>RECALL</button>
              </div>
            </div>`
          : ""
      }
      <div class="xv-speed-line">Speed: 0 m/s &nbsp;|&nbsp; Heading: 0°</div>
      <!-- Seeded here as well as in updateReadouts, matching the speed line
           above: the frame loop fills it, but the very first paint happens before
           the first frame, and a blank line there reads as a missing feature. -->
      <div class="xv-provisions-line ${provisionsLineClass()}" data-provisions-line>${provisionsLineText()}</div>
    </div>

    <div class="xv-target-bar" data-target-bar></div>

    <div class="xv-scanner-col sf-panel">
      <div class="xv-sc-header"><span class="xv-sc-title" data-sc-title>Scanner UI</span><button type="button" class="xv-sc-sort-btn" data-sort>NEAR ▾</button></div>
      <div class="xv-sc-sub" data-sc-sub></div>
      <div class="xv-sc-list" data-sc-list></div>
      <div class="xv-scan-btn-wrap"><button type="button" class="xv-scan-btn" data-sc-scan-btn>SCAN</button></div>
    </div>
    <button type="button" class="sf-engine-btn-danger xv-ft-slideout" data-cmd="fastTravel" data-ft-slideout disabled>FAST TRAVEL</button>

    <div class="xv-scanning-popup" data-scanning-popup>
      <div class="xv-scanning-panel sf-panel">
        <div class="xv-scanning-header">
          <div>
            <div class="xv-scanning-title" data-scanning-title>Scanning UI</div>
            <div class="xv-scanning-sub" data-scanning-sub></div>
          </div>
          <button type="button" class="xv-scanning-close" data-scanning-close>&times;</button>
        </div>
        <div class="xv-scanning-body" data-scanning-body></div>
        <div class="xv-scan-btn-wrap"><button type="button" class="xv-scan-btn" data-scanning-scan-btn>SCAN</button></div>
      </div>
    </div>

    <div class="lot-modal" data-loot-modal></div>

    <div class="xv-rack-outer">
      <div class="xv-rack">
        ${RACK_SHELLS.map(rackModuleHtml).join("")}
        <div class="xv-engine-cluster">
          ${rackModuleHtml(ENGINE_SHELL)}
          <div class="xv-orbit-btns">
            <button type="button" class="sf-engine-btn-safe" data-cmd="orbitNear" disabled>Orbit Near</button>
            <button type="button" class="sf-engine-btn-safe" data-cmd="orbitFar" disabled>Orbit Far</button>
          </div>
          <div class="xv-engine-btns">
            <button type="button" class="sf-engine-btn-safe" data-cmd="moveto" disabled>Move To</button>
            <button type="button" class="sf-engine-btn-safe" data-cmd="loot" disabled>Loot</button>
            <button type="button" class="sf-engine-btn-safe" data-cmd="buyLease" disabled title="10,000 CR">Buy Lease</button>
            <button type="button" class="sf-engine-btn-safe" data-cmd="dock" disabled>Dock</button>
          </div>
          <div class="xv-engine-btns">
            <button type="button" class="sf-engine-btn-safe" data-cmd="cloak" title="5s no-attack/no-lock, 60s cooldown">CLOAK</button>
            <button type="button" class="sf-engine-btn-safe" data-cmd="tractorBeam" disabled title="Pulls wrecks within 2000m toward you">TRACTOR BEAM</button>
          </div>
          <div class="xv-engine-btns">
            <button type="button" class="sf-engine-btn-safe" data-cmd="warp" disabled>WARP</button>
          </div>
        </div>
      </div>
    </div>

    <div class="xv-zoom-mini-wrap">
      <div class="xv-zoom sf-panel" data-zoom>
        <!-- 16.98: these were hardcoded to highlight Med, so setting Far or
             Close as the Options default left the radar claiming Med. The live
             \`zoom\` variable has always come from settings.defaultZoom -- only
             the highlight disagreed with it. -->
        <div data-z="far" class="${settings.defaultZoom === "far" ? "active" : ""}">Far</div>
        <div data-z="medium" class="${settings.defaultZoom === "medium" ? "active" : ""}">Med</div>
        <div data-z="close" class="${settings.defaultZoom === "close" ? "active" : ""}">Close</div>
      </div>
      <div class="xv-minimap sf-minimap" data-minimap>
        <div class="xv-mm-label">MINI-MAP</div>
        <canvas class="xv-minimap-canvas" data-minimap-canvas></canvas>
      </div>
    </div>

    <div class="xv-minimap-full" data-minimap-full>
      <div class="xv-minimap-full-canvas-wrap">
        <canvas class="xv-minimap-full-canvas" data-minimap-full-canvas></canvas>
      </div>
      <div class="xv-mm-sel-hint" data-mm-sel-hint>Tap anywhere to drop a marker, or tap a dot to target it.</div>
      <div class="xv-mm-legend">
        <span><span class="xv-leg-dot" style="background:var(--sf-color-shield)"></span>You</span>
        <span><span class="xv-leg-dot" style="background:var(--sf-color-escort)"></span>Fleet</span>
        <span><span class="xv-leg-dot" style="background:var(--sf-color-danger)"></span>Hostile</span>
        <span><span class="xv-leg-dot" style="background:#8a8f96"></span>Asteroid/Wreck</span>
        <span><span class="xv-leg-dot" style="background:#8a5fd6"></span>Jump Gate</span>
        <span><span class="xv-leg-dot" style="background:var(--sf-color-attention)"></span>Marker</span>
      </div>
      <button type="button" class="xv-mm-close-btn" data-mm-close>Close</button>
    </div>

    <div class="xv-banner" data-banner></div>
    <div class="xv-distress-panel sf-panel" data-distress-panel style="display:none"></div>
    <div class="xv-depart-overlay" data-depart-overlay><div data-depart-text>Turning to new heading…</div></div>
    <div class="xv-arrival" data-arrival><div>ARRIVING —</div></div>
    <!-- 16.82: the one-time discovery reveal for a hiddenUntilScanned
         system. Dismissible rather than timed -- it's the only text in the
         game the captain is meant to actually read, and a beat that fades
         on its own schedule would be read once and never again. It stays
         permanently available in the Trophy Room afterwards. -->
    <div class="xv-discovery" data-discovery>
      <div class="xv-discovery-panel sf-panel">
        <div class="xv-discovery-eyebrow" data-discovery-eyebrow></div>
        <h2 data-discovery-title></h2>
        <p data-discovery-body></p>
        <button type="button" data-discovery-close>Log it</button>
      </div>
    </div>
  `;

  // Quick-access icons to every UI reachable from out in space (Don's
  // direction: "from the Expedition View we need buttons to access all
  // relevant UIs"). Each opens as a pane on top of this still-running
  // Expedition View (app.ts's PANE_SCREENS) rather than leaving it --
  // real-time combat/mining and the game clock keep going underneath.
  // Market is included even though buying/selling needs a real dock: the
  // captain still needs to read prices and plan trade routes from space.
  // There's no dedicated "Exit" button (Don's direction: every UI is
  // reached via a contextual button where it shows up, or the DEV DEBUG
  // panel, not a special escape hatch) -- still save on the way into a
  // pane, since Mining Laser mutates state.cargo directly and there's no
  // guarantee the pane's own screen triggers a save itself.
  // Save and Return to Menu -- the one deliberate way *out* of the three
  // Views. Going to the Main Menu also pauses the game clock (16.34, see
  // app.ts's shouldPauseClock), so this is a real "stop playing" action,
  // not just another pane.
  wireNavIcons(
    wrap,
    (screen) => {
      triggerSave(ctx);
      ctx.navigate(screen);
    },
    () => {
      // Don ruled Save & Exit stays available mid-fight, on the condition that
      // the captain is TOLD what happens (16.99). One press while hostiles are
      // live explains it; a second one leaves.
      //
      // A two-step press rather than a modal: a blocking confirm in the middle
      // of a firefight is its own kind of hostile, and the message needs to be
      // read rather than dismissed. The banner is already the screen's own way
      // of saying things, and it is now safe to use here because 16.98 stopped
      // messages expiring under overlays.
      if (combatExitGuard?.()) return;
      triggerSave(ctx);
      ctx.navigate("mainMenu");
    }
  );

  container.appendChild(wrap);

  // Sized from clientWidth/clientHeight -- must run after the element is
  // attached to the DOM, or it measures a detached (0x0) node.
  setUpSpaceCanvas(wrap, ctx, fleetShips);
}

// A null `modules` list renders the "no lock" empty-slot state (Section
// 4a): a target the captain has selected but hasn't assigned any module
// to yet.
function targetSlotHtml(
  name: string,
  distance: string,
  hpPercent: number,
  modules: { label: string; inRange: boolean }[] | null,
  isSelected = false,
  blueprintId: string | null = null
): string {
  return `
    <div class="xv-target-slot sf-panel${isSelected ? " selected" : ""}">
      <div class="xv-target-name">${name}</div>
      <div class="xv-target-dist">${distance}</div>
      <div class="xv-target-hp-track"><div class="xv-target-hp-fill" style="width:${hpPercent}%"></div></div>
      <div class="xv-mod-list">
        ${
          modules
            ? modules
                .map(
                  (m) =>
                    `<div>${m.label}<span class="${m.inRange ? "in-range" : "out-of-range"}">${m.inRange ? "IN RANGE" : "OUT OF RANGE"}</span></div>`
                )
                .join("")
            : `<div class="xv-no-lock">no lock</div>`
        }
      </div>
      ${blueprintId ? `<button type="button" class="sf-bp-trigger" data-bp-target="${blueprintId}">More Info</button>` : ""}
    </div>
  `;
}

// Circumference for r=27 (mockup's own module-circle ring radius, chosen
// to sit just inside the 64px sf-module-circle) -- mockup hardcodes "170"
// as its rounded approximation of 2*pi*27 (~169.6); kept as an exact
// computed value here instead.
const ARC_RADIUS = 27;
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;

// Illustrative static stat shown above each circle (Section 4a's "docked
// minimum truths") -- HP ratios stay at a fixed full-health placeholder
// since there's no damage system yet; weapon count is always "1x" absent
// real fitting data.
function initialStatText(shell: ModuleShell): string {
  if (shell.type === "shield") return "1200/1200";
  if (shell.type === "hull") return "2400/2400";
  if (shell.type === "weapon") return "1×";
  return "0 m/s"; // engine -- kept live by updateReadouts()
}

// The progress-ring circle is always rendered (never inserted/removed per
// frame); its visibility and stroke-dashoffset are toggled live via
// data-mod-arc-progress once mounted. Engine has no cycle, so no arc.
function cycleArcSvgShell(): string {
  return `
    <svg width="64" height="64" viewBox="0 0 64 64" class="xv-mod-arc">
      <circle cx="32" cy="32" r="${ARC_RADIUS}" fill="none" stroke="var(--sf-color-track-bg)" stroke-width="4" />
      <circle cx="32" cy="32" r="${ARC_RADIUS}" fill="none" stroke="var(--sf-color-engine-safe)" stroke-width="4"
        stroke-dasharray="${ARC_CIRCUMFERENCE}" stroke-dashoffset="${ARC_CIRCUMFERENCE}"
        transform="rotate(-90 32 32)" data-mod-arc-progress style="display:none" />
    </svg>
  `;
}

// Expand/minimize (Section 4a, LOCKED) says "tapping a docked module
// expands it" -- but tapping the module IS the already-locked gesture for
// assigning/deactivating its target (Section 4a, same section), and those
// two behaviors can't share one gesture (this is the same kind of
// collision the tap-bonus mechanic had, see moduleCycle.ts). Resolved
// here by making the stat readout above the circle -- not the circle
// itself -- the expand/collapse trigger, plus the doc's own "−" icon once
// expanded; the tap-to-target gesture on the circle is untouched.
function rackModuleHtml(shell: ModuleShell): string {
  return `
    <div class="xv-mod-circle-wrap xv-mod-type-${shell.type}" data-mod-key="${shell.key}">
      <div class="xv-mod-expand" data-mod-expand>
        <button type="button" class="xv-mod-expand-close" data-mod-expand-close>−</button>
        <div class="xv-mod-expand-stats" data-mod-expand-stats></div>
      </div>
      <div class="xv-mod-stat" data-mod-stat-trigger>${initialStatText(shell)}</div>
      <div class="sf-module-circle moduleOff" data-mod-circle>
        ${shell.type === "engine" ? "" : cycleArcSvgShell()}
        <span class="xv-mod-letter">${shell.letter}</span>
      </div>
      <div class="xv-mod-label">${shell.label}</div>
      ${shell.range ? `<div class="xv-mod-range">Range ${shell.range}m</div>` : ""}
    </div>
  `;
}

// ================= Space view: live movement + camera =================
// Ported from the mockup's shipShape/rockShape/w2s/tick functions --
// literal geometry/tuning match, not a re-derivation (see file-header
// note for what's still deliberately not ported: combat tick, Fast
// Travel sequence, module targeting).

// Zoom viewport radii (Section 2a, LOCKED) -- how many meters from
// ship-center each zoom level renders; only the Mini-map ever shows the
// full 22,500 m Location radius.
const ZOOM_RADII = { close: 1500, medium: 4000, far: 9000 } as const;
type ZoomLevel = keyof typeof ZOOM_RADII;


interface ShipBody extends Kinematics {
  topSpeed: number;
  stopDistance: number;
}

interface NamedPoint extends Point {
  id: string;
  name: string;
}

// Space Map scanner-tier gating (16.46) -- being physically present in a
// System is itself how it becomes "scanned" (Section 6), whether ordinary
// or one of the 10 real hiddenUntilScanned systems (doc: visiting
// "converts immediately to a normal, fully-known system").
function markSystemVisited(systemId: string): void {
  if (!state.visitedSystemIds.includes(systemId)) {
    state.visitedSystemIds = [...state.visitedSystemIds, systemId];
  }
}

// Placeholder weapon/detection ranges for NPC-controlled ships (16.43) --
// both hostiles and non-flown fleet ships, since neither is directly
// flown by the captain. No number is locked anywhere for this, so rather
// than invent fresh ones this reuses the real player weapon ranges
// already locked for Section 2a (WEAPON_SHELLS above) as anchors: T1 ~
// Auto-Turret (600m), T3 ~ Laser Turret (1400m), T4 ~ Missile Turret
// (3000m). Detection range is 2x weapon range, so a target is spotted
// before it's actually in firing range, giving the captain a moment to
// react.
const NPC_WEAPON_RANGE_M: Record<NpcHullTier, number> = { T1: 600, T2: 900, T3: 1400, T4: 3000 };

// How far out the station garrison rings the station (16.94). Comfortably
// outside a T4 weapon range (3,000 m) so a captain arriving at the Location
// sees the formation before anything can reach them -- an ambush would make
// the approach a surprise rather than a decision.
const STATION_GUARD_RING_M = 3600;
function npcDetectionRangeM(tier: NpcHullTier): number {
  return NPC_WEAPON_RANGE_M[tier] * 2;
}

// Endgame content for the 10 real hiddenUntilScanned systems (16.67,
// resolves 16.49's "endgame loot tables and combat scaling" item) --
// finding one used to reward nothing beyond finding it. Now: guaranteed
// Exceptional-richness ambient nodes (never rolled Standard), a single
// boss-tier guardian on the belt/mining Location (50% tougher than an
// ordinary same-tier Alien Elite -- reuses 16.24's existing "Exceptional
// nodes are always guardian-flagged" rule rather than inventing a new
// one), and a one-time credit bonus the first time a captain ever
// reaches one.
function isHiddenSystem(systemId: string): boolean {
  return HIDDEN_SYSTEM_IDS.includes(systemId);
}
// HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER moved to src/bestiary.ts (16.82) --
// the Trophy Room needs the same number to show a guardian's real stats,
// and two copies of a balance constant is exactly how they drift.
// Scales with the system's own Danger Tier, same "bigger risk, bigger
// number" logic already governing every other reward in the doc --
// no separate table needed for four possible tiers.
const HIDDEN_SYSTEM_DISCOVERY_BONUS_CR_PER_TIER = 5000;

function hiddenSystemDiscoveryBonusCr(dangerTier: number): number {
  return HIDDEN_SYSTEM_DISCOVERY_BONUS_CR_PER_TIER * Math.max(1, dangerTier);
}

// NPCs (hostile and fleet) don't carry a real Hardpoint-type identity, only
// a single weaponRangeM anchored to one of the player's own locked ranges
// (comment above) -- so the range-based hit-chance curve (16.66) they roll
// against is picked by bucketing that range onto the closest of the three
// real turret bands, rather than inventing a separate NPC curve.
function hitChanceCurveForRangeM(rangeM: number): HitChanceCurve {
  if (rangeM <= 700) return HIT_CHANCE_CURVES.auto;
  if (rangeM <= 2000) return HIT_CHANCE_CURVES.laser;
  return HIT_CHANCE_CURVES.missile;
}

// Real fleet ships (16.43) -- replaces the single hardcoded "Escort —
// example" test entity that used to sit here unconditionally. Every fleet
// ship (state.fleet) with stance "Active", other than whichever one is
// actually being flown (state.currentShipInstanceId), joins the scene as
// a real ship: its own real hull/shield (PlayerShip.hullHp/shieldHp, not
// illustrative placeholders), its own real shieldRegenPerS/repairPerS
// (passive, ticking every frame -- there's no module-activation UI for a
// ship that isn't the one being flown), its own real dps auto-firing at
// whatever hostile it's aggroed onto, and its own real cargoM3/oreHoldM3
// as a destination Loot can target. Passive-stance ships simply don't
// join the scene at all -- stance now has a real, honest effect instead
// of being stored-but-inert. Module-level (not nested in either
// renderExpeditionScreen or setUpSpaceCanvas) since both need it: the
// former to generate the right number of HUD rows, the latter to actually
// run movement/combat/rendering against the exact same array.
interface LiveFleetShip extends ShipBody {
  id: string;
  name: string;
  // 16.79: which canvas silhouette to draw -- the same role/hullTier a
  // Fleet Roster row already carries, just copied onto the live object
  // so draw() doesn't have to look the fleet entry back up every frame.
  role: string;
  hullTier: NpcHullTier;
  shield: number;
  shieldMax: number;
  hull: number;
  hullMax: number;
  shieldRegenPerS: number;
  repairPerS: number;
  dps: number;
  cargoM3: number;
  oreHoldM3: number;
  cargo: Record<string, number>;
  fled: boolean;
  detectionRangeM: number;
  weaponRangeM: number;
  aggroTargetId: string | null;
  nextFireAt: number | null;
}

function buildFleetShips(): LiveFleetShip[] {
  return state.fleet
    .filter((f) => f.stance === "Active" && f.id !== state.currentShipInstanceId)
    .map((f, i) => {
      const s = f.ship;
      // Escorts carry their damage between expeditions too (16.81) -- an
      // escort that limped home stays limping until something repairs it.
      const dmg = damageFor(state.shipDamage, f.id);
      return {
        id: f.id,
        name: s.name,
        role: s.role,
        hullTier: s.hullTier,
        x: -500 - i * 300,
        y: 600,
        heading: 0,
        speed: 0,
        topSpeed: inLocationTopSpeed(s.hullTierLabel),
        stopDistance: 400,
        shield: Math.max(0, s.shieldHp - dmg.shield),
        shieldMax: s.shieldHp,
        hull: Math.max(1, s.hullHp - dmg.hull),
        hullMax: s.hullHp,
        shieldRegenPerS: s.shieldRegenPerS ?? 0,
        repairPerS: s.repairPerS,
        dps: s.dps,
        cargoM3: s.cargoM3,
        oreHoldM3: s.oreHoldM3,
        cargo: {},
        fled: false,
        detectionRangeM: npcDetectionRangeM(s.hullTier),
        weaponRangeM: NPC_WEAPON_RANGE_M[s.hullTier],
        aggroTargetId: null,
        nextFireAt: null
      };
    });
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function rockShape(ctx: CanvasRenderingContext2D, sx: number, sy: number, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(sx, sy, r, 0, Math.PI * 2);
  ctx.fill();
}

// A physical Jump Gate object (Don's direction: "add a visual that
// represents a jump gate") -- a real ringed portal structure, not just a
// plain filled circle: an outer support ring with radiating struts (the
// physical "frame" a ship flies through), a lighter inner ring, and a
// pulsing glow at the very center standing in for the active event
// horizon. Distinct from every other object's ship/rock shape.
function gateShape(ctx: CanvasRenderingContext2D, sx: number, sy: number, r: number, color: string, pulse: number): void {
  ctx.save();
  ctx.translate(sx, sy);

  // Support struts -- short radial spokes around the outer ring, evenly
  // spaced, giving the ring a built "structure" silhouette rather than a
  // bare circle.
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  const strutCount = 8;
  for (let i = 0; i < strutCount; i++) {
    const angle = (i / strutCount) * Math.PI * 2;
    const inner = r * 1.05;
    const outer = r * 1.3;
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
    ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
    ctx.stroke();
  }

  // Outer support ring.
  ctx.lineWidth = 3.5;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();

  // Inner ring, lighter.
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.75, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Pulsing event-horizon glow at the center -- pulse is a slow 0..1
  // oscillation driven by the caller (real elapsed time, not a fixed
  // frame count), so the gate visibly breathes rather than sitting inert.
  const glowR = r * (0.4 + pulse * 0.15);
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glowR);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = gradient;
  ctx.globalAlpha = 0.5 + pulse * 0.3;
  ctx.beginPath();
  ctx.arc(0, 0, glowR, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.restore();
}

// A dropped Move To/Orbit marker (not in the original mockup) -- a small
// gold crosshair, distinct from every real object's ship/rock shape.
function markerShape(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
  ctx.strokeStyle = "#f0c94a";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(sx - 9, sy);
  ctx.lineTo(sx + 9, sy);
  ctx.moveTo(sx, sy - 9);
  ctx.lineTo(sx, sy + 9);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(sx, sy, 3, 0, Math.PI * 2);
  ctx.fillStyle = "#f0c94a";
  ctx.fill();
}

// Planet/Station backgrounds (Don's direction: "add a background image of
// the planet, uniquely colored based on the planet and system
// description" / faction-colored for Stations) -- no real art assets
// exist yet, so a CSS/canvas-placeholder color fill stands in, matching
// the design doc's own already-established convention for this exact gap
// (Section 6's implementation note: base art uses real filenames when
// available, falls back to a flat placeholder color otherwise).
const PLANET_TYPE_COLORS: Record<string, string> = {
  "Ice moon": "#8fd3e8",
  Earthlike: "#4f9d6a",
  Waterworld: "#2e6fa8",
  "Exotic/alien": "#9a5fd6",
  Volcanic: "#c9463f",
  "Hot rocky": "#c9723a"
};

function planetTypeColor(planetType: string): string {
  return PLANET_TYPE_COLORS[planetType] ?? "#7a8494";
}

// Faction color, straight from the System's own region field (Section 6's
// region names -- "crimson"/"azure"/"solthane" -- already ARE the
// placeholder red/blue/yellow color-coding the doc describes; "safe"/
// "neutral"/"pirate" get a neutral gray, no faction ownership implied).
const FACTION_COLORS: Record<string, string> = {
  crimson: "#c9463f",
  azure: "#4f8cff",
  solthane: "#d6b23a"
};

function stationFactionColor(region: string): string {
  return FACTION_COLORS[region] ?? "#8a8f96";
}

// A large, always-visible backdrop object -- a soft radial-gradient disc
// (the planet/station itself) behind a brighter rim, big enough to read
// as "a world/structure you're near," not just another small icon like an
// asteroid or wreck.
function planetShape(ctx: CanvasRenderingContext2D, sx: number, sy: number, r: number, color: string): void {
  ctx.save();
  const gradient = ctx.createRadialGradient(sx - r * 0.3, sy - r * 0.3, r * 0.1, sx, sy, r);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, "#04070d");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(sx, sy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

// A Station's backdrop -- a faceted structure silhouette (angular, not a
// smooth sphere like a planet), faction-colored, so the two large
// backdrop kinds stay visually distinct at a glance.
function stationShape(ctx: CanvasRenderingContext2D, sx: number, sy: number, r: number, color: string): void {
  ctx.save();
  ctx.translate(sx, sy);
  const sides = 6;
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2;
    const px = Math.cos(angle) * r;
    const py = Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  const gradient = ctx.createRadialGradient(0, 0, r * 0.1, 0, 0, r);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, "#1a1f2e");
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();
  // Inner ring, echoing the gate's "structure" language for a docked hub.
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.restore();
}

function setUpSpaceCanvas(wrap: HTMLElement, ctx: ScreenContext, fleetShips: LiveFleetShip[]): void {
  const FLEET_SHIP_FLEE_HULL_FRACTION = 0.1; // Section 10: auto-flee at 10% hull

  function fleetShipById(id: string): LiveFleetShip | undefined {
    return fleetShips.find((f) => f.id === id);
  }

  // A live-synced (not snapshotted) selectable point per fleet ship, so
  // Move To/Orbit/repair-targeting all track real position each frame
  // rather than freezing wherever it was at click time.
  const fleetShipPoints: NamedPoint[] = fleetShips.map((f) => ({ id: f.id, name: f.name, x: f.x, y: f.y }));

  const canvas = wrap.querySelector<HTMLCanvasElement>(".xv-space-canvas")!;
  const c2d = canvas.getContext("2d")!;

  // Opens at whichever of Section 4b's three locked radii the captain set
  // as their default (16.69); "medium" remains the shipped default.
  let zoom: ZoomLevel = settings.defaultZoom;

  // The real current System (src/data/systems.json via getSystem, the same
  // save-state src/ui/components.ts's header already reads) -- reassigned
  // only on a real WARP through a Gate (see completeFlee), never on an
  // ordinary same-System Fast Travel between Locations.
  let currentSystem: StarSystem = getSystem(state.currentSystemId)!;
  // Space Map scanner-tier gating (16.46): being physically present in a
  // System is itself how it becomes "scanned" (Section 6). Marked here at
  // mount too, not only on a real Gate arrival (completeFlee) -- a save
  // written before this feature existed would otherwise show its own
  // current System as merely "visible, unscanned" despite the captain
  // literally standing in it.
  markSystemVisited(currentSystem.id);
  // Which real Location (within currentSystem) the ship is actually in --
  // see buildSystemLocations. Persisted the same way currentSystemId is.
  let currentLocationId: string = state.currentLocationId || "planet-1";
  // The current Location's kind and, for a "missionCombat"/"missionMining"
  // Location, which accepted mission it belongs to -- both set exclusively
  // by rebuildLocationScene, consulted by checkWaveProgress/
  // extractFromAsteroid to know whether a wave-clear/extraction should
  // resolve a real accepted mission instead of the ambient flavor content.
  let currentLocationKind: LocationKind = "planet";
  let activeMissionId: string | null = null;

  // Camera always centers on the live player position (Section 2a) --
  // player is drawn at exact screen-center, everything else offset
  // relative to it.
  // Real max HP/regen from the selected ship (PlayerShip), falling back to
  // an illustrative value only where the real data is genuinely 0/null
  // (see file header -- e.g. the Digger has no shield or repair fitted).
  const shieldMaxHp = state.currentShip?.shieldHp || FALLBACK_SHIELD_MAX_HP;
  const hullMaxHp = state.currentShip?.hullHp ?? 2400;
  const shieldRegenPerS = state.currentShip?.shieldRegenPerS || FALLBACK_SHIELD_REGEN_PER_S;
  const hullRepairPerS = state.currentShip?.repairPerS || FALLBACK_HULL_REPAIR_PER_S;
  const playerDamage = damageFor(state.shipDamage, state.currentShipInstanceId);

  const player: ShipBody & { shield: number; shieldMax: number; hull: number; hullMax: number } = {
    x: 0,
    y: 0,
    heading: -Math.PI / 2,
    speed: 0,
    // A podded captain moves at the pod's own derived 765 m/s -- 50%
    // above the fastest possible hull (16.72) -- rather than inheriting
    // whatever hull they were last flying.
    topSpeed: state.inEscapePod
      ? POD_IN_LOCATION_MPS
      : inLocationTopSpeed(state.currentShip?.hullTierLabel ?? "Medium"),
    stopDistance: 150,
    // Hull/shield damage is persistent now (16.81): the ship comes back
    // out exactly as beaten up as it went in. This used to open at full
    // HP every time, which made leaving and re-entering the Expedition
    // View a free, instant, unlimited repair.
    shield: Math.max(0, shieldMaxHp - playerDamage.shield),
    shieldMax: shieldMaxHp,
    hull: Math.max(1, hullMaxHp - playerDamage.hull),
    hullMax: hullMaxHp
  };
  interface LiveHostile extends NamedPoint {
    shield: number;
    shieldMax: number;
    hull: number;
    hullMax: number;
    dps: number;
    // 16.79: faction + hull tier drive which canvas silhouette this
    // hostile draws as. `name` already carries the subtype (e.g. "Pirate
    // Raider"), so only the two axes name can't fully give us on its own
    // (faction as a real NpcFaction, not a string split; tier at all)
    // need to be captured at spawn time.
    faction: NpcFaction;
    hullTier: NpcHullTier;
    nextFireAt: number | null;
    heading: number;
    speed: number;
    topSpeed: number;
    detectionRangeM: number;
    weaponRangeM: number;
    aggroTargetId: string | null;
    /**
     * A station guard (16.94). Guards differ from every other hostile in two
     * ways: they may be present without being hostile, and while any of them
     * is engaged the captain cannot dock.
     */
    guard?: boolean;
    /**
     * Present but holding fire. Set on guards whose station tolerates or
     * welcomes the captain; cleared the instant one is damaged, for the whole
     * garrison at once -- a station whose guards are being shot does not have
     * some of them politely watching.
     */
    passive?: boolean;
    // Cloak (16.61) -- snapshot of this hostile's target point at the
    // instant Cloak activates, so it keeps moving in its last real
    // heading instead of homing in on the real (now-hidden) position for
    // the duration. Cleared once Cloak ends.
    frozenTargetPoint?: Point;
  }

  // A real Wave encounter (src/data/encounters.json) -- not the mockup's
  // invented single "Micro Pirate 420 HP / 38 DPS" placeholder. PT1-013 is
  // a real 2-wave T1 Pirate encounter ("2x Raider" then "3x Raider+2x
  // Micro"); its composition string is parsed with the same
  // encounterComposition.ts functions combatResolutionLoop.ts's
  // initCombat() uses for exactly this purpose. This is the ambient
  // "combat" Location's fixed flavor encounter -- a "missionCombat"
  // Location swaps `encounter`/`waveSegments` to the specific accepted
  // mission's own encounter instead (see rebuildLocationScene).
  const defaultEncounter = loadEncounters().find((e) => e.id === "PT1-013")!;
  let encounter = defaultEncounter;
  const allUnits = loadUnits();
  let waveSegments = parseStageSegments(encounter.composition);

  /**
   * Rebuild one saved hostile (16.99).
   *
   * The saved fields are the ones a fight cannot be resumed without: identity,
   * position and remaining HP. Everything else is re-derived rather than stored,
   * because it either belongs to this session (`nextFireAt` is a
   * `performance.now()` value, and restoring one would make every hostile fire
   * the instant the screen loads) or is a constant of the unit type that would
   * only rot in the save.
   */
  function restoreHostile(h: HostileSnapshot): LiveHostile {
    const unit = allUnits.find((u) => u.name === h.name);
    return {
      id: h.id,
      name: h.name,
      faction: h.faction,
      hullTier: h.hullTier,
      x: h.x,
      y: h.y,
      shield: h.shield,
      shieldMax: h.shieldMax,
      hull: h.hull,
      hullMax: h.hullMax,
      dps: h.dps,
      nextFireAt: null,
      heading: 0,
      speed: 0,
      // Same three derivations buildWaveHostiles uses, so a restored hostile and
      // a freshly spawned one of the same type are mechanically identical. Only
      // the speed needs the roster entry; the other two key off tier alone, so a
      // renamed unit degrades to a slow hostile rather than an undefined one.
      topSpeed: inLocationTopSpeed(unit?.hullTierLabel ?? "Medium") * 0.9,
      detectionRangeM: npcDetectionRangeM(h.hullTier),
      weaponRangeM: NPC_WEAPON_RANGE_M[h.hullTier],
      aggroTargetId: null
    };
  }

  function buildWaveHostiles(waveIndex: number): LiveHostile[] {
    const counts = parseCompositionUnits(waveSegments[waveIndex], encounter.faction, encounter.hullTier, allUnits);
    const totalCount = counts.reduce((sum, c) => sum + c.count, 0);
    const baseAngle = Math.random() * Math.PI * 2;
    const result: LiveHostile[] = [];
    let i = 0;
    counts.forEach(({ unit, count }) => {
      for (let k = 0; k < count; k++) {
        const angle = baseAngle + (i / totalCount) * Math.PI * 2;
        const dist = 1800 + (i % 2) * 300;
        result.push({
          id: `wave${waveIndex}-${i}`,
          name: unit.name,
          faction: unit.faction,
          hullTier: unit.hullTier,
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist,
          shield: unit.shieldHp,
          shieldMax: unit.shieldHp,
          hull: unit.hullHp,
          hullMax: unit.hullHp,
          dps: unit.dps,
          nextFireAt: null,
          heading: 0,
          speed: 0,
          // 16.43: 90% of the comparable player-ship class's real top
          // speed (inLocationSpeed.ts's own HULL_SIZE_BASE_SPEED_MPS,
          // keyed the same way PlayerShip's hullTierLabel already is) --
          // reuses the real player speed table rather than inventing a
          // separate NPC one.
          topSpeed: inLocationTopSpeed(unit.hullTierLabel) * 0.9,
          detectionRangeM: npcDetectionRangeM(unit.hullTier),
          weaponRangeM: NPC_WEAPON_RANGE_M[unit.hullTier],
          aggroTargetId: null
        });
        i++;
      }
    });
    return result;
  }

  // Hidden-system endgame guardian (16.67): a single boss-tier Alien Elite
  // on the belt/mining Location of one of the 10 real hiddenUntilScanned
  // systems -- 50% tougher than an ordinary same-tier Elite, matching
  // 16.24's existing "Exceptional-richness nodes are always
  // guardian-flagged" rule rather than inventing a separate one.
  function spawnHiddenSystemGuardian(): LiveHostile | null {
    const tierKey = Math.min(4, Math.max(1, currentSystem.dangerTier));
    const hullTier = `T${tierKey}` as NpcHullTier;
    const elite = allUnits.find((u) => u.faction === "Alien" && u.hullTier === hullTier && u.name.endsWith("Elite"));
    if (!elite) return null;
    return {
      id: "hidden-guardian",
      name: guardianName(elite.name),
      faction: elite.faction,
      hullTier: elite.hullTier,
      x: 0,
      y: -2400,
      shield: elite.shieldHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
      shieldMax: elite.shieldHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
      hull: elite.hullHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
      hullMax: elite.hullHp * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
      dps: elite.dps * HIDDEN_SYSTEM_GUARDIAN_MULTIPLIER,
      nextFireAt: null,
      heading: 0,
      speed: 0,
      topSpeed: inLocationTopSpeed(elite.hullTierLabel) * 0.9,
      detectionRangeM: npcDetectionRangeM(hullTier),
      weaponRangeM: NPC_WEAPON_RANGE_M[hullTier],
      aggroTargetId: null
    };
  }

  // Planetary Defenses / random-attack state (16.70). Declared HERE, well
  // above the per-frame functions that use them, because
  // rebuildLocationScene() -- which resets both -- is called during mount,
  // long before those functions' own section of this file. A `const`/`let`
  // declared next to its functions would be in the temporal dead zone at
  // that point and throw at runtime (TypeScript can't catch that).
  const defenseLocks = new Map<string, number>(); // hostile id -> kill-at timestamp
  let nextRandomAttackAt: number | null = null;

  // Selection / movement state. Declared up here for the same
  // hoisting reason as defenseLocks above: rebuildLocationScene() resets
  // all three and is called during mount, well before this file's own
  // selection/movement section -- declaring them next to that code puts
  // them in the temporal dead zone at mount and throws at runtime, which
  // TypeScript will not catch.
  let selected: NamedPoint | null = null;
  let activeMoveMode: "moveto" | "orbitNear" | "orbitFar" | "cruise" | null = null;
  /**
   * Where the ship is FLYING, as distinct from what the captain is looking at
   * (16.100, Don's report).
   *
   * Movement used to read straight off `selected`, so a single click anywhere
   * redirected the ship. That made the viewport unusable for its most common
   * job: picking a target to shoot while holding position. Don's words -- "it
   * makes it difficult to click on targets to activate modules while staying in
   * one position."
   *
   * Now selection and destination are two separate things. Selection is a
   * single click and changes nothing about the ship's course; a destination is
   * only ever set by a deliberate act -- a double-click, or a Move To / Orbit
   * press against whatever is currently selected.
   *
   * Held as the object rather than a copied position, so orbiting and chasing
   * still track a moving ship.
   */
  let moveTarget: NamedPoint | null = null;
  // `marker` is dropped by tapping empty space in the main viewport or the
  // Mini-map popup (see selectAtWorldPoint) -- it lets the captain Move
  // To/Orbit anywhere in the Location, not only objects that happen to be
  // visible. A single reusable marker, not an accumulating trail:
  // dropping a new one replaces the last, selecting anything else clears
  // it, and so does changing Location (16.71).
  let marker: NamedPoint | null = null;

  let currentWaveIndex = 0;
  // Mutated in place (splice/push), never reassigned -- destroyHostile and
  // the wave-advance logic both rely on this being the same array
  // reference throughout. Populated by rebuildLocationScene below, not
  // here -- whether it starts non-empty depends on which real Location
  // the ship is actually in (only the "combat" Location has a scripted
  // encounter now, see buildSystemLocations).
  const hostiles: LiveHostile[] = [];
  // Absolute timestamp the gap ends (16.30's load-bearing rule: countdowns
  // are target timestamps, never a value decremented on an interval).
  let waveGapUntil: number | null = null;
  let encounterCleared = false;
  // Gates checkWaveProgress -- only the "combat" Location's scripted
  // PT1-013 encounter uses the wave/gap machinery at all now; every other
  // Location kind leaves hostiles/waveGapUntil alone entirely (ambient
  // contacts still work everywhere, independently, see below).
  let encounterActive = false;

  interface FixedObject extends NamedPoint {
    kind: "asteroid" | "wreck" | "gate" | "planet" | "station";
    discovered: boolean;
    resource?: string; // asteroid-kind only
    poolM3?: number; // asteroid-kind only -- remaining extractable m3
    poolMaxM3?: number;
    jumpToSystemId?: string; // gate-kind only -- real destination System id
    loot?: Record<string, number>; // wreck-kind only -- resource name -> m3, real Market items
    planetType?: string; // planet-kind only -- flavor type, drives background color
    buildable?: boolean; // planet-kind only
    leaseKey?: string; // planet-kind only -- state.ownedLeases entry, "<systemId>:<planetId>"
    // asteroid-kind, belt/mining Locations only (16.53) -- the persisted
    // regen record key (src/resourceNodeRegen.ts) this node's poolM3
    // mirrors. Undefined for missionMining's own ephemeral, mission-scoped
    // nodes, which aren't persisted world resources.
    nodeRegenKey?: string;
    // Tractor Beam (16.61) -- wreck-kind only. True while this wreck is
    // being pulled toward the player; cleared once it arrives in loot
    // range.
    tractored?: boolean;
  }

  // Real current System's own resource list and dangerTier (src/data/
  // systems.json) drive the belt's resource and pool size now -- richness
  // is still an illustrative "Standard" stand-in, since StarSystem has no
  // richness field in the real data model (Section 16.2/16.25's nodePoolM3
  // itself is real, just fed real dangerTier instead of a hardcoded 0).
  let asteroidResource = currentSystem.resources[0] ?? "Ferrite Ore";
  let asteroidPoolMaxM3 =
    nodePoolM3(currentSystem.dangerTier, isHiddenSystem(currentSystem.id) ? "Exceptional" : "Standard") *
    dangerTierRichnessMultiplier(currentSystem.dangerTier);

  // Section 8's "revealed only by scanning" category -- undiscovered
  // objects aren't drawn, selectable, or shown in the Scanner UI beyond a
  // generic "? Unknown Signature" placeholder, until a SCAN reveals them.
  // Populated by rebuildLocationScene below, not a static literal --
  // which objects exist now depends on which real Location this is.
  const fixedObjects: FixedObject[] = [];

  function initialWreck(): FixedObject {
    return { id: "wreck-scripted", name: "Derelict Wreck", kind: "wreck", x: 3000, y: 1600, discovered: false, loot: buildWreckLoot(null) };
  }

  // Fixed per-object backdrop position (16.50): Gates/planets/Stations used
  // to all spawn at the exact same hardcoded (x, y) in every Location, which
  // read as repetitive since none of them ever varied. seededUnit derives a
  // stable angle+distance from the object's own identity instead, so the
  // same gate/planet/station always lands in the same spot on every visit,
  // but different objects land in visibly different spots from each other.
  function fixedObjectPosition(seed: string): { x: number; y: number } {
    const angle = seededUnit(`${seed}:angle`) * Math.PI * 2;
    const distance = 4000 + seededUnit(`${seed}:dist`) * 2000;
    return { x: Math.round(Math.cos(angle) * distance), y: Math.round(Math.sin(angle) * distance) };
  }

  // Real wreck loot, drawn from the Market's own Salvage (common) and
  // Artifact (rare) catalog categories (src/data/marketItems.ts) --
  // "generate loot tables for wrecks based on their difficulty level and
  // narrative use case," per Don's direction. `source` is the LiveHostile
  // that died here (null for the one pre-existing "Derelict Wreck" flavor
  // object, which gets a flat middling roll instead):
  // - **Difficulty** -- real per-unit dps + combined shield/hull (both
  //   already tiered by 16.1's growth curve for whatever Danger Tier this
  //   is) drive both how many Salvage stacks drop and the odds of a rarer
  //   Artifact stack -- a tougher kill leaves more behind, not a flat roll
  //   regardless of what was actually destroyed.
  // - **Narrative** -- an Alien kill (real unit names like "Alien
  //   Duelist"/"Alien Elite", 16.13) drops Artifact-leaning loot twice as
  //   often as a Pirate kill, matching Section 6's own "aliens... drop
  //   significantly better loot than pirates" rule.
  // Exact counts/odds are still illustrative -- no loot-table numbers are
  // locked anywhere in the design doc -- but the shape (tougher/rarer
  // kills drop more/better) is real, not a uniform random grab-bag.
  function buildWreckLoot(source: LiveHostile | null): Record<string, number> {
    const salvage = itemsByCategory("Salvage");
    const artifact = itemsByCategory("Artifact");
    const power = source ? source.dps + (source.hullMax + source.shieldMax) / 40 : 20;
    const salvageCount = Math.min(6, Math.max(1, Math.round(power / 15)));
    const artifactChance = Math.min(0.7, 0.1 + power / 200);
    const isAlienFlavor = source?.name.toLowerCase().includes("alien") ?? false;

    const loot: Record<string, number> = {};
    for (let i = 0; i < salvageCount; i++) {
      const item = salvage[Math.floor(Math.random() * salvage.length)];
      loot[item.name] = (loot[item.name] ?? 0) + (1 + Math.floor(Math.random() * 5));
    }
    const artifactRolls = isAlienFlavor ? 2 : 1;
    for (let i = 0; i < artifactRolls; i++) {
      if (Math.random() < artifactChance) {
        const item = artifact[Math.floor(Math.random() * artifact.length)];
        loot[item.name] = (loot[item.name] ?? 0) + (1 + Math.floor(Math.random() * 2));
      }
    }
    return loot;
  }

  // Populates fixedObjects/hostiles for whichever real Location the ship
  // is currently in (see buildSystemLocations) -- called once at mount
  // and again every time a Fast Travel/Warp completes. Mutates the
  // existing fixedObjects/hostiles arrays in place (never reassigns them
  // -- destroyHostile/depleteAsteroid/drone targeting all hold the same
  // array references for the lifetime of this screen).
  function rebuildLocationScene(locationId: string): void {
    currentLocationId = locationId;
    state.currentLocationId = locationId;
    state.lastTopLevelView = "expedition";
    const loc = buildSystemLocations(currentSystem).find((l) => l.id === locationId);
    const kind: LocationKind = loc?.kind ?? "planet";
    currentLocationKind = kind;
    activeMissionId = loc?.missionId ?? null;

    fixedObjects.length = 0;
    if (kind === "belt" || kind === "mining") {
      // Real per-node regen (16.53): each node looks up its own persisted
      // pool (state.nodeRegen), regenerated for whatever real wall-clock
      // time has passed since it was last touched, rather than always
      // starting full on every (re-)visit. Node ids are reused verbatim
      // across every belt/mining Location in the galaxy, so the regen key
      // is composite (systemId:locationId:nodeId), not the bare node id.
      const nowMs = Date.now();
      const ast1Key = nodeRegenKey(currentSystem.id, locationId, "ast1");
      const ast2Key = nodeRegenKey(currentSystem.id, locationId, "ast2");
      const ast1Regen = readNodePool(state.nodeRegen, ast1Key, asteroidPoolMaxM3, nowMs);
      const ast2Regen = readNodePool(state.nodeRegen, ast2Key, asteroidPoolMaxM3, nowMs);
      state.nodeRegen = upsertNodeRegen(upsertNodeRegen(state.nodeRegen, ast1Regen), ast2Regen);
      fixedObjects.push(
        {
          id: "ast1",
          name: "Asteroid Node A",
          kind: "asteroid",
          x: 1800,
          y: -2200,
          discovered: false,
          resource: asteroidResource,
          poolM3: ast1Regen.poolM3,
          poolMaxM3: asteroidPoolMaxM3,
          nodeRegenKey: ast1Key
        },
        {
          id: "ast2",
          name: "Asteroid Node B",
          kind: "asteroid",
          x: -2600,
          y: 1400,
          discovered: false,
          resource: asteroidResource,
          poolM3: ast2Regen.poolM3,
          poolMaxM3: asteroidPoolMaxM3,
          nodeRegenKey: ast2Key
        }
      );
    } else if (kind === "missionMining" && activeMissionId) {
      // Real mission-created Location (Don's direction: "a Mission will
      // create a new Location in that system which shows up when we
      // arrive"). Pool is sized generously against the mission's
      // *remaining* quota, not an exact match -- Section 8's asteroid
      // pools are never sized down to precisely what's needed.
      const mission = state.acceptedMissions.find(
        (m): m is AcceptedMiningMission => m.id === activeMissionId && m.type === "Mining"
      );
      const resource = mission?.resource ?? asteroidResource;
      const remaining = mission ? mission.quotaM3 - mission.taggedM3 : asteroidPoolMaxM3;
      const poolMax = Math.max(remaining * 1.5, 50);
      fixedObjects.push(
        { id: "ast1", name: "Asteroid Node A", kind: "asteroid", x: 1800, y: -2200, discovered: false, resource, poolM3: poolMax, poolMaxM3: poolMax },
        { id: "ast2", name: "Asteroid Node B", kind: "asteroid", x: -2600, y: 1400, discovered: false, resource, poolM3: poolMax, poolMaxM3: poolMax }
      );
    } else if (kind === "combat") {
      fixedObjects.push(initialWreck());
    } else if (kind === "gate" && loc) {
      fixedObjects.push({
        id: loc.id,
        name: loc.name,
        kind: "gate",
        ...fixedObjectPosition(`${currentSystem.id}:gate:${loc.jumpToSystemId}`),
        discovered: true,
        jumpToSystemId: loc.jumpToSystemId
      });
    } else if (kind === "planet" && loc?.planetId) {
      // Real per-planet Location (Don's direction, 16.35's planets[]):
      // a large, always-visible backdrop object -- discovered immediately
      // like a Gate, never something you need to scan in first, since a
      // whole planet isn't a hidden signature.
      const planet = currentSystem.planets.find((p) => p.id === loc.planetId);
      if (planet) {
        fixedObjects.push({
          id: planet.id,
          name: planet.name,
          kind: "planet",
          ...fixedObjectPosition(`${currentSystem.id}:planet:${planet.id}`),
          discovered: true,
          planetType: planet.planetType,
          buildable: planet.buildable,
          leaseKey: `${currentSystem.id}:${planet.id}`
        });
      }
    } else if (kind === "station" && loc) {
      // Real Station Location -- also a large, always-visible backdrop
      // object, faction-colored (see stationFactionColor) rather than
      // planet-type-colored.
      fixedObjects.push({
        id: "station-obj",
        name: loc.name,
        kind: "station",
        ...fixedObjectPosition(`${currentSystem.id}:station`),
        discovered: true
      });
    }
    // missionCombat: no pre-placed objects -- its hostiles arrive entirely
    // via the wave system below, and real wrecks drop as they're destroyed.

    currentWaveIndex = 0;
    waveGapUntil = null;
    encounterCleared = false;
    hostiles.length = 0;
    // Hidden-system guardian (16.67): always present, not ambient-random,
    // on the belt/mining Location of one of the 10 hiddenUntilScanned
    // systems -- guarding the guaranteed-Exceptional nodes there.
    if ((kind === "belt" || kind === "mining") && isHiddenSystem(currentSystem.id)) {
      const guardian = spawnHiddenSystemGuardian();
      if (guardian) hostiles.push(guardian);
    }
    // Station garrison (16.94): 5-10 ships at every station, always present.
    // Present even when they will never fire, deliberately -- a visible
    // garrison is the information that makes approaching a hostile station a
    // decision rather than an ambush.
    if (kind === "station") {
      hostiles.push(...spawnStationGuards());
    }
    // Only a real mission-created Location forces a guaranteed, scripted
    // encounter (Don's direction: mission combat must stay deterministic).
    // The ambient "combat" flavor Location used to ALSO force the same
    // scripted PT1-013 wave on every single visit -- Don's later direction
    // ("random NPC encounters... shouldn't automatically show up... we
    // shouldn't always see the same opponents statically showing up in the
    // same locations") removes that: an ambient "combat" Location now
    // relies entirely on the real danger-tier-driven ambient contact system
    // below (maybeTriggerAmbientContact/spawnAmbientGroup, 16.1) exactly
    // like every other non-mission Location already does -- it's no longer
    // mechanically special, just a flavor label in the Scanner UI.
    encounterActive = kind === "missionCombat";
    if (kind === "missionCombat" && activeMissionId) {
      // Real mission-created Location: swap the wave encounter to the
      // specific accepted mission's own encounter (same real Combat
      // Resolution data, src/data/encounters.json, that engageCombat's
      // synchronous fast-forward used to resolve this mission with) --
      // replacing that instant button-click resolution with actually
      // flying here and fighting it out in real time.
      const mission = state.acceptedMissions.find(
        (m): m is AcceptedCombatMission => m.id === activeMissionId && m.type === "Combat"
      );
      const missionEncounter = mission ? loadEncounters().find((e) => e.id === mission.encounterId) : undefined;
      encounter = missionEncounter ?? defaultEncounter;
      waveSegments = parseStageSegments(encounter.composition);
    }
    if (encounterActive) {
      // Resume an interrupted fight rather than restarting it (16.99). Without
      // this, exiting mid-combat was a free escape: damage persisted (16.81) but
      // the enemy did not, so a losing captain could leave and come back to an
      // empty Location. `canRestoreCombat` is what keeps it honest -- it refuses
      // a snapshot from a different place, a different encounter, or a fight
      // whose last hostile was already dead.
      const snap = state.combatSnapshot;
      if (canRestoreCombat(snap, currentSystem.id, state.currentLocationId, encounter.id)) {
        currentWaveIndex = snap!.waveIndex;
        hostiles.push(...survivingHostiles(snap!).map((h) => restoreHostile(h)));
        player.x = snap!.playerX;
        player.y = snap!.playerY;
        state.lastEvent = `Combat resumed — ${hostiles.length} hostile${hostiles.length === 1 ? "" : "s"} still on the field.`;
      } else {
        state.combatSnapshot = null;
        hostiles.push(...buildWaveHostiles(0));
      }
    }

    // A dropped Move To marker is a world-space point in the Location
    // that was just left, so it's meaningless here (16.71) -- clear it,
    // along with any selection/move mode still pointing at it.
    if (marker) {
      if (selected?.id === "marker") {
        selected = null;
      }
      if (moveTarget?.id === "marker") {
        moveTarget = null;
        activeMoveMode = null;
      }
      marker = null;
    }

    // Planetary Defenses (16.70): a fresh scene means fresh locks, and a
    // new random-attack countdown for this System's danger tier.
    defenseLocks.clear();
    rescheduleRandomAttack(performance.now());
    if (defendedPlanetObject()) {
      showBanner("Planetary Defenses online — this space is protected");
    }
  }
  // Populates the initial scene at mount, for whichever real Location the
  // save/state already has the ship in (defaults to "planet-1" for a
  // brand-new save).
  rebuildLocationScene(currentLocationId);
  renderDistressPanel();

  // Drone System (Section 16.23, LOCKED) -- real per-tier capacity/stats,
  // not invented. Individual drones are tracked (each rolls its own
  // attack independently per 16.30, "not a live per-tick" bay-total
  // stat), even though the UI above only shows one aggregate bar for the
  // whole bay (no per-drone health bar is specified anywhere in the doc).
  // "Individual drones are real, ownable, losable assets" (16.23) --
  // destroyed ones are removed permanently, not just knocked to 0.
  const hasDroneBay = shipHasDefaultDroneBay(state.currentShip?.role ?? "");
  interface LiveDrone {
    hp: number;
    maxHp: number;
    dps: number;
    deployed: boolean; // false = recalled/docked -- not fighting, not targetable, heals during gaps
    nextFireAt: number | null;
  }
  const droneHullTier = state.currentShip?.hullTier ?? "T1";
  const droneStatsPerUnit = DRONE_STATS[droneHullTier];
  // The bay is loaded from what the captain is actually CARRYING (16.74),
  // capped by the hull's own capacity -- it's no longer magically full on
  // arrival. A destroyed drone is consumed from cargo for good, so the bay
  // stays short until replacements are bought at a Market.
  const droneItemName = droneEntryForHull(droneHullTier).name;
  const droneRole = state.currentShip?.role ?? "";
  // Only the deployed complement launches; the rest are spares that stay
  // docked until a loss pulls one out (16.87). Previously every loadable drone
  // launched at once, which is what made the bay's DPS scale with its whole
  // capacity instead of with the number actually fighting.
  const droneComplementCount = droneComplement(droneRole);
  const drones: LiveDrone[] = hasDroneBay
    ? Array.from({ length: loadableDroneCount(droneRole, droneHullTier, state.cargo) }, (_unused, i) => ({
        hp: droneStatsPerUnit.ehp,
        maxHp: droneStatsPerUnit.ehp,
        dps: droneStatsPerUnit.dps,
        deployed: i < droneComplementCount,
        nextFireAt: null
      }))
    : [];
  // Illustrative -- DRONE_STATS has no cycle-duration figure, same gap as
  // the player's own modules (see CYCLE_DURATION_MS's comment).
  const DRONE_CYCLE_DURATION_MS = 2200;
  let droneMode: "auto" | "manual" = "auto";
  let droneTargetId: string | null = null;

  let orbit: OrbitPoint | null = null;
  // Double-click-to-engage (Don's direction, items 2/3): a double-clicked
  // Dock/Loot candidate doesn't have an automatic in-range fire cycle the
  // way hostiles/asteroids do (handleDoubleClickTarget arms those directly
  // below), so this remembers "engage this once Move To actually gets us
  // in range" and checkAutoEngage (called every frame) fires the same
  // action the manual Dock/Loot button would once that range check passes.
  let autoEngageTargetId: string | null = null;
  // Engine tapped while the ship already has speed (see the Engine circle
  // handler below) -- an explicit, active brake to a stop, distinct from
  // stepTowardTarget's own gentler "nothing engaged" passive coast.
  let braking = false;
  // Which real Location (within the current System) the Fast Travel list
  // has selected as a destination -- separate from `selected` (the Move
  // To/Orbit/weapon target), since a Location isn't a NamedPoint. The
  // Fast Travel button itself stays disabled until this is set (Don's
  // direction: "I should not be able to click on Fast Travel unless I
  // have selected a target").
  let pendingFastTravelLocationId: string | null = null;
  const ORBIT_NEAR_RADIUS = 450;
  // WARP activation range (Don's explicit number): "activate the Gate
  // with a new WARP button that shows up next to Fast Travel once we are
  // in range of the Gate (1000m)."
  const GATE_WARP_RANGE_M = 1000;
  const ORBIT_FAR_RADIUS = 1200;


  // ---- Combat/mining visual effects (Don's direction: distinct fire
  // animation per weapon type, mining-beam + resource-collection
  // particles, hostile/fleet attack visuals, shield/hull repair pulses)
  // -- purely cosmetic, spawned alongside the real damage/extraction/
  // repair logic below, never driving it. `beam`/`projectile` resolve
  // their live endpoints each frame via id lookups (so a flash still
  // tracks a moving ship correctly); `particle` effects carry their own
  // position/velocity, updated once per frame in frame() and drawn in
  // draw(). Culled once `now - spawnedAt >= durationMs`.
  type FxKind = "beam" | "projectile" | "particle" | "pulse";
  interface FxEffect {
    kind: FxKind;
    weaponKey: string; // "laser" | "auto" | "missile" | "mining" | "hostile" | "fleet" | "shield" | "hull" | "explosion"
    spawnedAt: number;
    durationMs: number;
    sourceId?: string; // beam only -- resolved live each frame
    targetId?: string; // beam/projectile -- resolved live each frame
    fromX?: number; // projectile only -- fixed launch point
    fromY?: number;
    x?: number; // particle only -- live position
    y?: number;
    vx?: number;
    vy?: number;
    seed?: number; // stagger/jitter for multi-tracer bursts (auto-turret)
  }
  let effects: FxEffect[] = [];

  function resolveEffectPoint(id: string): { x: number; y: number } | null {
    if (id === "player") return player;
    return resolveNamedPoint(id);
  }

  // Every spawn* helper below is the single choke point for its effect
  // kind, so the Graphics "combat & mining effects" setting (16.69) is
  // enforced here rather than at ~15 scattered call sites. Audio is
  // deliberately NOT gated by it -- sound and visuals are independent
  // settings, and a captain who turned effects off to reduce visual noise
  // still wants to hear that they're being shot at.
  function spawnBeam(weaponKey: string, sourceId: string, targetId: string, now: number, durationMs: number, seed = 0): void {
    if (!settings.showCombatFx) return;
    effects.push({ kind: "beam", weaponKey, sourceId, targetId, spawnedAt: now, durationMs, seed });
  }

  function spawnProjectile(weaponKey: string, from: Point, target: NamedPoint, now: number, durationMs: number): void {
    if (!settings.showCombatFx) return;
    effects.push({ kind: "projectile", weaponKey, targetId: target.id, fromX: from.x, fromY: from.y, spawnedAt: now, durationMs });
    // A missile's impact reads as a separate beat from its travel -- this
    // companion pulse is scheduled to start the instant the travel phase
    // above ends (a spawnedAt in the future is fine: draw() just skips it
    // until `now` catches up). Falls back to the target's launch-time
    // position (x/y) if it's already been destroyed by the time the
    // explosion actually fires -- a killing blow shouldn't just skip it.
    effects.push({ kind: "pulse", weaponKey: "explosion", targetId: target.id, x: target.x, y: target.y, spawnedAt: now + durationMs, durationMs: 320 });
  }

  function spawnParticle(weaponKey: string, at: Point, toward: Point, now: number, durationMs: number): void {
    if (!settings.showCombatFx) return;
    // Drifts loosely from `at` toward `toward` (asteroid -> ship for mining
    // collection) rather than snapping straight there -- a little jitter so
    // several particles from the same tick don't overlap in a single line.
    const jitterAngle = (Math.random() - 0.5) * 0.9;
    const baseAngle = Math.atan2(toward.y - at.y, toward.x - at.x) + jitterAngle;
    const speed = 400 + Math.random() * 300;
    effects.push({
      kind: "particle",
      weaponKey,
      x: at.x,
      y: at.y,
      vx: Math.cos(baseAngle) * speed,
      vy: Math.sin(baseAngle) * speed,
      spawnedAt: now,
      durationMs
    });
  }

  // ---- Module tap-to-target state (Section 4a, LOCKED) ----
  // The actual tap/cycle/range state machine lives in moduleCycle.ts as
  // pure, unit-tested functions (including 16.30's load-bearing
  // absolute-timestamp rule for countdowns) -- this is just the static
  // shell (label/range/duration) each live instance carries alongside its
  // cycle state.
  interface LiveWeapon {
    key: string;
    label: string;
    range: number;
    cycleDurationMs: number;
    cycle: WeaponCycleState;
  }
  interface LiveSelfModule {
    key: "shield" | "hull";
    cycleDurationMs: number;
    cycle: SelfModuleCycleState;
  }

  const weapons: LiveWeapon[] = WEAPON_SHELLS.map((s) => ({
    key: s.key,
    label: s.label,
    range: s.range!,
    cycleDurationMs: CYCLE_DURATION_MS[s.key],
    cycle: IDLE_WEAPON
  }));
  const selfModules: LiveSelfModule[] = SELF_MODULE_SHELLS.map((s) => ({
    key: s.key as "shield" | "hull",
    cycleDurationMs: CYCLE_DURATION_MS[s.key],
    cycle: IDLE_SELF_MODULE
  }));

  // maxManualTargets (Section 4a, LOCKED): caps how many *different*
  // targets can be actively assigned across manual modules at once --
  // one Target Bar slot per target, not a weapon-count cap. Capital hull
  // has no locked value (the doc only defines Small/Medium/Large), so
  // it's left uncapped rather than inventing a number.
  const MAX_MANUAL_TARGETS: Partial<Record<string, number>> = { Small: 1, Medium: 2, Large: 5 };
  const maxManualTargets = MAX_MANUAL_TARGETS[state.currentShip?.hullTierLabel ?? "Medium"] ?? null;

  function canAssignTarget(targetId: string): boolean {
    if (maxManualTargets === null) return true;
    const locked = new Set(weapons.filter((w) => w.cycle.targetId).map((w) => w.cycle.targetId as string));
    return locked.has(targetId) || locked.size < maxManualTargets;
  }

  function resolveNamedPoint(id: string): NamedPoint | null {
    if (id === "marker") return marker;
    return (
      hostiles.find((o) => o.id === id) ??
      fixedObjects.find((o) => o.id === id) ??
      fleetShipPoints.find((p) => p.id === id) ??
      null
    );
  }

  function resolveHostile(id: string): LiveHostile | undefined {
    return hostiles.find((h) => h.id === id);
  }

  function resolveAsteroid(id: string): FixedObject | undefined {
    return fixedObjects.find((f) => f.id === id && f.kind === "asteroid");
  }

  // Section 16.23/16.30's "Defense" priority for drones: (1) continue the
  // current lock if it's still alive, (2) highest-healing/self-repairing
  // hostile present, (3) highest-DPS hostile present. Tier 2 is skipped
  // here, not just always-empty -- LiveHostile carries no repairPerS
  // field at all (this encounter's real unit data has none self-repairing
  // either way, so it's never actually exercised, but the honest reason
  // it's absent is the data model, not a guaranteed-empty check). Manual
  // mode (captain override, "works like weapon-module targeting") just
  // keeps whatever was last explicitly assigned.
  function updateDroneTargeting(): void {
    if (droneTargetId && resolveHostile(droneTargetId)) {
      return; // current lock still alive -- keep it in both modes
    }
    if (droneMode === "manual") {
      droneTargetId = null; // target died -- captain must reassign, not auto-picked
      return;
    }
    droneTargetId = hostiles.length > 0 ? hostiles.reduce((best, h) => (h.dps > best.dps ? h : best)).id : null;
  }

  // Fast Travel sequence state (Section 2a/4b) -- camera normally follows
  // the player; during the "exiting" stage it freezes in place so the ship
  // can visibly fly out of frame under its own motion.
  let fleeing = false;
  let fleeStage: FleeStage = "turning";
  let fleeHeadingTarget = 0;
  let fleeElapsedS = 0;
  let fleeDestName: string | null = null;
  let fleeJumpToSystemId: string | null = null;
  // Which real Location to land in once this flee sequence completes --
  // set for every Fast Travel/Warp (same-System Location change or a
  // real inter-System jump both end by landing somewhere specific now
  // that a System has multiple real Locations, not one shared scene).
  let fleeTargetLocationId: string = "planet-1";
  let cameraLocked = true;
  let frozenCam: Point = { x: 0, y: 0 };
  let controlsLocked = true; // released once the initial arrival beat fades

  // Cloak (16.61) -- 5s no-attack/no-lock window, 60s cooldown. Both
  // timestamps are on the same rAF `performance.now()` clock frame()
  // already runs on (real-time, not the pausable game clock) -- Cloak
  // only ever matters mid-encounter, so it's fine for it to simply not
  // exist while the Expedition View itself isn't mounted.
  let cloakActiveUntil = 0;
  let cloakReadyAt = 0;
  const CLOAK_DURATION_MS = 5000;
  const CLOAK_COOLDOWN_MS = 60000;
  // Tractor Beam (16.61) -- no cooldown, just a range gate.
  const TRACTOR_BEAM_RANGE_M = 2000;
  const TRACTOR_BEAM_SPEED_MPS = 600;

  // World-space starfield, ported verbatim from the mockup -- generated
  // once and given a slight parallax drift opposite the camera each
  // frame, not regenerated per frame (which would just be visual noise).
  const stars = Array.from({ length: 160 }, () => ({
    x: Math.random() * 3000 - 1500,
    y: Math.random() * 3000 - 1500,
    r: Math.random() * 1.3 + 0.3,
    a: Math.random() * 0.5 + 0.3
  }));

  function scalePxPerMeter(): number {
    const halfPx = (Math.min(canvas.width, canvas.height) / 2) * 0.94;
    return halfPx / ZOOM_RADII[zoom];
  }

  /**
   * Draw radius in PIXELS for a world radius in METRES (16.101).
   *
   * Replaces 16.91's pixel-based os()/objectScalePx, which sized sprites in
   * screen space and so made an object's apparent size in metres depend on the
   * zoom. Zoom is now purely visual for drawing as well as for game logic.
   */
  function wr(radiusM: number): number {
    return drawRadiusPx(radiusM, scalePxPerMeter());
  }

  function cameraOrigin(): Point {
    return cameraLocked ? { x: player.x, y: player.y } : frozenCam;
  }

  function worldToScreen(p: Point): Point {
    const s = scalePxPerMeter();
    const o = cameraOrigin();
    return { x: canvas.width / 2 + (p.x - o.x) * s, y: canvas.height / 2 + (p.y - o.y) * s };
  }

  function screenToWorld(p: Point): Point {
    const s = scalePxPerMeter();
    const o = cameraOrigin();
    return { x: o.x + (p.x - canvas.width / 2) / s, y: o.y + (p.y - canvas.height / 2) / s };
  }

  // Direct-tap target selection (Section 2a's "Move To" input path 1),
  // extended so a tap on empty space drops/moves a marker instead of
  // doing nothing -- the captain can then Move To or Orbit anywhere in
  // the Location, not only objects that happen to be visible. Shared by
  // the main viewport and the Mini-map popup, each passing their own
  // screen<->world projection.
  // Shared by direct-tap selection (viewport/Mini-map) and now the Scanner
  // UI's own object rows (Section 2a: "In This Location" objects are Move
  // To/Orbit candidates, same as tapping them directly) -- one place that
  // applies the actual selection plus the target-then-module weapon-lock
  // order (Section 4a).
  function selectTarget(point: NamedPoint): void {
    // Drop a stale Move To marker the moment the captain targets anything
    // else (16.71). resolveMoveTarget() reads the position off `selected`,
    // so as soon as selection moves away the marker stops being the
    // destination -- leaving its "+" on screen was a purely visual orphan
    // that pointed at nothing, and the arrival-cleanup below could never
    // fire for it either (that path requires the marker to still be
    // selected). Scratch scaffolding for one Move To, nothing more.
    if (marker && point.id !== "marker") {
      marker = null;
    }
    // Targeting anything other than the wreck being looted dismisses the loot
    // popup (Don's direction: clicking outside it, "like selecting another
    // opponent", should close it even if Loot was never pressed).
    //
    // Keyed on selection rather than a document-level outside-click listener:
    // the popup floats over a live canvas the captain is still flying in, so
    // "clicked outside" and "picked a new target" are the same gesture, and
    // this way the popup cannot outlive the wreck it belongs to.
    if (lootPopupWreckId && point.id !== lootPopupWreckId) {
      closeLootPopup();
    }
    selected = point;
    weapons.forEach((w) => {
      if (w.cycle.armed && canAssignTarget(point.id)) {
        w.cycle = lockArmedWeapon(w.cycle, point.id);
      }
    });
    updateEngineButtons();
  }

  // Shared nearest-candidate lookup for both single- and double-click
  // handling, in either the main viewport or the Mini-map popup (each
  // passes its own screen<->world projection). Returns null on empty
  // space -- callers decide what that means (single click drops a
  // marker; double-click just does nothing).
  function findObjectAtWorldPoint(clickScreen: Point, toScreen: (p: Point) => Point, thresholdPx: number): NamedPoint | null {
    let best: NamedPoint | null = null;
    let bestD = thresholdPx;
    const candidates: NamedPoint[] = [...hostiles, ...fixedObjects.filter((f) => f.discovered)];
    fleetShips.forEach((f) => {
      if (!f.fled) candidates.push(fleetShipPoints.find((p) => p.id === f.id)!);
    });
    candidates.forEach((o) => {
      const p = toScreen(o);
      const d = Math.hypot(p.x - clickScreen.x, p.y - clickScreen.y);
      if (d < bestD) {
        bestD = d;
        best = o;
      }
    });
    return best;
  }

  function selectAtWorldPoint(clickScreen: Point, toScreen: (p: Point) => Point, clickWorld: Point, thresholdPx: number): void {
    let best = findObjectAtWorldPoint(clickScreen, toScreen, thresholdPx);
    if (!best) {
      marker = { id: "marker", name: "Marker", x: clickWorld.x, y: clickWorld.y };
      best = marker;
    }
    selectTarget(best);
  }

  // Double-click on a real object (main viewport or Mini-map popup, per
  // Don's direction -- not in the original mockup/design doc): select it,
  // Move To it, and immediately activate whichever module(s) make sense
  // for what it is -- all non-mining weapon modules for an NPC ship, the
  // Mining Laser for an asteroid. A Jump Gate just selects + Move To's
  // like anything else now -- warping through it is a separate, explicit
  // WARP button press once actually in range (see updateEngineButtons),
  // not an automatic side effect of double-clicking. Same
  // maxManualTargets cap as the manual tap-module-then-target gesture.
  function handleDoubleClickTarget(point: NamedPoint): void {
    if (controlsLocked || fleeing) return;
    selectTarget(point);
    braking = false;
    orbit = null;
    // The other deliberate act (16.100): a double-click means go there.
    activeMoveMode = "moveto";
    moveTarget = point;

    const hostile = resolveHostile(point.id);
    const asteroid = resolveAsteroid(point.id);
    const fixedObj = fixedObjects.find((f) => f.id === point.id);
    autoEngageTargetId = null;

    if (hostile) {
      if (!canAssignTarget(point.id)) {
        showBanner(`Target Bar full (${maxManualTargets}/${maxManualTargets}) — deselect a target first`);
      } else {
        weapons.forEach((w) => {
          if (w.key === "mining") return;
          w.cycle = { ...w.cycle, targetId: point.id, armed: false };
        });
      }
    } else if (asteroid) {
      const mining = weapons.find((w) => w.key === "mining");
      if (mining) {
        if (!canAssignTarget(point.id)) {
          showBanner(`Target Bar full (${maxManualTargets}/${maxManualTargets}) — deselect a target first`);
        } else {
          mining.cycle = { ...mining.cycle, targetId: point.id, armed: false };
        }
      }
    } else if (fixedObj && (fixedObj.kind === "planet" || fixedObj.kind === "station")) {
      // Attacking/mining fire the instant the weapon/mining cycle is armed
      // and in range (existing behavior above) -- docking has no such
      // cycle, so remember the intent and checkAutoEngage (per-frame)
      // fires the real Dock action once Move To actually gets us there.
      autoEngageTargetId = fixedObj.id;
    } else if (fixedObj && fixedObj.kind === "wreck" && fixedObj.loot && Object.keys(fixedObj.loot).length > 0) {
      autoEngageTargetId = fixedObj.id;
    }
    updateEngineButtons();
  }

  /**
   * How close the ship should try to get to what it is flying at (16.101).
   *
   * A flat 150m worked while nothing was solid. Now that ships cannot enter an
   * asteroid, arriving at 150m and being pushed back to 270m every frame would
   * be a permanent argument between the mover and the separation pass -- so the
   * mover is told the real answer up front. Only asteroids are solid among fixed
   * objects; a hostile is solid too, and stopping outside it beats grinding
   * against it.
   */
  function stopDistanceFor(target: NamedPoint | null): number {
    if (!target) return player.stopDistance;
    const asteroid = fixedObjects.find((f) => f.id === target.id && f.kind === "asteroid");
    if (asteroid) return Math.max(player.stopDistance, SHIP_TO_ASTEROID_CLEARANCE_M);
    if (hostiles.some((h) => h.id === target.id)) {
      return Math.max(player.stopDistance, SHIP_TO_SHIP_CLEARANCE_M);
    }
    return player.stopDistance;
  }

  function resolveMoveTarget(): Point | null {
    // 16.100: keyed on moveTarget rather than selected, so looking at something
    // is no longer the same as flying at it.
    if (!activeMoveMode || !moveTarget) {
      return null;
    }
    if (activeMoveMode === "moveto") {
      return { x: moveTarget.x, y: moveTarget.y };
    }
    return orbit ? orbitPointPosition(moveTarget, orbit) : null;
  }

  function updateEngineButtons(): void {
    wrap.querySelectorAll<HTMLButtonElement>(".xv-engine-cluster button[data-cmd]").forEach((btn) => {
      const cmd = btn.dataset.cmd!;
      if (cmd === "warp") {
        // Only the current Location's own Gate object, and only once
        // actually within WARP range (Don's explicit 1000m) -- this is
        // the one real action that changes System (see beginFlee's
        // jumpToSystemId handling in the click listener above).
        const gate = selected && fixedObjects.find((f) => f.id === selected!.id && f.kind === "gate");
        const inRange = !!gate && Math.hypot(gate.x - player.x, gate.y - player.y) <= GATE_WARP_RANGE_M;
        const hasFuel = state.fuelOnBoard >= warpFuelCost(state.currentShip?.hullTierLabel ?? "Medium");
        btn.disabled = controlsLocked || fleeing || !inRange || !hasFuel;
        // 16.104: a Warp is the most expensive thing a captain can press -- 5x a
        // Fast Travel -- and it strands them in a DIFFERENT system if it empties
        // the tank. That makes it the button that most needs its price on it.
        setTravelLabel(btn, "WARP", travelPreviewFor("warp"));
        return;
      }
      if (cmd === "cloak") {
        // 16.61: not target-dependent (unlike the generic fallback below),
        // gated only by controlsLocked/fleeing and its own 60s cooldown.
        btn.disabled = controlsLocked || fleeing || performance.now() < cloakReadyAt;
        return;
      }
      if (cmd === "tractorBeam") {
        // 16.61: no cooldown, no target selection needed -- just needs at
        // least one lootable wreck within 2000m to actually pull.
        const anyWreckInRange = fixedObjects.some(
          (f) => f.kind === "wreck" && f.loot && Object.keys(f.loot).length > 0 && Math.hypot(f.x - player.x, f.y - player.y) <= TRACTOR_BEAM_RANGE_M
        );
        btn.disabled = controlsLocked || fleeing || !anyWreckInRange;
        return;
      }
      if (cmd === "loot") {
        const wreck = selected && fixedObjects.find((f) => f.id === selected!.id && f.kind === "wreck" && f.loot && Object.keys(f.loot).length > 0);
        const inRange = !!wreck && Math.hypot(wreck.x - player.x, wreck.y - player.y) <= player.stopDistance;
        btn.disabled = controlsLocked || fleeing || !inRange;
        return;
      }
      if (cmd === "buyLease") {
        // Founding a Base (Don's direction: "price of a lease should be set
        // to 10,000 credits for now") -- only ever offered for a buildable
        // planet with no existing lease. No range requirement, matching
        // the design doc's "Dock at the planet if they already have a
        // lease" wording (only Dock itself, not the purchase, implies
        // proximity).
        const planet = selected && fixedObjects.find((f) => f.id === selected!.id && f.kind === "planet");
        const eligible = !!planet && planet.buildable && !hasLease(planet.leaseKey);
        btn.disabled = controlsLocked || fleeing || !eligible;
        return;
      }
      if (cmd === "dock") {
        const obj = selected && fixedObjects.find((f) => f.id === selected!.id && (f.kind === "planet" || f.kind === "station"));
        let canDock = false;
        if (obj?.kind === "planet") {
          canDock = hasLease(obj.leaseKey);
        } else if (obj?.kind === "station") {
          canDock = Math.hypot(obj.x - player.x, obj.y - player.y) <= STATION_DOCK_RANGE_M;
        }
        btn.disabled = controlsLocked || fleeing || !canDock;
        btn.textContent = obj?.kind === "station" ? "Dock (Station)" : "Dock (Base)";
        return;
      }
      btn.disabled = controlsLocked || fleeing || !selected;
      btn.classList.toggle("active", !fleeing && cmd === activeMoveMode);
    });
    const engineCircle = moduleCircleEls.get("engine")?.circle;
    if (engineCircle) {
      engineCircle.classList.toggle("moduleOff", !activeMoveMode);
    }
    updateFastTravelButton();
  }

  // Fast Travel slide-out (Don's direction, item 1): docked out of sight
  // to the right, behind the Scanner UI panel, until a Fast Travel row is
  // actually selected -- then it slides out to sit directly to the left
  // of THAT row (not the panel as a whole), tracking it if the list is
  // re-sorted/re-scrolled. Rendered as a sibling of .xv-scanner-col (not
  // nested inside it) so it can visually extend past that panel's own
  // `overflow: hidden` -- position is computed against .xv-wrap (the one
  // positioned ancestor both share) via getBoundingClientRect rather than
  // plain CSS, since the target row's position isn't known until render.
  /**
   * A travel button with its price under its name (16.104).
   *
   * Two lines rather than one long string: the verb has to stay instantly
   * readable at a glance mid-combat, and the cost has to be legible without
   * competing with it.
   */
  function travelButtonLabel(verb: string, p: TravelCostPreview): string {
    const cls = !p.affordable ? "xv-travel-cost-bad" : p.lastJump ? "xv-travel-cost-warn" : "";
    return `<span class="xv-travel-verb">${verb}</span><span class="xv-travel-cost ${cls}">${travelCostLabel(p)}</span>`;
  }

  /**
   * Write a travel button label ONLY when it has changed (16.105).
   *
   * 16.104 put the price inside these buttons with a plain innerHTML assignment,
   * and both of them are refreshed by updateEngineButtons -- which runs every
   * frame. So the label spans were destroyed and recreated ~60 times a second,
   * and a click never completed: the element that received pointerdown no longer
   * existed at pointerup, so the browser had nothing to fire click on. WARP and
   * the Fast Travel slide-out both became visually perfect and completely inert.
   *
   * The guard is the fix -- the label changes only when fuel does, which is a few
   * times a session rather than sixty times a second.  on
   * the spans is the belt to this braces: it keeps the BUTTON the event target
   * whatever else is done to its children later.
   */
  const writeTravelLabel = createLabelWriter();
  function setTravelLabel(btn: HTMLElement, verb: string, p: TravelCostPreview): void {
    writeTravelLabel(btn, travelButtonLabel(verb, p));
  }
  function travelPreviewFor(kind: TravelKind): TravelCostPreview {
    return travelCostPreview(kind, state.currentShip?.hullTierLabel ?? "Medium", state.fuelOnBoard);
  }

  function updateFastTravelButton(): void {
    const ftSlideout = wrap.querySelector<HTMLButtonElement>("[data-ft-slideout]")!;
    const hasFastTravelFuel = state.fuelOnBoard >= fastTravelFuelCost(state.currentShip?.hullTierLabel ?? "Medium");
    ftSlideout.disabled = controlsLocked || fleeing || !pendingFastTravelLocationId || !hasFastTravelFuel;
    ftSlideout.classList.toggle("active", fleeing);
    // 16.104 (Don): the price on the button, before it is pressed. The Distress
    // Signal (16.54) rescues a stranded captain; nothing used to PREVENT the
    // strand. Showing the cost and what is left after it turns a rescue into a
    // decision -- and it costs a captain nothing to read.
    setTravelLabel(ftSlideout, "FAST TRAVEL", travelPreviewFor("fastTravel"));

    const panel = wrap.querySelector<HTMLElement>(".xv-scanner-col")!;
    const wrapRect = wrap.getBoundingClientRect();
    const dockedRight = `${wrapRect.right - panel.getBoundingClientRect().left}px`;

    const list = wrap.querySelector<HTMLElement>("[data-sc-list]")!;
    const row = pendingFastTravelLocationId
      ? list.querySelector<HTMLElement>(`.xv-sc-item.system[data-location-id="${pendingFastTravelLocationId}"]`)
      : null;
    if (!row) {
      ftSlideout.style.right = dockedRight;
      ftSlideout.classList.remove("show");
      return;
    }
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.bottom <= listRect.top || rowRect.top >= listRect.bottom) {
      // Selected row has scrolled out of the list's visible area -- stay
      // tucked away rather than floating next to a row the captain can't
      // actually see right now.
      ftSlideout.style.right = dockedRight;
      ftSlideout.classList.remove("show");
      return;
    }
    ftSlideout.style.top = `${rowRect.top - wrapRect.top + rowRect.height / 2 - ftSlideout.offsetHeight / 2}px`;
    ftSlideout.style.right = `${wrapRect.right - panel.getBoundingClientRect().left + 6}px`;
    ftSlideout.classList.add("show");
  }
  wrap.querySelector<HTMLElement>("[data-sc-list]")!.addEventListener("scroll", () => {
    if (pendingFastTravelLocationId) updateFastTravelButton();
  });

  // Clicking anywhere else in the Expedition View deselects the pending
  // Fast Travel destination and slides the button back out of sight
  // (Don's direction) -- everything EXCEPT the slide-out button itself
  // (clicking it triggers the trip) and other Fast Travel rows (clicking
  // one just swaps the selection to it, handled by handleFastTravelClick
  // above, which runs first since it's bound to a descendant of `wrap`).
  wrap.addEventListener("click", (ev) => {
    if (!pendingFastTravelLocationId) return;
    const target = ev.target as HTMLElement;
    if (target.closest("[data-ft-slideout]") || target.closest(".xv-sc-item.system")) return;
    pendingFastTravelLocationId = null;
    renderScannerList();
    updateFastTravelButton();
  });

  // Every in-world banner is, by construction, something the captain needs
  // to notice right now (hostiles inbound, hazard incoming, mutiny warning,
  // drone lost) -- so the alert tone rides along here rather than being
  // hand-wired at each of the ~20 call sites (16.69).
  function showBanner(text: string): void {
    playSound("alert");
    const banner = wrap.querySelector<HTMLElement>("[data-banner]")!;
    banner.textContent = text;
    banner.classList.add("show");
    setTimeout(() => banner.classList.remove("show"), 2400); // --sf-banner-fade-seconds
  }

  // Distress Signal (16.54) -- a real rescue mechanic for a ship stranded
  // with insufficient fuel to Fast Travel/Warp anywhere. `tickDistressSignal`
  // re-rolls the 25%-per-check chance every 2 real-clock minutes until a
  // rescuer answers (src/distressSignal.ts, pure/testable); this file only
  // wires that state into the panel and the credits/fuel transaction once
  // an offer is accepted.
  function isStranded(): boolean {
    const hullTierLabel = state.currentShip?.hullTierLabel ?? "Medium";
    return state.fuelOnBoard < fastTravelFuelCost(hullTierLabel);
  }

  function sendDistressSignal(): void {
    if (state.distressSignal.active) return;
    state.distressSignal = activateDistressSignal(effectiveNow(state.clock, Date.now()));
    showBanner("Distress Signal sent -- broadcasting on all frequencies…");
    renderDistressPanel();
  }

  function cancelDistress(): void {
    state.distressSignal = cancelDistressSignal();
    renderDistressPanel();
  }

  function buyDistressFuel(): void {
    const markup = state.distressSignal.markup;
    if (markup === null) return;
    const needed = state.fuelCapacity - state.fuelOnBoard;
    if (needed <= 0) return;
    const pricePerUnit = marketPricePerUnit("Fuel") * markup;
    const affordableUnits = Math.floor(state.credits / pricePerUnit);
    const units = Math.min(needed, affordableUnits);
    if (units <= 0) {
      state.lastEvent = "Not enough credits to buy any fuel from the rescuer.";
      renderDistressPanel();
      return;
    }
    const cost = Math.round(units * pricePerUnit);
    state.credits -= cost;
    state.fuelOnBoard += units;
    state.lastEvent = `Bought ${units} fuel from a rescuer for ${cost.toLocaleString()} CR (${markup.toFixed(1)}x markup).`;
    if (state.fuelOnBoard >= state.fuelCapacity) {
      state.distressSignal = cancelDistressSignal();
    }
    triggerSave(ctx);
    renderDistressPanel();
    updateEngineButtons();
  }

  function renderDistressPanel(): void {
    const panel = wrap.querySelector<HTMLElement>("[data-distress-panel]")!;
    const signal = state.distressSignal;
    if (!signal.active && !isStranded()) {
      panel.style.display = "none";
      return;
    }
    panel.style.display = "";
    if (!signal.active) {
      panel.innerHTML = `
        <div>Stranded -- not enough fuel to Fast Travel or Warp.</div>
        <button type="button" data-send-distress>Send Distress Signal</button>
      `;
    } else if (signal.markup === null) {
      panel.innerHTML = `
        <div>Distress Signal active -- awaiting a response…</div>
        <button type="button" data-cancel-distress>Cancel</button>
      `;
    } else {
      const pricePerUnit = marketPricePerUnit("Fuel") * signal.markup;
      panel.innerHTML = `
        <div>A rescuer answers -- offering fuel at ${signal.markup.toFixed(1)}x markup (${pricePerUnit.toFixed(1)} CR/unit).</div>
        <button type="button" data-buy-distress-fuel>Buy Fuel</button>
        <button type="button" data-cancel-distress>Decline</button>
      `;
    }
    panel.querySelector<HTMLButtonElement>("[data-send-distress]")?.addEventListener("click", sendDistressSignal);
    panel.querySelector<HTMLButtonElement>("[data-cancel-distress]")?.addEventListener("click", cancelDistress);
    panel.querySelector<HTMLButtonElement>("[data-buy-distress-fuel]")?.addEventListener("click", buyDistressFuel);
  }

  // state.lastEvent/state.saveNotice are the app-wide transient banners,
  // but they're only ever *rendered* by renderHeader (components.ts) --
  // which this screen deliberately doesn't use, since the Expedition View
  // is a full-viewport HUD with no header. Without this, anything setting
  // either while the captain is out in space was silently invisible: a
  // "Save failed: ..." error, or (worse) the "server wins" conflict
  // notice explaining why the world just rebuilt around them mid-flight
  // (persistence.ts's doSave). Drained once per frame -- the same "check
  // fresh every tick, no separate polling loop" pattern already used for
  // wave progress and ambient contacts -- and cleared on consumption so
  // each shows exactly once here rather than re-firing every frame or
  // again on the next screen. saveNotice wins if both are pending: it
  // explains a wholesale state replacement, which matters more than
  // whatever routine event preceded it.
  /**
   * Keep `state.combatSnapshot` current so ANY save picks the fight up (16.99).
   *
   * Same strategy as `persistShipDamage` above and for the same reason: the
   * captain can leave this screen by a dozen paths, and auditing every one for a
   * save-on-exit hook is how you end up with the one that got missed. Writing it
   * continuously means no exit path has to know it exists.
   *
   * Throttled to twice a second rather than per-frame, because unlike
   * persistShipDamage's two numbers this allocates an object per hostile, and a
   * fight the captain could act on inside 500ms does not exist.
   *
   * Cleared -- not merely left stale -- the moment there is nothing to resume.
   * A stale snapshot is the one failure mode with teeth: it would repopulate a
   * cleared encounter on the next visit.
   */
  // Armed by the first Save & Exit press during a live fight; disarmed the
  // moment the fight ends, so it can never be left primed for a later,
  // out-of-combat press that deserved no warning.
  let exitConfirmArmed = false;
  combatExitGuard = () => {
    if (!state.combatSnapshot || exitConfirmArmed) return false;
    exitConfirmArmed = true;
    showBanner("Fight in progress — it will be saved and resume exactly here. Press 💾 again to exit.");
    return true;
  };

  let nextSnapshotAt = 0;
  function persistCombatSnapshot(now: number): void {
    if (now < nextSnapshotAt) return;
    nextSnapshotAt = now + 500;
    const live = hostiles.filter((h) => !h.guard && h.hull > 0);
    if (!encounterActive || encounterCleared || live.length === 0) {
      state.combatSnapshot = null;
      exitConfirmArmed = false;
      return;
    }
    state.combatSnapshot = {
      version: COMBAT_SNAPSHOT_VERSION,
      systemId: currentSystem.id,
      locationId: state.currentLocationId,
      encounterId: encounter.id,
      waveIndex: currentWaveIndex,
      hostiles: live.map((h) => ({
        id: h.id,
        name: h.name,
        faction: h.faction,
        hullTier: h.hullTier,
        x: h.x,
        y: h.y,
        shield: h.shield,
        shieldMax: h.shieldMax,
        hull: h.hull,
        hullMax: h.hullMax,
        dps: h.dps
      })),
      playerX: player.x,
      playerY: player.y
    };
  }

  /**
   * Push apart anything that ended the frame overlapping (16.100).
   *
   * The player is in the list like everything else. That is deliberate: a
   * hostile ramming the ship should displace both, not phase through. Because
   * `ASTEROID_CLEARANCE_M` (140m) sits inside the player's own 150m stop
   * distance, an approach never fights this -- the ship stops before the rock
   * ever pushes back.
   */
  function separateBodies(): void {
    const shipRadius = SHIP_SEPARATION_M / 2;
    const bodies: SeparationBody[] = [
      { id: "player", x: player.x, y: player.y, movable: true, radius: shipRadius }
    ];
    hostiles.forEach((h) => bodies.push({ id: h.id, x: h.x, y: h.y, movable: true, radius: shipRadius }));
    fleetShips.forEach((f) => {
      if (!f.fled) bodies.push({ id: f.id, x: f.x, y: f.y, movable: true, radius: shipRadius });
    });
    // Asteroids are immovable, and carry the whole ship-to-rock clearance
    // themselves so a rock-to-rock pair (both immovable) is never considered.
    fixedObjects.forEach((f) => {
      if (f.kind === "asteroid") {
        bodies.push({ id: f.id, x: f.x, y: f.y, movable: false, radius: ASTEROID_CLEARANCE_M - shipRadius });
      }
    });

    const moved = resolveSeparation(bodies);
    if (moved.size === 0) return;

    const p = moved.get("player");
    if (p) {
      player.x = p.x;
      player.y = p.y;
    }
    hostiles.forEach((h) => {
      const m = moved.get(h.id);
      if (m) {
        h.x = m.x;
        h.y = m.y;
      }
    });
    fleetShips.forEach((f) => {
      const m = moved.get(f.id);
      if (!m) return;
      f.x = m.x;
      f.y = m.y;
      // The drawn point is a separate object from the kinematic body, and a
      // stale one would leave the sprite behind where the hull used to be.
      const point = fleetShipPoints.find((q) => q.id === f.id);
      if (point) {
        point.x = f.x;
        point.y = f.y;
      }
    });
  }

  function drainGlobalEvent(): void {
    // Only when the Expedition View is the screen actually being looked
    // at. An overlay (Cargo/Fleet/Market/Board/Diary) keeps this frame
    // loop running underneath it (16.36), but those screens render their
    // own header banner -- draining here would consume the message before
    // that header could show it, and stack a second copy behind the
    // panel.
    if (state.screen !== "expedition") return;
    // And not while anything is covering the screen (16.98). Neither message is
    // cleared here, so both simply wait -- this loop re-checks every frame, and
    // the first frame after the last overlay clears is when they land.
    if (isScreenTransitioning()) return;
    if (state.saveNotice) {
      showBanner(state.saveNotice);
      state.saveNotice = null;
      state.lastEvent = null;
      return;
    }
    if (state.lastEvent) {
      showBanner(state.lastEvent);
      state.lastEvent = null;
    }
  }

  // Fast Travel/Warp (Section 2a) -- this is also how fleeing/disengaging
  // works, there is no separate mechanic. Always succeeds barring
  // destruction mid-sequence (not modeled yet, since there's no combat
  // tick here). `targetLocationId` is where the ship lands once this
  // completes -- a same-System Location for an ordinary Fast Travel, or
  // the new System's own entry Location when `jumpToSystemId` is set
  // (an actual WARP through a Gate).
  function beginFlee(destName: string | null, jumpToSystemId: string | null, targetLocationId: string): void {
    if (fleeing || controlsLocked) {
      return;
    }
    // Fuel is deducted at commit-to-travel time, not on arrival (16.50) --
    // the turn/accelerate/exit sequence always succeeds once started, so
    // there's no mid-flight failure state that would make charging on
    // arrival meaningfully different, and this keeps the deduction next to
    // the other state this same commit point already locks in
    // (fleeJumpToSystemId/fleeTargetLocationId below).
    const hullTierLabel = state.currentShip?.hullTierLabel ?? "Medium";
    const fuelCost = jumpToSystemId ? warpFuelCost(hullTierLabel) : fastTravelFuelCost(hullTierLabel);
    if (state.fuelOnBoard < fuelCost) {
      return;
    }
    state.fuelOnBoard -= fuelCost;
    fleeing = true;
    fleeStage = "turning";
    fleeHeadingTarget = Math.random() * Math.PI * 2;
    fleeElapsedS = 0;
    fleeDestName = destName;
    fleeJumpToSystemId = jumpToSystemId;
    fleeTargetLocationId = targetLocationId;
    cameraLocked = true;
    activeMoveMode = null;
    moveTarget = null;
    orbit = null;
    braking = false;

    const overlay = wrap.querySelector<HTMLElement>("[data-depart-overlay]")!;
    const text = wrap.querySelector<HTMLElement>("[data-depart-text]")!;
    overlay.classList.add("show");
    text.textContent = "Turning to new heading…";
    showBanner(destName ? `Fast Traveling to ${destName}…` : "Attempting to disengage — Fast Travel");
    updateEngineButtons();
  }

  // Runs both on initial mount and after a Fast Travel completes -- there's
  // still no per-System Location roster in the data model (see file
  // header), so "arriving at a new Location" resets this same illustrative
  // scene, just re-seeded from the real current System's own resource/
  // dangerTier when the Fast Travel was a real inter-System jump.
  function playArrivalBeat(): void {
    controlsLocked = true;
    // The arrival beat is an opaque overlay, so it is a transition for message
    // purposes too (16.98): anything drained while it is up chimes at a black
    // screen and expires unread. Released only after the fade completes, not
    // when the controls unlock, because the last 650ms is still opaque enough
    // to lose a banner in.
    beginScreenTransition();
    const arrival = wrap.querySelector<HTMLElement>("[data-arrival]")!;
    arrival.style.display = "flex";
    arrival.style.opacity = "1";
    updateEngineButtons();
    setTimeout(() => {
      arrival.style.opacity = "0";
      setTimeout(() => {
        arrival.style.display = "none";
        endScreenTransition();
      }, 650);
      controlsLocked = false;
      updateEngineButtons();
    }, 2600); // --sf-arrival-fade-seconds
  }

  const moduleCircleEls = new Map<string, { circle: HTMLElement; arcProgress: SVGCircleElement | null }>();
  wrap.querySelectorAll<HTMLElement>("[data-mod-key]").forEach((el) => {
    moduleCircleEls.set(el.dataset.modKey!, {
      circle: el.querySelector<HTMLElement>("[data-mod-circle]")!,
      arcProgress: el.querySelector<SVGCircleElement>("[data-mod-arc-progress]")
    });
  });

  // Expand/minimize (Section 4a, LOCKED) -- "multiple modules may be
  // expanded simultaneously," "stays expanded until the captain
  // explicitly minimizes it." See rackModuleHtml's comment for why the
  // stat readout, not the circle, is the trigger.
  const expandedKeys = new Set<string>();
  // The module the pointer is currently over, if any. Separate from
  // expandedKeys so hovering never un-pins a panel the captain tapped open.
  let hoveredKey: string | null = null;
  const expandEls = new Map<string, { panel: HTMLElement; stats: HTMLElement }>();
  wrap.querySelectorAll<HTMLElement>("[data-mod-key]").forEach((el) => {
    const key = el.dataset.modKey!;
    const panel = el.querySelector<HTMLElement>("[data-mod-expand]")!;
    const stats = el.querySelector<HTMLElement>("[data-mod-expand-stats]")!;
    expandEls.set(key, { panel, stats });
    const closeBtn = el.querySelector<HTMLElement>("[data-mod-expand-close]")!;
    // Hover shows the stats as well as tapping (16.103, Don). The tap-to-pin
    // behaviour is unchanged and still what a tablet uses -- hover is additive,
    // and a pinned panel is never closed by the mouse leaving it, or reading a
    // module would fight with reaching for the button next to it.
    el.addEventListener("pointerenter", () => {
      if (expandedKeys.has(key)) return;
      hoveredKey = key;
      panel.classList.add("show");
      updateExpandedStats();
    });
    el.addEventListener("pointerleave", () => {
      if (hoveredKey === key) hoveredKey = null;
      if (!expandedKeys.has(key)) panel.classList.remove("show");
    });
    el.querySelector<HTMLElement>("[data-mod-stat-trigger]")!.addEventListener("click", () => {
      if (expandedKeys.has(key)) {
        expandedKeys.delete(key);
      } else {
        expandedKeys.add(key);
      }
      panel.classList.toggle("show", expandedKeys.has(key));
    });
    closeBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      expandedKeys.delete(key);
      panel.classList.remove("show");
    });
  });

  /** Pinned by a tap, or merely hovered -- either way its stats are on screen. */
  function isShowingStats(key: string): boolean {
    return expandedKeys.has(key) || hoveredKey === key;
  }

  function statRow(label: string, value: string): string {
    return `<div><span>${label}</span><span>${value}</span></div>`;
  }

  // Expanded-view stats per module type (Section 4a's exact list per
  // type). Hostile count/incoming DPS are approximations: the combat tick
  // doesn't persistently track "who's currently targeting this ship,"
  // only rolls a per-shot target choice, so this counts all alive
  // hostiles as potential incoming fire rather than a precise live tally.
  function updateExpandedStats(): void {
    weapons.forEach((w) => {
      if (!isShowingStats(w.key)) return;
      const stats = expandEls.get(w.key)!.stats;
      const target = w.cycle.targetId ? resolveNamedPoint(w.cycle.targetId) : null;
      const distance = target ? Math.hypot(target.x - player.x, target.y - player.y) : null;

      if (w.key === "mining") {
        const asteroid = w.cycle.targetId ? resolveAsteroid(w.cycle.targetId) : undefined;
        const pctRemaining = asteroid ? Math.round(((asteroid.poolM3 ?? 0) / (asteroid.poolMaxM3 ?? 1)) * 100) : null;
        stats.innerHTML =
          statRow("Installed", "1×") +
          statRow(
            "Extraction rate",
            `${effectiveYieldRate(state.currentShip?.hullTier ?? "T1", state.captainSkills.Mining).toFixed(3)} m3/s`
          ) +
          statRow("Range to target", distance !== null ? `${Math.round(distance)}m` : "—") +
          statRow("Max range", `${w.range}m`) +
          statRow("% of target remaining", pctRemaining !== null ? `${pctRemaining}%` : "—");
        return;
      }

      const hostileTarget = w.cycle.targetId ? resolveHostile(w.cycle.targetId) : undefined;
      const pctRemaining = hostileTarget
        ? Math.round(((hostileTarget.shield + hostileTarget.hull) / (hostileTarget.shieldMax + hostileTarget.hullMax)) * 100)
        : null;
      const level = state.currentShipInstanceId
        ? weaponLevel(state.weaponUpgrades, state.currentShipInstanceId, w.key as WeaponKey)
        : 1;
      const effectiveDps = WEAPON_DPS[w.key] * WEAPON_LEVEL_MULTIPLIER[level];
      stats.innerHTML =
        statRow("Installed", "1×") +
        statRow("Avg DPS/rate", `${effectiveDps.toFixed(1)} (Lv.${level})`) +
        statRow("Range to target", distance !== null ? `${Math.round(distance)}m` : "—") +
        statRow("Max range", `${w.range}m`) +
        statRow("Target remaining", pctRemaining !== null ? `${pctRemaining}%` : "—");
    });

    selfModules.forEach((m) => {
      if (!isShowingStats(m.key)) return;
      const stats = expandEls.get(m.key)!.stats;
      const repairTarget = m.cycle.targetId ? fleetShipById(m.cycle.targetId) : undefined;
      const perSecond = m.key === "shield" ? (repairTarget?.shieldRegenPerS ?? shieldRegenPerS) : (repairTarget?.repairPerS ?? hullRepairPerS);
      const perCycle = Math.round(perSecond * (m.cycleDurationMs / 1000));
      const hpNow = repairTarget ? (m.key === "shield" ? repairTarget.shield : repairTarget.hull) : m.key === "shield" ? player.shield : player.hull;
      const incomingDps = hostiles.reduce((sum, h) => sum + h.dps, 0);
      const timeToDepleted = incomingDps > 0 ? `${Math.round(hpNow / incomingDps)}s` : "∞";
      // Hull repair's real Ferrite cost (16.80), so the captain can see how
      // many more cycles the hold actually pays for before committing.
      const ferriteAboard = state.cargo[REPAIR_ORE] ?? 0;
      const perCycleOre = ferritePerCycle(perSecond, m.cycleDurationMs);
      const oreRows =
        m.key === "hull"
          ? statRow(`${REPAIR_ORE}/cycle`, perCycleOre.toFixed(1)) +
            statRow(
              `${REPAIR_ORE} aboard`,
              `${Math.floor(ferriteAboard)} (${perCycleOre > 0 ? Math.floor(ferriteAboard / perCycleOre) : 0} cycles)`
            )
          : "";
      // 16.101: with a cooldown the per-cycle figure alone is misleading -- what
      // decides whether a fight is survivable is the SUSTAINED rate, so show both
      // and let the captain compare it against incoming DPS two rows down.
      const cooldownMs = SELF_MODULE_COOLDOWN_MS[m.key] ?? 0;
      const intervalMs = m.cycleDurationMs + cooldownMs;
      const cooldownRows =
        cooldownMs > 0
          ? statRow("Cooldown after cycle", `${(cooldownMs / 1000).toFixed(0)}s`) +
            statRow("Sustained repair", `${(perCycle / (intervalMs / 1000)).toFixed(1)} HP/s`)
          : "";
      stats.innerHTML =
        statRow(`${m.key === "shield" ? "Shield" : "Hull"} restored/cycle`, String(perCycle)) +
        cooldownRows +
        oreRows +
        statRow("Hostiles locked on", String(hostiles.length)) +
        statRow("Incoming DPS", String(Math.round(incomingDps))) +
        statRow("Time until depleted", timeToDepleted);
    });

    if (isShowingStats("engine")) {
      const stats = expandEls.get("engine")!.stats;
      // Thrust isn't modeled anywhere in the game yet, and the
      // keep-distance slider's actual near/far distances are explicitly
      // "still TBD" per Section 15 -- shown honestly rather than invented.
      // Fuel is real now (16.50).
      // 16.103 (Don): the Engine is where fuel consumption belongs, because the
      // Engine is what spends it. The pool was already shown; what a captain
      // needs is what it BUYS -- so the costs and the jumps remaining are here
      // too, expressed in jumps rather than fuel units, since "how many more
      // places can I go" is the actual question and nobody converts 350 fuel
      // into that mid-flight.
      //
      // Move To costs nothing and is stated as such, because its absence from
      // the list is otherwise indistinguishable from an oversight.
      const fuel = fuelSummary(state.currentShip?.hullTierLabel ?? "Medium", state.fuelOnBoard, state.fuelCapacity);
      stats.innerHTML =
        statRow("Speed", `${Math.round(player.speed)} m/s`) +
        statRow("Thrust", "n/a (not modeled)") +
        statRow("Fuel", `${Math.round(fuel.onBoard)} / ${Math.round(fuel.capacity)}`) +
        statRow("Range left", fuelHeadline(fuel)) +
        statRow("Fast Travel costs", `${fuel.fastTravelCost} fuel`) +
        statRow("Warp costs", `${fuel.warpCost} fuel`) +
        statRow("Move To / orbit costs", "nothing");
      // The old "Keep-distance slider: not implemented" row is gone --
      // 16.57 confirmed that slider was SUPERSEDED by the real Orbit
      // Near/Orbit Far buttons, not left unbuilt, so advertising it as
      // missing was misleading.
    }
  }
  // Module tap-to-target state machine (Section 4a, LOCKED) -- see
  // moduleCycle.ts for the actual rules (armed/pending, target-then-module
  // vs. module-then-target).
  weapons.forEach((w) => {
    moduleCircleEls.get(w.key)!.circle.addEventListener("click", () => {
      if (controlsLocked || fleeing) return;
      const selId = selected?.id ?? null;
      if (selId && !w.cycle.targetId && !canAssignTarget(selId)) {
        showBanner(`Target Bar full (${maxManualTargets}/${maxManualTargets}) — deselect a target first`);
        return;
      }
      w.cycle = tapWeapon(w.cycle, selId);
    });
  });
  // Which ship a Shield/Hull tap should actually target -- "player", or
  // whatever fleet ship's own id is currently selected (Section 4a: "can
  // target the flagship or any other fleet ship"). Anything else selected
  // (a hostile, an object, a marker) isn't a valid repair target, so this
  // falls back to "player" rather than silently healing nothing.
  function resolveRepairTargetId(selectedId: string | null): string {
    if (selectedId && fleetShipById(selectedId)) return selectedId;
    return "player";
  }

  selfModules.forEach((m) => {
    moduleCircleEls.get(m.key)!.circle.addEventListener("click", () => {
      if (controlsLocked || fleeing) return;
      const wasActive = m.cycle.active;
      // Hull repair is the one module with a consumable behind it (16.80) --
      // switching it on with an empty hold would just burn a 5s cycle for
      // nothing, so it refuses up front rather than failing silently.
      if (!wasActive && m.key === "hull" && (state.cargo[REPAIR_ORE] ?? 0) <= 0) {
        showBanner(`No ${REPAIR_ORE} aboard — hull repair needs ore`);
        return;
      }
      m.cycle = tapSelfModule(
        m.cycle,
        performance.now(),
        m.cycleDurationMs,
        resolveRepairTargetId(selected?.id ?? null),
        SELF_MODULE_COOLDOWN_MS[m.key] ?? 0
      );
      // Shield repairs immediately on activation, then again at the start
      // of each subsequent cycle (i.e. the existing per-cycle-completion
      // firing below, since cycles run back-to-back) -- per Don's
      // direction. Hull deliberately keeps its original end-of-cycle-only
      // timing; only Shield gets the immediate first tick.
      if (!wasActive && m.cycle.active && m.key === "shield" && m.cycle.targetId) {
        applySelfModuleRepair(m, m.cycle.targetId);
      }
    });
  });

  // Engine circle, tapped directly (new -- not in the original mockup):
  // cruise in whatever heading the ship already has, with no target
  // needed at all. Toggles off on a second tap, same as every other
  // module. Cancels any Move To/Orbit in progress, and is itself
  // cancelled by picking a new Move To/Orbit target or target-then-module
  // assignment. Extended per Don's direction: if the ship already has any
  // speed -- under cruise, Move To, Orbit, or just coasting -- tapping
  // Engine instead actively brakes it to a stop (at the same rate it
  // would normally accelerate) rather than switching into cruise.
  moduleCircleEls.get("engine")!.circle.addEventListener("click", () => {
    if (controlsLocked || fleeing) return;
    if (player.speed > 0.5) {
      braking = true;
      activeMoveMode = null;
      moveTarget = null;
      orbit = null;
    } else {
      braking = false;
      activeMoveMode = activeMoveMode === "cruise" ? null : "cruise";
      orbit = null;
    }
    updateEngineButtons();
  });

  // Drone Bay controls (Section 16.23) -- "manual target mode... works
  // like weapon-module targeting," mirrored here as the same
  // target-then-module tap gesture, just on the Fleet Health Box row
  // instead of a rack circle (see the row's own comment for why).
  function updateDroneModeButton(): void {
    const modeBtn = wrap.querySelector<HTMLButtonElement>("[data-drone-mode]");
    if (!modeBtn) return;
    modeBtn.textContent = droneMode === "auto" ? "AUTO" : "MANUAL";
    modeBtn.classList.toggle("active", droneMode === "manual");
  }
  if (hasDroneBay) {
    wrap.querySelector<HTMLElement>("[data-drone-trigger]")!.addEventListener("click", () => {
      if (controlsLocked || fleeing || !selected) return;
      droneMode = "manual";
      droneTargetId = selected.id;
      updateDroneModeButton();
    });
    wrap.querySelector<HTMLButtonElement>("[data-drone-mode]")!.addEventListener("click", () => {
      if (controlsLocked || fleeing) return;
      droneMode = droneMode === "auto" ? "manual" : "auto";
      updateDroneModeButton();
    });
    wrap.querySelector<HTMLButtonElement>("[data-drone-recall]")!.addEventListener("click", (ev) => {
      if (controlsLocked || fleeing) return;
      // An empty bay has nothing to recall or redeploy -- without this it
      // cheerfully reported "Drones redeployed" with zero drones aboard
      // (16.74). Points the captain at the actual fix instead.
      if (drones.length === 0) {
        showBanner(`Drone bay is empty — buy ${droneItemName}s at a Market`);
        return;
      }
      const anyDeployed = drones.some((d) => d.deployed);
      if (anyDeployed) {
        drones.forEach((d) => {
          d.deployed = false;
        });
      } else {
        // Redeploy the complement only, not the whole bay (16.87) -- and
        // healthiest-first, so a recalled-and-repaired drone goes back out
        // ahead of one still patching itself up.
        const readiest = [...drones].sort((a, b) => b.hp - a.hp).slice(0, droneComplementCount);
        drones.forEach((d) => {
          d.deployed = readiest.includes(d);
          if (d.deployed) d.nextFireAt = null;
        });
      }
      (ev.target as HTMLButtonElement).textContent = anyDeployed ? "REDEPLOY" : "RECALL";
      showBanner(anyDeployed ? "Drones recalled — repairing in the bay" : "Drones redeployed");
    });
    updateDroneModeButton();
  }

  // Move To/Orbit Near/Orbit Far all toggle off on a second tap while
  // already active -- same "tapping an active module deactivates it"
  // convention as every rack circle, extended to these buttons per Don's
  // direction (previously only ever turned modes on).
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="moveto"]')!.addEventListener("click", () => {
    if (!selected || fleeing || controlsLocked) return;
    braking = false;
    // Pressing Move To is the deliberate act that turns "what I am looking at"
    // into "where I am going" (16.100).
    const engaging = activeMoveMode !== "moveto";
    activeMoveMode = engaging ? "moveto" : null;
    moveTarget = engaging ? selected : null;
    orbit = null;
    updateEngineButtons();
  });
  const beginOrbit = (radius: number, mode: "orbitNear" | "orbitFar") => {
    if (!selected || fleeing || controlsLocked) return;
    braking = false;
    if (activeMoveMode === mode) {
      activeMoveMode = null;
      orbit = null;
      moveTarget = null;
      updateEngineButtons();
      return;
    }
    // Orbit commits the selection too -- you orbit what you were looking at when
    // you pressed it, and a later single click does not drag the orbit with it.
    moveTarget = selected;
    const startAngle = Math.atan2(player.y - selected.y, player.x - selected.x);
    orbit = { angle: startAngle, radius };
    activeMoveMode = mode;
    updateEngineButtons();
  };
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="orbitNear"]')!.addEventListener("click", () => beginOrbit(ORBIT_NEAR_RADIUS, "orbitNear"));
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="orbitFar"]')!.addEventListener("click", () => beginOrbit(ORBIT_FAR_RADIUS, "orbitFar"));

  // Fast Travel now requires an explicit Scanner UI selection first (Don's
  // direction) -- the button stays disabled (see updateEngineButtons)
  // until a Fast Travel row is picked, and only ever moves the ship to
  // another real Location in the *current* System. Reaching another
  // System is a separate, explicit WARP action below.
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="fastTravel"]')!.addEventListener("click", () => {
    if (!pendingFastTravelLocationId) return;
    const loc = buildSystemLocations(currentSystem).find((l) => l.id === pendingFastTravelLocationId);
    if (!loc) return;
    beginFlee(loc.name, null, loc.id);
  });

  // WARP -- the actual inter-System jump (Don's direction): only enabled
  // once `selected` is the current Location's own Gate object and the
  // ship is within 1000m of it (see updateEngineButtons). Reuses the same
  // turn/accelerate/exit Fast Travel sequence as an ordinary same-System
  // trip; `beginFlee`'s jumpToSystemId is what tells completeFlee this one
  // actually changes state.currentSystemId.
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="warp"]')!.addEventListener("click", () => {
    const gate = selected && fixedObjects.find((f) => f.id === selected!.id && f.kind === "gate");
    if (!gate?.jumpToSystemId) return;
    if (Math.hypot(gate.x - player.x, gate.y - player.y) > GATE_WARP_RANGE_M) return;
    beginFlee(gate.name, gate.jumpToSystemId, "planet-1");
  });

  // Cloak (16.61): snapshots every currently-aggroed hostile's live
  // target point so it keeps heading there (not the real, now-hidden
  // position) for the 5s window -- updateHostileAggro/hostileMoveTargetPoint
  // below both check `cloakActive()` to honor this. No new locks either
  // (updateHostileAggro is a full no-op while active).
  function cloakActive(nowMs: number): boolean {
    return nowMs < cloakActiveUntil;
  }
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="cloak"]')!.addEventListener("click", () => {
    const now = performance.now();
    if (controlsLocked || fleeing || now < cloakReadyAt) return;
    cloakActiveUntil = now + CLOAK_DURATION_MS;
    cloakReadyAt = now + CLOAK_COOLDOWN_MS;
    hostiles.forEach((h) => {
      if (h.aggroTargetId) {
        h.frozenTargetPoint = hostileMoveTargetPoint(h) ?? undefined;
      }
    });
    showBanner("Cloak engaged — no enemy can attack or lock on for 5 seconds.");
    updateEngineButtons();
  });

  // Tractor Beam (16.61): marks every lootable wreck within 2000m as
  // `tractored` -- frame()'s own per-tick movement (below) steadily pulls
  // it toward the player until it's in loot range, then clears the flag.
  // No cooldown, so this can simply be re-clicked any time.
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="tractorBeam"]')!.addEventListener("click", () => {
    if (controlsLocked || fleeing) return;
    let pulled = 0;
    fixedObjects.forEach((f) => {
      if (f.kind !== "wreck" || !f.loot || Object.keys(f.loot).length === 0) return;
      if (Math.hypot(f.x - player.x, f.y - player.y) <= TRACTOR_BEAM_RANGE_M) {
        f.tractored = true;
        pulled++;
      }
    });
    showBanner(pulled > 0 ? `Tractor Beam engaged — pulling ${pulled} wreck${pulled === 1 ? "" : "s"} toward you.` : "No wrecks within 2000m.");
  });

  // Loot (Section 8's wreck taxonomy, extended per Don's direction): opens
  // the transfer popup rather than instantly moving everything -- Transfer
  // All inside the popup reproduces the old one-click behavior for anyone
  // who just wants it gone. Extracted so both the manual button and
  // checkAutoEngage's double-click-to-engage path (items 2/3) share the
  // exact same eligibility check instead of drifting apart.
  function tryLoot(wreckId: string): boolean {
    if (fleeing || controlsLocked) return false;
    const wreck = fixedObjects.find((f) => f.id === wreckId && f.kind === "wreck");
    if (!wreck?.loot || Object.keys(wreck.loot).length === 0) return false;
    openLootPopup(wreck.id);
    return true;
  }
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="loot"]')!.addEventListener("click", () => {
    if (!selected) return;
    tryLoot(selected.id);
  });

  // Founding a Base (Don's direction: "price of a lease should be set to
  // 10,000 credits for now" -- flat, no range requirement; only Dock
  // itself is proximity/ownership-gated).
  const LEASE_COST_CR = 10_000;
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="buyLease"]')!.addEventListener("click", () => {
    if (fleeing || controlsLocked || !selected) return;
    const planet = fixedObjects.find((f) => f.id === selected!.id && f.kind === "planet");
    if (!planet?.leaseKey || !planet.buildable || hasLease(planet.leaseKey)) return;
    if (state.credits < LEASE_COST_CR) {
      showBanner(`Need ${LEASE_COST_CR.toLocaleString()} CR to found a base here`);
      return;
    }
    state.credits -= LEASE_COST_CR;
    state.ownedLeases = [...state.ownedLeases, planet.leaseKey];
    // Real Base ownership (16.36): the lease itself already gated Dock,
    // this is what that ownership actually holds -- storage, initially
    // empty (src/baseStorage.ts).
    state.bases = [...state.bases, createBase(currentSystem.id, planet.id)];
    showBanner(`Lease purchased — ${planet.name} is now a Base`);
    triggerSave(ctx);
    renderScannerList();
    updateEngineButtons();
  });

  // Dock (Don's direction: leased planet = Base View, Station within 500m
  // = Station View) -- both a real "leave space" transition (app.ts tears
  // down the Expedition View for these, same as Shipyard/Lounge), unlike
  // the Cargo/Fleet/Market/Mission overlay screens. Extracted (same reason
  // as tryLoot above) so checkAutoEngage's double-click-to-engage path
  // shares this exact eligibility check with the manual button.
  function tryDock(objId: string): boolean {
    if (fleeing || controlsLocked) return false;
    const obj = fixedObjects.find((f) => f.id === objId && (f.kind === "planet" || f.kind === "station"));
    if (!obj) return false;
    // The docking gate (16.94). Refused while any guard is engaging, which is
    // both of Don's routes in one condition rather than two special cases: a
    // welcome captain has no engaged guards, and a captain who fought their way
    // in has none left. Keyed on guards actually engaged rather than on
    // standing, so forcing entry genuinely works and being welcome does not
    // protect a captain who shot first.
    //
    // Planets are exempt: a Base is the captain's own, and nothing guards it.
    if (obj.kind === "station") {
      const check = checkDocking(engagedGuardCount(), guardDisposition(state.standing, currentSystem.region as GalaxyRegion));
      if (!check.allowed) {
        showBanner(check.reason);
        return false;
      }
    }
    // Every ship records where it last docked -- both so mutiny (16.62)
    // knows where to return the ship+crew, and so 16.72's storage model
    // knows where a hull the captain walks away from is actually parked.
    // Stations count for this now, not just Player-Owned Bases.
    const rememberDock = (locationId: string): void => {
      const fleetEntry = state.fleet.find((f) => f.id === state.currentShipInstanceId);
      if (fleetEntry) {
        fleetEntry.dockedAtKey = dockKey(currentSystem.id, locationId);
      }
    };

    if (obj.kind === "planet") {
      if (!hasLease(obj.leaseKey)) return false;
      rememberDock(obj.id);
      state.lastTopLevelView = "base";
      triggerSave(ctx);
      ctx.navigate("base");
    } else {
      if (Math.hypot(obj.x - player.x, obj.y - player.y) > STATION_DOCK_RANGE_M) return false;
      rememberDock(STATION_LOCATION_ID);
      state.lastTopLevelView = "station";
      triggerSave(ctx);
      ctx.navigate("station");
    }
    return true;
  }
  wrap.querySelector<HTMLButtonElement>('button[data-cmd="dock"]')!.addEventListener("click", () => {
    if (!selected) return;
    tryDock(selected.id);
  });

  // Double-click-to-engage arrival check (items 2/3, checked every frame
  // via frame() below): fires the same Dock/Loot action the manual button
  // would, the instant it becomes eligible after a double-click Move To
  // set autoEngageTargetId. Attack/mine have no such gap -- their weapon/
  // mining cycle (armed in handleDoubleClickTarget) already fires on its
  // own once in range, same as a manually-selected target would.
  function checkAutoEngage(): void {
    if (!autoEngageTargetId || fleeing || controlsLocked) {
      return;
    }
    const obj = fixedObjects.find((f) => f.id === autoEngageTargetId);
    if (!obj) {
      autoEngageTargetId = null;
      return;
    }
    if (obj.kind === "wreck") {
      if (tryLoot(obj.id)) {
        autoEngageTargetId = null;
      }
    } else if (obj.kind === "planet" || obj.kind === "station") {
      if (tryDock(obj.id)) {
        autoEngageTargetId = null;
      }
    } else {
      autoEngageTargetId = null;
    }
  }

  // Fast Travel list rows (Scanner UI's "Fast Travel -- This System" group)
  // -- event delegation since renderScannerList rebuilds this list's
  // innerHTML on every NEAR/FAR sort toggle and every SCAN. Shared by both
  // the small panel's list and the Scanning UI popup's body -- same rows,
  // same handler. Clicking a row only *selects* a Fast Travel destination
  // now (highlighted, shown on the Fast Travel button) -- the button
  // itself is the actual trigger, matching the same select-then-act
  // pattern Move To/Orbit already use.
  function handleFastTravelClick(ev: Event): void {
    const item = (ev.target as HTMLElement).closest<HTMLElement>(".xv-sc-item.system");
    if (item?.dataset.locationId) {
      pendingFastTravelLocationId = item.dataset.locationId;
      renderScannerList();
      updateEngineButtons();
    }
  }
  // Scanner UI object rows (Section 2a: "In This Location" objects are
  // Move To/Orbit candidates, the same as tapping them directly in the
  // viewport/Mini-map) -- selects via the same selectTarget() the direct-
  // tap paths use, so the Target Bar and weapon lock-on behave identically
  // either way. Selecting doesn't Move To by itself; the captain still
  // presses Move To/Orbit on the Engine module afterward, same as a direct
  // tap.
  /**
   * Anything the Scanner can list in this Location, hostiles included (16.100).
   *
   * Both row handlers used to look only in fixedObjects, so they could never
   * have acted on an enemy ship -- which is what Don asked the Scanner double-
   * click to do. One lookup for both, so a row that renders is always a row that
   * responds.
   */
  function resolveScannerObject(id: string | undefined): NamedPoint | null {
    if (!id) return null;
    return fixedObjects.find((f) => f.id === id) ?? hostiles.find((h) => h.id === id) ?? null;
  }

  function handleObjectRowClick(ev: Event): void {
    const item = (ev.target as HTMLElement).closest<HTMLElement>(".xv-sc-item[data-obj-id]");
    if (!item || fleeing || controlsLocked) return;
    const obj = resolveScannerObject(item.dataset.objId);
    if (obj) {
      selectTarget(obj);
      renderScannerList();
    }
  }
  wrap.querySelector<HTMLElement>("[data-sc-list]")!.addEventListener("click", (ev) => {
    handleFastTravelClick(ev);
    handleObjectRowClick(ev);
  });
  wrap.querySelector<HTMLElement>("[data-scanning-body]")!.addEventListener("click", (ev) => {
    handleFastTravelClick(ev);
    handleObjectRowClick(ev);
  });

  // Double-click a scanned object row (item 2, Don's direction) -- same
  // Move To + engage as double-clicking it directly in the viewport/Mini-
  // map (handleDoubleClickTarget), just reached from the Scanner UI's own
  // list instead. Deliberately excludes the Fast Travel rows below it
  // (".xv-sc-item[data-obj-id]" only matches "In This Location" objects,
  // not ".xv-sc-item.system" Fast Travel destinations) -- Don's own
  // wording: "not the Fast Travel Locations at the bottom."
  function handleObjectRowDoubleClick(ev: Event): void {
    const item = (ev.target as HTMLElement).closest<HTMLElement>(".xv-sc-item[data-obj-id]");
    if (!item || fleeing || controlsLocked) return;
    const obj = resolveScannerObject(item.dataset.objId);
    if (obj) {
      // handleDoubleClickTarget already assigns every non-mining weapon to a
      // hostile and the Mining Laser to an asteroid, so "auto target it with our
      // weapons" (Don) needs no separate path -- only the lookup was missing.
      handleDoubleClickTarget(obj);
    }
  }
  wrap.querySelector<HTMLElement>("[data-sc-list]")!.addEventListener("dblclick", handleObjectRowDoubleClick);
  wrap.querySelector<HTMLElement>("[data-scanning-body]")!.addEventListener("dblclick", handleObjectRowDoubleClick);

  // ---- Scanner UI (Section 8, LOCKED): "In This Location" is now derived
  // live from fixedObjects (real distance, real discovered/undiscovered
  // state) instead of the disconnected static placeholder list this used
  // to be. SCAN rolls each still-hidden object independently -- the doc
  // only locks the *shape* of this ("a probability of revealing any given
  // still-hidden object... rolled independently per scan," "not a flat
  // cooldown timer"), not an exact number, so the reveal chance below
  // matches the tested mockup's value rather than inventing a new one.
  // The doc's other locked consequence -- a scan attempt risking triggering
  // a hostile contact -- isn't modeled: there's no encounter-spawning
  // system in the Expedition View at all yet (only one pre-placed
  // hostile), so that's a real gap, not a shortcut taken here.
  const SCAN_REVEAL_CHANCE = 0.4;
  // Section 8's locked upper bound ("roughly 10 attempts at the high end
  // for the hardest-to-find objects") -- flavor text only, since the
  // actual reveal roll above is a flat per-scan chance, not a per-object
  // attempt counter.
  const MAX_SCAN_ATTEMPTS = 10;
  let scanDir: "near" | "far" = "near";

  function locationSubText(): string {
    // Same "<name> · Danger Tier <n>" convention src/ui/components.ts's
    // renderHeader already uses for the real current System -- there's no
    // separate per-Location name in the data model (see file header), so
    // the sub-header identifies the System the current scene belongs to.
    return `${currentSystem.name} · Danger Tier ${currentSystem.dangerTier}`;
  }

  function discoveredWithDistance() {
    return fixedObjects
      .filter((f) => f.discovered)
      .map((f) => ({ obj: f, distance: Math.hypot(f.x - player.x, f.y - player.y) }))
      .sort((a, b) => (scanDir === "near" ? a.distance - b.distance : b.distance - a.distance));
  }

  function sortedFastTravelRows(): ScanRow[] {
    return buildFastTravelRows(currentSystem, currentLocationId).sort((a, b) =>
      scanDir === "near" ? a.distanceValue - b.distanceValue : b.distanceValue - a.distanceValue
    );
  }

  function objectKindLabel(kind: FixedObject["kind"]): string {
    if (kind === "wreck") return "Wreck";
    if (kind === "gate") return "Jump Gate";
    if (kind === "planet") return "Planet";
    if (kind === "station") return "Station";
    return "Asteroid";
  }

  function hasLease(leaseKey: string | undefined): boolean {
    return !!leaseKey && state.ownedLeases.includes(leaseKey);
  }

  // Section 6/Don's direction: a Station lets you Dock once within 500m --
  // unlike a planet's lease-gated Dock, there's no ownership check, just
  // proximity (a faction Station is never player-owned).
  const STATION_DOCK_RANGE_M = 500;

  function objectDetailText(obj: FixedObject): string {
    if (obj.kind === "asteroid" && obj.poolM3 != null && obj.poolMaxM3) {
      return `${obj.resource} · ${Math.round((obj.poolM3 / obj.poolMaxM3) * 100)}% remaining`;
    }
    if (obj.kind === "gate") {
      return `Move To, then WARP once within ${GATE_WARP_RANGE_M}m`;
    }
    if (obj.kind === "planet") {
      if (!obj.buildable) return `${obj.planetType} — not buildable`;
      return hasLease(obj.leaseKey)
        ? `${obj.planetType} — leased, Dock available`
        : `${obj.planetType} — no lease (10,000 CR to found a base)`;
    }
    if (obj.kind === "station") {
      return `Dock within ${STATION_DOCK_RANGE_M}m`;
    }
    const lootCount = obj.loot ? Object.keys(obj.loot).length : 0;
    return lootCount > 0 ? `${lootCount} item type${lootCount === 1 ? "" : "s"} to loot` : "Wreck — already looted clean";
  }

  function renderScannerList(): void {
    const list = wrap.querySelector<HTMLElement>("[data-sc-list]")!;
    const discovered = discoveredWithDistance();
    const hiddenCount = fixedObjects.filter((f) => !f.discovered).length;
    const fastTravel = sortedFastTravelRows();
    const waypointNextId = nextWaypointLocationId();

    wrap.querySelector<HTMLElement>("[data-sc-sub]")!.textContent = locationSubText();

    // Ships in this Location, nearest first (16.100). The Scanner listed rocks,
    // wrecks, gates and stations but never the one class of object a captain most
    // needs to reach quickly -- so Don's "double click ... and if it's an enemy
    // ship we auto target it" had nothing to double-click. The Target Bar shows
    // who is shooting at you; this shows everything that is out there.
    const contacts = hostiles
      .map((h) => ({ h, distance: Math.hypot(h.x - player.x, h.y - player.y) }))
      .sort((a, b) => a.distance - b.distance);

    list.innerHTML = `
      <div class="xv-sc-group-label">In This Location (Move To / Orbit)</div>
      ${contacts
        .map(({ h, distance }) => {
          // Guards read as what they are rather than as "hostile": a tolerated
          // garrison is not a fight, and labelling it one would make every
          // station approach look like an ambush.
          const kind = h.guard ? (h.passive ? "Station Guard — holding fire" : "Station Guard — engaging") : "Hostile Ship";
          return scanItemHtml(
            { name: h.name, meta: kind, distanceValue: distance, objId: h.id },
            `${Math.round(distance).toLocaleString()}m · ${kind}`,
            selected?.id === h.id
          );
        })
        .join("")}
      ${discovered
        .map(({ obj, distance }) =>
          scanItemHtml(
            { name: obj.name, meta: objectKindLabel(obj.kind), distanceValue: distance, objId: obj.id },
            `${Math.round(distance).toLocaleString()}m · ${objectKindLabel(obj.kind)}`,
            selected?.id === obj.id
          )
        )
        .join("")}
      ${Array.from({ length: hiddenCount })
        .map(() => scanItemHtml({ name: "? Unknown Signature", meta: "not yet scanned", unknown: true, distanceValue: Infinity }, ""))
        .join("")}
      <div class="xv-sc-group-label"><span class="xv-sc-system">${currentSystem.name.toUpperCase()}</span> — Fast Travel</div>
      ${fastTravel
        .map((r) =>
          scanItemHtml(
            r,
            `${r.meta} — ${r.distanceValue.toFixed(1)} AU`,
            r.locationId === pendingFastTravelLocationId,
            r.locationId === waypointNextId
          )
        )
        .join("")}
    `;

    updateScanButtons(hiddenCount);
    renderScanningPopup();
  }

  // Real next-hop highlight for an active Space Map waypoint (Don's
  // direction): "highlight the next gate we should be taking in our
  // Waypoint list... keep the next gate highlighted until we reach our
  // target waypoint system or we change to a new waypoint/system." Uses
  // the same real shortestRoute() the Space Map itself already uses for
  // its own route line -- the next hop's destination system is exactly
  // which of the current System's real Gate Locations to highlight.
  function nextWaypointLocationId(): string | null {
    if (!state.waypointSystemId || state.waypointSystemId === currentSystem.id) return null;
    const route = shortestRoute(currentSystem.id, state.waypointSystemId, allSystems);
    if (route.hops === 0) return null;
    return `gate-${route.path[1]}`;
  }

  // Scanning UI popup (Section 2a/8: "the larger popup opened from the
  // Scanner UI; holds the actual scan-attempt controls and fuller per-
  // Location detail. Same System-wide scope as the small panel, just
  // expanded"). Section 4b separately locks a SCAN button pinned to the
  // small panel itself, so the two locked descriptions conflict on where
  // "the" SCAN button lives -- resolved here by giving both panels a real,
  // independent-looking SCAN button wired to the same shared doScan(), per
  // the design doc's note next to Section 4b's Scanner UI column entry.
  function renderScanningPopup(): void {
    const body = wrap.querySelector<HTMLElement>("[data-scanning-body]")!;
    const discovered = discoveredWithDistance();
    const hidden = fixedObjects.filter((f) => !f.discovered);
    const fastTravel = sortedFastTravelRows();

    wrap.querySelector<HTMLElement>("[data-scanning-sub]")!.textContent = locationSubText();

    body.innerHTML = `
      <div class="xv-sc-group-label">In This Location — full detail</div>
      ${discovered
        .map(({ obj, distance }) => {
          const detail = objectDetailText(obj);
          const selClass = selected?.id === obj.id ? " selected" : "";
          return `
            <div class="xv-sc-item selectable${selClass}" data-obj-id="${obj.id}">
              <span class="xv-sc-name">${obj.name}</span>
              <span class="xv-sc-meta">${Math.round(distance).toLocaleString()}m · ${detail}</span>
            </div>
          `;
        })
        .join("")}
      ${hidden
        .map(
          () => `
            <div class="xv-sc-item unknown">
              <span class="xv-sc-name">? Unknown Signature</span>
              <span class="xv-sc-meta">not yet scanned — up to ${MAX_SCAN_ATTEMPTS} attempts to reveal</span>
            </div>
          `
        )
        .join("")}
      <div class="xv-sc-group-label"><span class="xv-sc-system">${currentSystem.name.toUpperCase()}</span> — Fast Travel</div>
      ${fastTravel
        .map((r) =>
          scanItemHtml(
            r,
            `${r.meta} — ${r.distanceValue.toFixed(1)} AU`,
            r.locationId === pendingFastTravelLocationId,
            r.locationId === nextWaypointLocationId()
          )
        )
        .join("")}
    `;
  }

  function updateScanButtons(hiddenCount: number): void {
    wrap.querySelectorAll<HTMLButtonElement>(".xv-scan-btn").forEach((btn) => {
      btn.disabled = hiddenCount === 0;
      btn.textContent = hiddenCount === 0 ? "FULLY SCANNED" : "SCAN";
    });
  }

  const sortBtn = wrap.querySelector<HTMLButtonElement>("button[data-sort]")!;
  sortBtn.addEventListener("click", () => {
    scanDir = scanDir === "near" ? "far" : "near";
    sortBtn.textContent = scanDir === "near" ? "NEAR ▾" : "FAR ▾";
    renderScannerList();
  });

  function doScan(): void {
    if (controlsLocked || fleeing) return;
    let revealed = 0;
    fixedObjects.forEach((f) => {
      if (!f.discovered && Math.random() < SCAN_REVEAL_CHANCE) {
        f.discovered = true;
        revealed++;
      }
    });
    // Neighbor-gate-scan discovery for the 10 real hiddenUntilScanned
    // endgame systems (16.46, Section 6) -- "while present in a system
    // with a hidden neighbor, scanning has a per-attempt chance of
    // detecting that neighbor's jump gate," the same soft-gate roll model
    // as the in-system reveal above (SCAN_REVEAL_CHANCE), just applied to
    // a hidden gate instead of a fixedObject.
    const newlyDetected = hiddenNeighborsOf(currentSystem.id, allSystems).filter(
      (id) => !state.detectedHiddenSystemIds.includes(id) && Math.random() < SCAN_REVEAL_CHANCE
    );
    if (newlyDetected.length > 0) {
      state.detectedHiddenSystemIds = [...state.detectedHiddenSystemIds, ...newlyDetected];
      triggerSave(ctx);
    }
    renderScannerList();
    showBanner(revealed > 0 ? `Scan complete — ${revealed} new signature(s) detected` : "Scan complete — no new signatures");
    // Section 8: "repeated scanning... each attempt raises detection odds"
    // -- every attempt rolls the real ambient-contact chance, regardless of
    // whether it also revealed a new signature.
    maybeTriggerAmbientContact();
    maybeTriggerEnvironmentalHazard();
  }
  wrap.querySelectorAll<HTMLButtonElement>(".xv-scan-btn").forEach((btn) => btn.addEventListener("click", doScan));

  // Trigger element for the Scanning UI popup isn't specified anywhere in
  // the doc (only that it's "opened from the Scanner UI") -- the small
  // panel's own title label is used here, a reasonable, low-risk choice
  // that doesn't collide with the sort pill or the per-row Fast Travel tap
  // gesture next to it.
  const scanningPopup = wrap.querySelector<HTMLElement>("[data-scanning-popup]")!;
  wrap.querySelector<HTMLElement>("[data-sc-title]")!.addEventListener("click", () => {
    renderScanningPopup();
    scanningPopup.classList.add("show");
  });
  wrap.querySelector<HTMLElement>("[data-scanning-close]")!.addEventListener("click", () => {
    scanningPopup.classList.remove("show");
  });
  scanningPopup.addEventListener("click", (ev) => {
    if (ev.target === scanningPopup) {
      scanningPopup.classList.remove("show");
    }
  });

  renderScannerList();

  // Direct-tap target selection (Section 2a's "Move To" input path 1) --
  // nearest candidate within a small pixel threshold, in screen space, or
  // a dropped marker if nothing's there. Includes the escort so it can be
  // selected as a Shield/Hull repair target too (Section 4a: Shield/Hull
  // can target the flagship or any other fleet ship).
  canvas.addEventListener("pointerdown", (ev) => {
    if (fleeing || controlsLocked) return;
    const clickScreen = canvasPointFromEvent(canvas, ev);
    selectAtWorldPoint(clickScreen, worldToScreen, screenToWorld(clickScreen), 26);
  });
  canvas.addEventListener("dblclick", (ev) => {
    if (fleeing || controlsLocked) return;
    const clickScreen = canvasPointFromEvent(canvas, ev);
    const found = findObjectAtWorldPoint(clickScreen, worldToScreen, 26);
    if (found) {
      handleDoubleClickTarget(found);
    } else {
      // Empty space (Don's direction): drop a marker there AND
      // immediately Move To it, same "double-click = select + engage"
      // default already used for real objects, rather than requiring a
      // separate manual Move To press afterward.
      const clickWorld = screenToWorld(clickScreen);
      marker = { id: "marker", name: "Marker", x: clickWorld.x, y: clickWorld.y };
      handleDoubleClickTarget(marker);
    }
  });

  wrap.querySelectorAll<HTMLElement>(".xv-zoom div[data-z]").forEach((el) => {
    el.addEventListener("click", () => {
      zoom = el.dataset.z as ZoomLevel;
      wrap.querySelectorAll(".xv-zoom div[data-z]").forEach((sib) => sib.classList.remove("active"));
      el.classList.add("active");
    });
  });

  // ---- Mini-map (Section 4b, LOCKED: circular, ship-centered, fixed to
  // the Location's own 22,500 m radius -- not zoom-relative like the main
  // viewport) and its full-screen popup, extended (not in the original
  // mockup) so a tap on empty space there also drops a marker. ----
  const LOCATION_RADIUS = 22500;
  const minimapCanvas = wrap.querySelector<HTMLCanvasElement>("[data-minimap-canvas]")!;
  const minimapFull = wrap.querySelector<HTMLElement>("[data-minimap-full]")!;
  const minimapFullCanvas = wrap.querySelector<HTMLCanvasElement>("[data-minimap-full-canvas]")!;

  function drawMiniMap(mmCanvas: HTMLCanvasElement, full: boolean): void {
    mmCanvas.width = mmCanvas.clientWidth;
    mmCanvas.height = mmCanvas.clientHeight;
    const mctx = mmCanvas.getContext("2d")!;
    mctx.clearRect(0, 0, mmCanvas.width, mmCanvas.height);
    const R = Math.min(mmCanvas.width, mmCanvas.height) / 2 - 4;
    const scale = R / LOCATION_RADIUS;
    const cx = mmCanvas.width / 2;
    const cy = mmCanvas.height / 2;
    const dotR = full ? 5 : 3;

    fixedObjects
      .filter((f) => f.discovered)
      .forEach((f) => {
        const dx = (f.x - player.x) * scale;
        const dy = (f.y - player.y) * scale;
        mctx.fillStyle = f.kind === "gate" ? "#8a5fd6" : "#8a8f96";
        mctx.beginPath();
        mctx.arc(cx + dx, cy + dy, full ? 4 : 2, 0, Math.PI * 2);
        mctx.fill();
      });

    fleetShips.forEach((f) => {
      if (f.fled) return;
      const fx = (f.x - player.x) * scale;
      const fy = (f.y - player.y) * scale;
      mctx.fillStyle = "#7fa8c9";
      mctx.beginPath();
      mctx.arc(cx + fx, cy + fy, dotR, 0, Math.PI * 2);
      mctx.fill();
    });

    hostiles.forEach((h) => {
      const dx = (h.x - player.x) * scale;
      const dy = (h.y - player.y) * scale;
      mctx.fillStyle = "#c0392b";
      mctx.beginPath();
      mctx.arc(cx + dx, cy + dy, dotR, 0, Math.PI * 2);
      mctx.fill();
    });

    if (marker) {
      const dx = (marker.x - player.x) * scale;
      const dy = (marker.y - player.y) * scale;
      mctx.fillStyle = "#f0c94a";
      mctx.beginPath();
      mctx.arc(cx + dx, cy + dy, dotR, 0, Math.PI * 2);
      mctx.fill();
    }

    // Selection ring -- same "instant highlight" as the main viewport
    // (Section 4b), generic to whatever's selected (hostile, discovered
    // object, or a dropped marker), not just hostiles, and shown on both
    // the small always-visible mini-map and its full-screen popup.
    if (selected) {
      const dx = (selected.x - player.x) * scale;
      const dy = (selected.y - player.y) * scale;
      mctx.strokeStyle = "#f0c94a";
      mctx.lineWidth = 1.5;
      mctx.beginPath();
      mctx.arc(cx + dx, cy + dy, dotR + 4, 0, Math.PI * 2);
      mctx.stroke();
    }

    mctx.fillStyle = "#3aa0e8";
    mctx.beginPath();
    mctx.arc(cx, cy, full ? 6 : 3.5, 0, Math.PI * 2);
    mctx.fill();
  }

  wrap.querySelector<HTMLElement>("[data-minimap]")!.addEventListener("click", () => {
    minimapFull.classList.add("show");
  });
  wrap.querySelector<HTMLElement>("[data-mm-close]")!.addEventListener("click", () => {
    minimapFull.classList.remove("show");
  });
  minimapFullCanvas.addEventListener("pointerdown", (ev) => {
    const clickScreen = canvasPointFromEvent(minimapFullCanvas, ev);
    const R = Math.min(minimapFullCanvas.width, minimapFullCanvas.height) / 2 - 4;
    const scale = R / LOCATION_RADIUS;
    const cx = minimapFullCanvas.width / 2;
    const cy = minimapFullCanvas.height / 2;
    const toMiniScreen = (p: Point) => ({ x: cx + (p.x - player.x) * scale, y: cy + (p.y - player.y) * scale });
    const clickWorld = { x: player.x + (clickScreen.x - cx) / scale, y: player.y + (clickScreen.y - cy) / scale };
    selectAtWorldPoint(clickScreen, toMiniScreen, clickWorld, 22);
    const hint = wrap.querySelector<HTMLElement>("[data-mm-sel-hint]")!;
    hint.textContent = selected ? `Selected: ${selected.name}. Close this to Move To or Orbit it.` : "";
  });
  minimapFullCanvas.addEventListener("dblclick", (ev) => {
    if (fleeing || controlsLocked) return;
    const clickScreen = canvasPointFromEvent(minimapFullCanvas, ev);
    const R = Math.min(minimapFullCanvas.width, minimapFullCanvas.height) / 2 - 4;
    const scale = R / LOCATION_RADIUS;
    const cx = minimapFullCanvas.width / 2;
    const cy = minimapFullCanvas.height / 2;
    const toMiniScreen = (p: Point) => ({ x: cx + (p.x - player.x) * scale, y: cy + (p.y - player.y) * scale });
    const found = findObjectAtWorldPoint(clickScreen, toMiniScreen, 22);
    const hint = wrap.querySelector<HTMLElement>("[data-mm-sel-hint]")!;
    if (found) {
      handleDoubleClickTarget(found);
      hint.textContent = `Selected: ${found.name}. Moving to it now.`;
    } else {
      // Empty space, same as the main viewport's own dblclick: drop a
      // marker and Move To it immediately.
      const clickWorld = { x: player.x + (clickScreen.x - cx) / scale, y: player.y + (clickScreen.y - cy) / scale };
      marker = { id: "marker", name: "Marker", x: clickWorld.x, y: clickWorld.y };
      handleDoubleClickTarget(marker);
      hint.textContent = "Marker dropped. Moving to it now.";
    }
  });

  // Shared glowing-line primitive every beam-style effect (laser, auto
  // tracers, hostile/fleet fire, the continuous mining beam) draws
  // through -- a soft outer glow pass plus a bright core, so a "beam"
  // reads as energy rather than a flat vector line.
  function drawFxLine(from: Point, to: Point, color: string, width: number, alpha: number): void {
    c2d.save();
    c2d.globalAlpha = alpha;
    c2d.strokeStyle = color;
    c2d.lineCap = "round";
    c2d.shadowColor = color;
    c2d.shadowBlur = 8;
    c2d.lineWidth = width;
    c2d.beginPath();
    c2d.moveTo(from.x, from.y);
    c2d.lineTo(to.x, to.y);
    c2d.stroke();
    c2d.restore();
  }

  // Per-weapon-type/effect-kind color -- the actual "distinct animation
  // per weapon" ask: each of laser/auto/missile/mining/hostile/fleet/
  // shield/hull gets its own hue so a captain can tell at a glance what
  // just fired without reading the HUD.
  const FX_COLOR: Record<string, string> = {
    laser: "#7fe0ff",
    auto: "#ffcf5c",
    missile: "#ff7a3d",
    mining: "#6be86b",
    hostile: "#ff4d4d",
    fleet: "#7fa8c9",
    shield: "#3aa0e8",
    hull: "#3fae5c",
    explosion: "#ff7a3d"
  };

  function drawEffects(): void {
    effects.forEach((e) => {
      const color = FX_COLOR[e.weaponKey] ?? "#ffffff";
      if (e.kind === "beam") {
        const start = lastT - (e.spawnedAt + (e.seed ?? 0));
        if (start < 0 || start >= e.durationMs) return; // not yet due, or this tracer's own window elapsed
        const from = e.sourceId ? resolveEffectPoint(e.sourceId) : null;
        const to = e.targetId ? resolveEffectPoint(e.targetId) : null;
        if (!from || !to) return;
        const alpha = 1 - start / e.durationMs;
        const width = e.weaponKey === "laser" ? 2.5 : e.weaponKey === "auto" ? 1.5 : 2;
        drawFxLine(worldToScreen(from), worldToScreen(to), color, width, alpha);
        return;
      }
      if (e.kind === "projectile") {
        const to = e.targetId ? resolveEffectPoint(e.targetId) : null;
        if (!to || e.fromX === undefined || e.fromY === undefined) return;
        const progress = clamp01((lastT - e.spawnedAt) / e.durationMs);
        const wx = e.fromX + (to.x - e.fromX) * progress;
        const wy = e.fromY + (to.y - e.fromY) * progress;
        const sp = worldToScreen({ x: wx, y: wy });
        // Short trail behind the projectile's own travel direction, plus
        // a bright glowing head -- reads as "moving" rather than a static dot.
        const tailProgress = Math.max(0, progress - 0.08);
        const tx = e.fromX + (to.x - e.fromX) * tailProgress;
        const ty = e.fromY + (to.y - e.fromY) * tailProgress;
        drawFxLine(worldToScreen({ x: tx, y: ty }), sp, color, 3, 0.6);
        c2d.save();
        c2d.globalAlpha = 0.9;
        c2d.fillStyle = color;
        c2d.shadowColor = color;
        c2d.shadowBlur = 10;
        c2d.beginPath();
        c2d.arc(sp.x, sp.y, 4, 0, Math.PI * 2);
        c2d.fill();
        c2d.restore();
        return;
      }
      if (e.kind === "particle") {
        if (e.x === undefined || e.y === undefined) return;
        const progress = clamp01((lastT - e.spawnedAt) / e.durationMs);
        const sp = worldToScreen({ x: e.x, y: e.y });
        c2d.save();
        c2d.globalAlpha = 1 - progress;
        c2d.fillStyle = color;
        c2d.fillRect(sp.x - 2, sp.y - 2, 4, 4);
        c2d.restore();
        return;
      }
      // pulse -- an expanding, fading ring at a live-tracked target (shield/
      // hull repair) or a fixed point (a missile's explosion).
      if (lastT < e.spawnedAt) return; // scheduled for later (explosion delay)
      const at = e.targetId ? resolveEffectPoint(e.targetId) : e.x !== undefined && e.y !== undefined ? { x: e.x, y: e.y } : null;
      if (!at) return;
      const progress = clamp01((lastT - e.spawnedAt) / e.durationMs);
      const sp = worldToScreen(at);
      const radius = 14 + progress * (e.weaponKey === "explosion" ? 22 : 14);
      c2d.save();
      c2d.globalAlpha = (1 - progress) * 0.8;
      c2d.strokeStyle = color;
      c2d.shadowColor = color;
      c2d.shadowBlur = 10;
      c2d.lineWidth = 2.5;
      c2d.beginPath();
      c2d.arc(sp.x, sp.y, radius, 0, Math.PI * 2);
      c2d.stroke();
      c2d.restore();
    });
  }

  function draw(): void {
    c2d.fillStyle = "#04070d";
    c2d.fillRect(0, 0, canvas.width, canvas.height);

    const camOrigin = cameraOrigin();
    c2d.save();
    stars.forEach((st) => {
      const sx = canvas.width / 2 + ((st.x * 4 - camOrigin.x * 0.02) % 2000);
      const sy = canvas.height / 2 + ((st.y * 4 - camOrigin.y * 0.02) % 2000);
      c2d.globalAlpha = st.a;
      c2d.fillStyle = "#cfe0f0";
      c2d.beginPath();
      c2d.arc(((sx % canvas.width) + canvas.width) % canvas.width, ((sy % canvas.height) + canvas.height) % canvas.height, st.r, 0, Math.PI * 2);
      c2d.fill();
    });
    c2d.globalAlpha = 1;
    c2d.restore();

    fixedObjects
      .filter((f) => f.discovered)
      .forEach((f) => {
        const p = worldToScreen(f);
        if (f.kind === "gate") {
          const pulse = (Math.sin(lastT / 500) + 1) / 2;
          gateShape(c2d, p.x, p.y, wr(GATE_RADIUS_M), "#8a5fd6", pulse);
          return;
        }
        if (f.kind === "planet") {
          planetShape(c2d, p.x, p.y, wr(PLANET_RADIUS_M), planetTypeColor(f.planetType ?? ""));
          return;
        }
        if (f.kind === "station") {
          stationShape(c2d, p.x, p.y, wr(STATION_RADIUS_M), stationFactionColor(currentSystem.region));
          return;
        }
        rockShape(c2d, p.x, p.y, wr(fixedObjectRadiusM(f.kind)), f.kind === "wreck" ? "#5a6b7d" : "#8a8f96");
      });

    fleetShips.forEach((f) => {
      if (f.fled) return;
      const fp = worldToScreen(f);
      drawHullShip(c2d, fp.x, fp.y, wr(SHIP_RADIUS_M), f.heading, playerShipShape(f.role, f.hullTier, false));
    });

    const pp = worldToScreen(player);
    // No currentShip while podded (16.72) -- falls back to a plain
    // Journeyman/T1 silhouette rather than crashing; the Pod itself has
    // no real visual design yet, same fidelity the old flat-triangle
    // shipShape() already had in that case.
    drawHullShip(
      c2d,
      pp.x,
      pp.y,
      // The flown ship draws a shade larger than the rest so the captain can
      // always pick themselves out of a crowded field -- a legibility choice,
      // not a claim that the hull is bigger.
      wr(SHIP_RADIUS_M * 1.15),
      player.heading,
      playerShipShape(state.currentShip?.role ?? "Journeyman", state.currentShip?.hullTier ?? "T1", true)
    );

    hostiles.forEach((h) => {
      const p = worldToScreen(h);
      drawHullShip(c2d, p.x, p.y, wr(SHIP_RADIUS_M), h.heading, npcShipShape(h.faction, h.name, h.hullTier));
    });

    // Continuous mining beam (Don's direction) -- unlike the other
    // Hardpoint types (a brief flash per completed cycle, spawned in
    // updateModules), the Mining Laser channels the whole time it's
    // locked onto an in-range asteroid, so this is drawn live off the
    // weapon's own real state every frame rather than as a timed effect.
    weapons
      .filter((w) => w.key === "mining" && w.cycle.targetId)
      .forEach((w) => {
        const target = resolveNamedPoint(w.cycle.targetId!);
        if (!target || Math.hypot(target.x - player.x, target.y - player.y) > w.range) return;
        drawFxLine(worldToScreen(player), worldToScreen(target), "#6be86b", 2, 0.7);
      });

    drawEffects();

    if (marker) {
      const p = worldToScreen(marker);
      markerShape(c2d, p.x, p.y);
    }

    if (selected) {
      const p = worldToScreen(selected);
      c2d.strokeStyle = "#f0c94a";
      c2d.globalAlpha = 0.7;
      c2d.lineWidth = 1.5;
      c2d.beginPath();
      // The selection ring sits just outside the hull it marks, so it scales with
      // the hull rather than with the screen.
      c2d.arc(p.x, p.y, wr(SHIP_RADIUS_M * 1.4), 0, Math.PI * 2);
      c2d.stroke();
      c2d.globalAlpha = 1;
    }
  }

  // Module states (Section 4b's five-state table) -- the cycle/range/tap
  // rules themselves live in moduleCycle.ts; this just resolves each
  // weapon's live in-range status (the one thing that requires this
  // file's world/position data) and applies the resulting visual.
  function applyModuleVisual(key: string, visual: string, progress: number): void {
    const els = moduleCircleEls.get(key);
    if (!els) return;
    const stateClass = visual === "off" ? "moduleOff" : visual === "cycling" ? "" : visual;
    els.circle.className = "sf-module-circle" + (stateClass ? ` ${stateClass}` : "");
    if (els.arcProgress) {
      const showArc = visual !== "off" && visual !== "armed";
      els.arcProgress.style.display = showArc ? "" : "none";
      if (showArc) {
        els.arcProgress.setAttribute("stroke-dashoffset", String(ARC_CIRCUMFERENCE * (1 - progress)));
      }
    }
  }

  // Combat tick (Section 4a/16.30) -- damage is rolled once per module
  // cycle *completion*, not once per global tick (16.30's revision), using
  // combatResolutionLoop.ts's real rollDamage() (same HIT_CHANCE/variance
  // as everywhere else) applied to shield first, then hull.
  function applyDamageToHostile(h: LiveHostile, damage: number): void {
    if (damage <= 0) return;
    // Firing on a garrison ends its patience, all of it at once (16.94).
    if (h.guard && h.passive) provokeGarrison();
    let remaining = damage;
    if (h.shield > 0) {
      const absorbed = Math.min(h.shield, remaining);
      h.shield -= absorbed;
      remaining -= absorbed;
    }
    h.hull = Math.max(0, h.hull - remaining);
    if (h.hull <= 0) {
      destroyHostile(h);
    }
  }

  // Mining Laser's real per-cycle extraction (Section 8/16.25): a real
  // m3/s rate for the selected ship's real hull tier, capped by whatever
  // is smaller of the node's remaining pool and the ship's remaining real
  // cargo room -- mutates state.cargo directly (the actual persistent
  // save-state cargo, not a scratch value), so mining here genuinely
  // feeds the same cargo the Mission Board/Cargo UI already use.
  // Section 11a flags the exact display treatment of a near-full/full
  // cargo hold as "not yet designed" -- but a fully-silent stop the first
  // time it happens is still a real UX gap, so a one-time banner (reset
  // once there's room again) is a reasonable, clearly-flagged choice here
  // rather than waiting on that undecided spec.
  let cargoFullWarned = false;

  // Pays out and removes a real accepted Mining mission the instant its
  // missionMining Location's real extraction reaches quota -- the direct
  // replacement for mineMission's old instant deposit (missionDiaryScreen.ts),
  // now driven by actually mining at the mission's own Location instead of
  // a button click.
  function extractForMissionMining(mission: AcceptedMiningMission, a: FixedObject, cycleDurationMs: number): void {
    const ship = state.currentShip;
    if (!ship) return;
    const rate = effectiveYieldRate(ship.hullTier, state.captainSkills.Mining);
    // Ore hold, not general cargo (16.39) -- mined quota ore is ore.
    const free = freeOreHoldM3(ship.oreHoldM3, state.cargo, state.acceptedMissions);
    if (free <= 0) {
      if (!cargoFullWarned) {
        cargoFullWarned = true;
        showBanner("Cargo hold full — mining suspended");
      }
      return;
    }
    cargoFullWarned = false;
    const amount = Math.min(rate * (cycleDurationMs / 1000), a.poolM3 ?? 0);
    if (amount <= 0) return;
    const result = depositMining(mission, amount, free);
    const actuallyTaken = result.taggedM3 - mission.taggedM3;
    // Mission-tagged ore counts toward the career total the same as
    // free-mined ore -- it was still physically mined by this captain.
    state.stats.oreMinedM3 += actuallyTaken;
    state.acceptedMissions = state.acceptedMissions.map((m) =>
      m.id === mission.id && m.type === "Mining" ? { ...m, taggedM3: result.taggedM3 } : m
    );
    a.poolM3 = (a.poolM3 ?? 0) - actuallyTaken;
    persistNodePool(a);
    if (a.poolM3 <= 0) {
      depleteAsteroid(a);
    }
    if (result.completed) {
      state.credits += mission.rewardCr;
      state.stats.missionsCompleted++;
      state.standing = applyMissionCompletionStanding(state.standing, mission.posterFaction);
      state.acceptedMissions = state.acceptedMissions.filter((m) => m.id !== mission.id);
      // No showBanner() here -- drainGlobalEvent picks state.lastEvent up
      // on the next frame and shows it, so setting both would just
      // double-fire the same message.
      state.lastEvent = `Mined ${mission.quotaM3} m3 ${mission.resource} -- earned ${mission.rewardCr} CR.`;
      triggerSave(ctx);
    }
  }

  function extractFromAsteroid(a: FixedObject, cycleDurationMs: number): void {
    if (currentLocationKind === "missionMining" && activeMissionId) {
      const mission = state.acceptedMissions.find(
        (m): m is AcceptedMiningMission => m.id === activeMissionId && m.type === "Mining"
      );
      if (mission) {
        extractForMissionMining(mission, a, cycleDurationMs);
      }
      return;
    }

    const rate = effectiveYieldRate(state.currentShip?.hullTier ?? "T1", state.captainSkills.Mining);
    // Ore hold, not general cargo (16.39) -- a hold full of wreck salvage no
    // longer stops the mining laser, and vice versa.
    const oreRoom = freeOreHoldM3(state.currentShip?.oreHoldM3 ?? 0, state.cargo, state.acceptedMissions);
    if (oreRoom <= 0) {
      if (!cargoFullWarned) {
        cargoFullWarned = true;
        showBanner("Ore hold full — mining suspended");
      }
      return;
    }
    cargoFullWarned = false;
    // extracted is real m3 mined this tick -- the pool/room caps above are
    // both volume constraints, so the cap math stays entirely m3-native.
    // Only the actual deposit into state.cargo converts to units (16.40),
    // the one place raw ore physically enters the ledger.
    const extracted = Math.min(rate * (cycleDurationMs / 1000), a.poolM3 ?? 0, oreRoom);
    if (extracted <= 0) {
      return;
    }
    const resource = a.resource!;
    const unitsExtracted = extracted / volumePerUnitM3(resource);
    state.cargo[resource] = (state.cargo[resource] ?? 0) + unitsExtracted;
    state.stats.oreMinedM3 += extracted;
    a.poolM3 = (a.poolM3 ?? 0) - extracted;
    persistNodePool(a);
    if (a.poolM3 <= 0) {
      depleteAsteroid(a);
    }
  }

  // Writes a real belt/mining node's just-mutated pool back into
  // state.nodeRegen (16.53) -- a no-op for missionMining's own ephemeral
  // nodes, which never get a nodeRegenKey assigned in rebuildLocationScene.
  function persistNodePool(a: FixedObject): void {
    if (!a.nodeRegenKey) return;
    state.nodeRegen = upsertNodeRegen(state.nodeRegen, {
      key: a.nodeRegenKey,
      poolM3: Math.max(0, a.poolM3 ?? 0),
      poolMaxM3: a.poolMaxM3 ?? 0,
      lastUpdatedAt: Date.now()
    });
  }

  // Shared by destroyHostile/depleteAsteroid/fleet-ship-flee: clears any
  // weapon lock and the current selection once their target stops
  // existing. "The instant the referenced target is destroyed, the
  // highlight clears and the engine goes idle" (Section 4b) -- Move
  // To/Orbit would otherwise stay visually "active" pointing at nothing.
  function clearReferencesTo(id: string): void {
    weapons.forEach((w) => {
      if (w.cycle.targetId === id) {
        w.cycle = { ...w.cycle, targetId: null, nextTickAt: null, pausedRemainingMs: null };
      }
    });
    // Only ever matches "player" or a live fleet ship's own id (16.43) --
    // self-modules can't target a hostile/asteroid, so this is a harmless
    // no-op for those ids.
    selfModules.forEach((m) => {
      if (m.cycle.targetId === id) {
        m.cycle = { ...m.cycle, active: false, targetId: null, nextTickAt: null };
      }
    });
    // Aggro references (16.43) -- a hostile or fleet ship whose current
    // target just stopped existing re-picks next tick anyway (see
    // updateHostileAggro/updateFleetShipAggro), but clearing it here keeps
    // no stale reference lingering even for one frame.
    hostiles.forEach((h) => {
      if (h.aggroTargetId === id) h.aggroTargetId = null;
    });
    fleetShips.forEach((f) => {
      if (f.aggroTargetId === id) f.aggroTargetId = null;
    });
    // 16.100: the destination is now separate from the selection, so both have
    // to be released -- otherwise the ship keeps flying at a dead hostile's last
    // known position with nothing on screen to explain why.
    if (moveTarget?.id === id) {
      moveTarget = null;
      activeMoveMode = null;
      orbit = null;
    }
    if (selected?.id === id) {
      selected = null;
      updateEngineButtons();
    }
  }

  let wreckCounter = 0;

  function destroyHostile(h: LiveHostile): void {
    // Read before clearReferencesTo, which is what drops the selection.
    const wasSelected = selected?.id === h.id;
    const idx = hostiles.indexOf(h);
    if (idx !== -1) hostiles.splice(idx, 1);
    clearReferencesTo(h.id);
    state.stats.hostilesDestroyed++;
    recordKill(state.bestiary, h.name, h.hullTier); // 16.82's per-opponent tally
    // 16.95: destroying a ship costs standing with its faction. Small by
    // design -- Don's ruling is that missions, not kills, drive alignment --
    // but a kill is no longer invisible to reputation, which is what stops a
    // career pirate-hunter staying welcome in pirate space.
    //
    // NPC "Faction" ships have no single owner in the roster, so the hit lands
    // on whichever faction actually holds this region: shooting a patrol in
    // Crimson space annoys the Crimson Concord and nobody else.
    const killedFaction: FactionName | null =
      h.faction === "Pirate" ? "Pirate" : stationGuardFaction(currentSystem.region as GalaxyRegion);
    if (killedFaction) {
      state.standing = adjustStanding(state.standing, killedFaction, shipKillStandingPenalty(h.hullTier));
    }
    playSound("explosion");
    // Section 8: "Wrecks -- destroyed ships... aftermath of a triggered
    // encounter." Already discovered (the captain just watched it happen),
    // unlike a scanned-in object. Real, lootable contents (see
    // buildWreckLoot) -- not a resource/pool like an asteroid, since a
    // wreck's contents are looted whole via the Loot action, not mined
    // incrementally.
    wreckCounter++;
    const wreck: FixedObject = { id: `wreck${wreckCounter}`, name: `${h.name} Wreck`, kind: "wreck", x: h.x, y: h.y, discovered: true, loot: buildWreckLoot(h) };
    fixedObjects.push(wreck);
    // Hand selection straight to the wreck if the captain was targeting the
    // ship that just died (Don's direction). clearReferencesTo above has
    // already dropped the selection, so without this the captain is left
    // targeting nothing at exactly the moment there is something worth
    // targeting, and has to hunt for a 9px rock to loot their own kill.
    //
    // Conditional on having had it selected: auto-selecting a wreck from a
    // kill the captain was not watching -- an escort's, or a stray hit --
    // would yank their target away mid-fight.
    if (wasSelected) {
      selectTarget(wreck);
    }
    showBanner(`${h.name} destroyed`);
  }

  // Transfers up to `names` (or every item, if omitted) from the wreck
  // into ship cargo, each capped by whatever real free cargo space is
  // left at the time it's taken (later items in the same call can get
  // less room, or none, if earlier ones already filled the hold) -- same
  // freeCargoM3 mining/Cargo UI/Market all already share. An emptied
  // wreck is removed from the scene; a partial transfer leaves the
  // remainder aboard for a return trip rather than discarding it.
  function transferFromWreck(wreck: FixedObject, names?: string[]): number {
    const ship = state.currentShip;
    if (!ship || !wreck.loot) return 0;
    let free = freeCargoM3(ship.cargoM3, state.cargo, state.acceptedMissions);
    let itemsTaken = 0;
    const targetNames = names ?? Object.keys(wreck.loot);
    targetNames.forEach((name) => {
      const qty = wreck.loot?.[name] ?? 0;
      if (free <= 0 || qty <= 0) return;
      const taken = Math.min(qty, free);
      if (taken <= 0) return;
      state.cargo[name] = (state.cargo[name] ?? 0) + taken;
      wreck.loot![name] -= taken;
      if (wreck.loot![name] <= 0) delete wreck.loot![name];
      free -= taken;
      itemsTaken += taken;
    });

    if (itemsTaken > 0) {
      triggerSave(ctx);
    }

    const remaining = Object.keys(wreck.loot).length;
    if (remaining === 0) {
      showBanner(itemsTaken > 0 ? `Looted ${wreck.name} clean` : `${wreck.name} has nothing left to loot`);
      const idx = fixedObjects.indexOf(wreck);
      if (idx !== -1) fixedObjects.splice(idx, 1);
      clearReferencesTo(wreck.id);
      closeLootPopup();
    } else if (itemsTaken > 0) {
      showBanner(`Looted ${wreck.name} -- cargo hold full, some loot left behind`);
    } else {
      showBanner("Cargo hold full -- nothing to loot");
    }
    updateEngineButtons();
    return itemsTaken;
  }

  // Loot popup (Don's direction: "show a small cargo transfer UI with
  // transfer all button or let us select an object... and move one or
  // more of them over") -- replaces the earlier one-click "loot
  // everything" action with a real per-item selection UI, plus a
  // Transfer All shortcut for the common case. Only wreck-to-ship is
  // supported (not the reverse) -- dumping cargo *into* a wreck you're
  // about to leave behind has no real use case here, unlike Cargo UI's
  // own Jettison action which already covers "get rid of unwanted cargo."
  let lootPopupWreckId: string | null = null;
  const lootPopupSelected = new Set<string>();

  function openLootPopup(wreckId: string): void {
    lootPopupWreckId = wreckId;
    lootPopupSelected.clear();
    renderLootPopup();
  }

  function closeLootPopup(): void {
    lootPopupWreckId = null;
    renderLootPopup();
  }

  function renderLootPopup(): void {
    const modal = wrap.querySelector<HTMLElement>("[data-loot-modal]")!;
    const wreck = lootPopupWreckId ? fixedObjects.find((f) => f.id === lootPopupWreckId && f.kind === "wreck") : null;
    if (!wreck?.loot) {
      modal.classList.remove("show");
      modal.innerHTML = "";
      return;
    }
    const rows = Object.entries(wreck.loot);
    modal.classList.add("show");
    modal.innerHTML = `
      <div class="lot-modal-panel">
        <div class="lot-modal-header">
          <strong>${wreck.name}</strong>
          <button type="button" class="lot-modal-close" data-loot-close>&times;</button>
        </div>
        <p class="dim">Select items to transfer to your cargo hold, or transfer everything at once.</p>
        <div class="cargo-list">
          ${rows
            .map(
              ([name, qty]) => `
                <div class="cargo-row">
                  <label>
                    <input type="checkbox" data-loot-select="${name}" ${lootPopupSelected.has(name) ? "checked" : ""} />
                    <span class="cargo-row-name">${name}</span>
                  </label>
                  <span class="dim">${qty.toFixed(1)} units</span>
                </div>
              `
            )
            .join("")}
        </div>
        <div class="cargo-actions">
          <button type="button" class="btn-secondary" data-loot-transfer-selected ${lootPopupSelected.size === 0 ? "disabled" : ""}>Transfer Selected</button>
          <button type="button" data-loot-transfer-all>Transfer All</button>
        </div>
      </div>
    `;

    modal.querySelector<HTMLButtonElement>("[data-loot-close]")!.addEventListener("click", closeLootPopup);
    modal.querySelectorAll<HTMLInputElement>("input[data-loot-select]").forEach((el) => {
      el.addEventListener("change", () => {
        const name = el.dataset.lootSelect!;
        if (el.checked) lootPopupSelected.add(name);
        else lootPopupSelected.delete(name);
        renderLootPopup();
      });
    });
    modal.querySelector<HTMLButtonElement>("[data-loot-transfer-selected]")?.addEventListener("click", () => {
      if (!wreck) return;
      transferFromWreck(wreck, [...lootPopupSelected]);
      lootPopupSelected.clear();
      renderLootPopup();
    });
    modal.querySelector<HTMLButtonElement>("[data-loot-transfer-all]")!.addEventListener("click", () => {
      if (!wreck) return;
      transferFromWreck(wreck);
      renderLootPopup();
    });
  }

  function depleteAsteroid(a: FixedObject): void {
    const idx = fixedObjects.indexOf(a);
    if (idx !== -1) fixedObjects.splice(idx, 1);
    clearReferencesTo(a.id);
    showBanner(`${a.name} depleted`);
  }

  // Pays out and removes a real accepted Combat mission once its
  // missionCombat Location's wave encounter is fully cleared -- the direct
  // replacement for engageMission's old instant "won" branch
  // (missionDiaryScreen.ts), now driven by actually surviving real-time
  // combat at the mission's own Location instead of a button click.
  function completeMissionCombat(missionId: string): void {
    const mission = state.acceptedMissions.find(
      (m): m is AcceptedCombatMission => m.id === missionId && m.type === "Combat"
    );
    if (!mission) return;
    state.credits += mission.rewardCr;
    state.stats.missionsCompleted++;
    state.standing = applyMissionCompletionStanding(state.standing, mission.posterFaction);
    state.acceptedMissions = state.acceptedMissions.filter((m) => m.id !== missionId);
    // No showBanner() here either -- see the note in
    // extractForMissionMining above.
    state.lastEvent = `Mission cleared -- earned ${mission.rewardCr} CR.`;
    triggerSave(ctx);
  }

  // Section 16.30/16.12, LOCKED: a Wave encounter's stages, with a
  // recovery gap in between. Called once per frame; a no-op unless the
  // active wave has just been cleared or a gap has just ended. Only a real
  // "missionCombat" Location (see buildSystemLocations/rebuildLocationScene)
  // arms this scripted machinery -- every other Location kind, including
  // the ambient "combat" flavor Location, leaves it untouched and relies
  // entirely on the danger-tier-driven ambient contact system instead
  // (maybeTriggerAmbientContact/spawnAmbientGroup below), which works
  // everywhere independently of this.
  function checkWaveProgress(now: number): void {
    if (!encounterActive) return;
    if (waveGapUntil !== null) {
      if (now >= waveGapUntil) {
        waveGapUntil = null;
        currentWaveIndex++;
        hostiles.push(...buildWaveHostiles(currentWaveIndex));
        showBanner(`Wave ${currentWaveIndex + 1}/${waveSegments.length} inbound`);
      }
      return;
    }
    if (hostiles.length > 0 || encounterCleared) {
      return;
    }
    if (currentWaveIndex + 1 < waveSegments.length) {
      waveGapUntil = now + WAVE_GAP_SECONDS * 1000;
      showBanner(`Wave ${currentWaveIndex + 1}/${waveSegments.length} cleared — recovery gap`);
    } else {
      encounterCleared = true;
      showBanner("Encounter cleared");
      if (currentLocationKind === "missionCombat" && activeMissionId) {
        completeMissionCombat(activeMissionId);
      }
    }
  }

  // Section 8/16.1, LOCKED shape: "Ambient" is one of three real Hostile
  // Contact sources ("procedural, risk accumulates from scanning
  // frequency, mining duration, and the location's danger tier") --
  // triggered per scan attempt and per mining tick below, using 16.1's
  // real per-Danger-Tier trigger-chance and group-size tables. Tier 0
  // (Safe) gets no ambient combat at all, consistent with "hub systems
  // being genuinely safe" elsewhere in the doc. Unit stats reuse the real
  // PT1-013 unit pool (allUnits, all Pirate faction), approximated by
  // mapping Danger Tier directly onto the closest hullTier (T1..T4) --
  // ship-size hullTier and System dangerTier are two different axes
  // nowhere unified in the real data model, so this is a flagged
  // illustrative proxy, not 16.1's exact HP/DPS curve.
  const AMBIENT_TRIGGER_CHANCE: Record<number, number> = { 0: 0, 1: 0.1, 2: 0.15, 3: 0.2, 4: 0.25 };
  const AMBIENT_GROUP_SIZE: Record<number, [number, number]> = { 0: [0, 0], 1: [1, 2], 2: [2, 3], 3: [2, 4], 4: [3, 5] };
  // Section 8's own locked wording for the staged warning sequence, giving
  // the captain real reaction time (a chance to flee) before an ambient
  // encounter actually arrives, instead of hostiles just appearing.
  const AMBIENT_WARNING_TEXT = ["Hostiles detected in the system", "We've been located", "They're closing in", "Contact!"];
  const AMBIENT_STAGE_GAP_MS = 3000;
  let ambientStage = 0; // 0 = idle; 1..4 = which warning banner is next
  let ambientNextAt: number | null = null;
  let ambientSpawnCounter = 0;

  // Environmental hazards (16.66, resolves 16.49's "hazard frequency per
  // system"): untied to danger tier -- only systems flagged with a real
  // `environmentalHazard` (src/data/systems.json, example content, not an
  // exhaustive pass) can trigger one at all. Always telegraphed with real
  // advance warning (Section 6), then a single chip-damage hit if the
  // captain is still in the same system when it lands -- inconvenience,
  // not lethal, and easy to dodge just by leaving.
  const ENV_HAZARD_CHECK_CHANCE = 0.15;
  const ENV_HAZARD_WARNING_MS = 12000;
  const ENV_HAZARD_CHIP_DAMAGE_PERCENT = 0.15;
  let envHazardWarningAt: number | null = null;
  let envHazardSystemId: string | null = null;

  function maybeTriggerEnvironmentalHazard(): void {
    if (envHazardWarningAt !== null || fleeing || controlsLocked) return;
    const hazard = currentSystem.environmentalHazard;
    if (!hazard || Math.random() >= ENV_HAZARD_CHECK_CHANCE) return;
    envHazardSystemId = currentSystem.id;
    envHazardWarningAt = performance.now() + ENV_HAZARD_WARNING_MS;
    showBanner(`${hazard.label} incoming -- clear the area or brace for impact`);
  }

  function updateEnvironmentalHazard(now: number): void {
    if (envHazardWarningAt === null || now < envHazardWarningAt) return;
    envHazardWarningAt = null;
    const hazard = currentSystem.environmentalHazard;
    // Fast-Traveled/Warped to a different system before it landed -- the
    // captain's choice to leave is exactly the escape this is meant to
    // reward, so the hazard simply doesn't happen.
    if (!hazard || currentSystem.id !== envHazardSystemId) return;
    const damage = (player.shieldMax + player.hullMax) * ENV_HAZARD_CHIP_DAMAGE_PERCENT;
    applyDamageToPlayer(damage);
    showBanner(`${hazard.label} hit -- chip damage taken`);
  }

  function maybeTriggerAmbientContact(): void {
    if (podIsImmune(state.inEscapePod)) return; // 16.72: the pod is never attacked
    if (ambientStage !== 0 || fleeing || controlsLocked) return;
    const chance = AMBIENT_TRIGGER_CHANCE[currentSystem.dangerTier] ?? 0;
    if (chance <= 0 || Math.random() >= chance) return;
    ambientStage = 1;
    ambientNextAt = 0; // fires on the very next updateAmbientContact() tick
  }

  // Random attacks (16.70): in any non-Safe system the captain can be
  // jumped regardless of what they're doing -- not only as a consequence
  // of scanning/mining, which is all 16.1's per-action check ever covered.
  // Reuses the exact same staged-warning + spawn path as an ambient
  // contact, so a random attack and a mining-triggered one look and
  // resolve identically; only the trigger differs.
  //
  // Scheduled against the Expedition View's own frame clock, so time only
  // counts while the captain is actually out in space -- a captain parked
  // in a Station/Base menu isn't quietly accruing an ambush.
  // (`nextRandomAttackAt` itself is declared far above -- see its comment.)

  function rescheduleRandomAttack(now: number): void {
    nextRandomAttackAt = systemAllowsRandomAttack(currentSystem.dangerTier)
      ? now + nextRandomAttackDelayMs()
      : null;
  }

  function updateRandomAttack(now: number): void {
    // 16.72: a podded captain is never jumped, no matter how dangerous
    // the system. Cleared rather than merely skipped so the countdown
    // restarts fresh once they're back in a real hull.
    if (podIsImmune(state.inEscapePod)) {
      nextRandomAttackAt = null;
      return;
    }
    if (!systemAllowsRandomAttack(currentSystem.dangerTier)) {
      nextRandomAttackAt = null;
      return;
    }
    if (nextRandomAttackAt === null) {
      rescheduleRandomAttack(now);
      return;
    }
    if (now < nextRandomAttackAt) return;
    rescheduleRandomAttack(now);
    // Don't stack on top of an ambush already inbound, and don't ambush a
    // ship mid-Fast-Travel -- just wait for the next scheduled roll.
    if (ambientStage !== 0 || fleeing || controlsLocked) return;
    ambientStage = 1;
    ambientNextAt = 0;
  }

  // Planetary Defenses (16.70): if the Location's planet has a Base with
  // its defenses built, anything hostile that comes within range is
  // acquired and destroyed a few seconds later. Tracked per hostile id so
  // each kill gets its own countdown (and its own beam), rather than one
  // shared timer that would wipe a whole group on the same frame.
  // (`defenseLocks` itself is declared far above -- see its comment.)

  function defendedPlanetObject(): FixedObject | undefined {
    const planet = fixedObjects.find((f) => f.kind === "planet");
    if (!planet?.leaseKey) return undefined;
    const [systemId, planetId] = planet.leaseKey.split(":");
    return planetIsDefended(state.bases, systemId, planetId) ? planet : undefined;
  }

  function updatePlanetaryDefenses(now: number): void {
    const planet = defendedPlanetObject();
    if (!planet) {
      if (defenseLocks.size > 0) defenseLocks.clear();
      return;
    }
    for (const h of [...hostiles]) {
      const inRange = Math.hypot(h.x - planet.x, h.y - planet.y) <= PLANETARY_DEFENSE_RANGE_M;
      if (!inRange) {
        defenseLocks.delete(h.id); // fled the umbrella -- it gets to live
        continue;
      }
      const killAt = defenseLocks.get(h.id);
      if (killAt === undefined) {
        defenseLocks.set(h.id, now + PLANETARY_DEFENSE_LOCK_MS);
        // One beam from the planet itself, lasting the whole lock, so the
        // kill is visibly *caused* by the defenses rather than unexplained.
        spawnBeam("laser", planet.id, h.id, now, PLANETARY_DEFENSE_LOCK_MS);
        continue;
      }
      if (now >= killAt) {
        defenseLocks.delete(h.id);
        destroyHostile(h); // already plays the explosion + drops a wreck
      }
    }
  }

  /**
   * The station's garrison (16.94).
   *
   * Ringed around the station rather than around the player, so a captain can
   * see the formation from a distance and decide whether to close -- and so a
   * hostile garrison has to be flown *through* rather than appearing on top of
   * you.
   *
   * Hull tier tracks the system's danger tier, which makes a hub in settled
   * space a genuinely hard target and a frontier outpost a plausible one.
   */
  function spawnStationGuards(): LiveHostile[] {
    const region = currentSystem.region as GalaxyRegion;
    const stationClass = currentSystem.stationClass ?? "hub";
    const count = stationGuardCount(currentSystem.id, stationClass);
    const npcFaction = guardNpcFaction(region);
    const hostileOnSight = guardsAttackOnSight(state.standing, region);

    const tierKey = Math.min(4, Math.max(1, currentSystem.dangerTier || 1));
    const hullTier = `T${tierKey}` as NpcHullTier;
    const pool = allUnits.filter((u) => u.faction === npcFaction && u.hullTier === hullTier);
    // Fall back through tiers rather than spawning nothing: a station with no
    // garrison because its exact tier/faction pairing is missing from the
    // roster would silently be free to dock at.
    const units = pool.length > 0 ? pool : allUnits.filter((u) => u.faction === npcFaction);
    if (units.length === 0) return [];

    const station = fixedObjects.find((f) => f.kind === "station");
    const cx = station?.x ?? 0;
    const cy = station?.y ?? 0;

    const guards: LiveHostile[] = [];
    for (let i = 0; i < count; i++) {
      const unit = units[i % units.length];
      const angle = (i / count) * Math.PI * 2;
      guards.push({
        id: `guard${i + 1}`,
        name: unit.name,
        faction: unit.faction,
        hullTier: unit.hullTier,
        x: cx + Math.cos(angle) * STATION_GUARD_RING_M,
        y: cy + Math.sin(angle) * STATION_GUARD_RING_M,
        shield: unit.shieldHp,
        shieldMax: unit.shieldHp,
        hull: unit.hullHp,
        hullMax: unit.hullHp,
        dps: unit.dps,
        nextFireAt: null,
        heading: angle + Math.PI,
        speed: 0,
        topSpeed: inLocationTopSpeed(unit.hullTierLabel) * 0.9,
        detectionRangeM: npcDetectionRangeM(unit.hullTier),
        weaponRangeM: NPC_WEAPON_RANGE_M[unit.hullTier],
        aggroTargetId: null,
        guard: true,
        passive: !hostileOnSight
      });
    }

    const disposition = guardDisposition(state.standing, region);
    if (hostileOnSight) {
      showBanner(`${count} station guards moving to intercept — you are not welcome here`);
    } else if (disposition === "welcome") {
      showBanner(`${count} station guards standing down — you are known here`);
    }
    return guards;
  }

  /** How many guards are actively shooting -- the docking gate reads this. */
  function engagedGuardCount(): number {
    return hostiles.filter((h) => h.guard && !h.passive && h.hull > 0).length;
  }

  /**
   * Shooting one guard brings the whole garrison. Called from the damage path
   * rather than from aggro, so it fires on the captain's first shot rather than
   * a frame later.
   */
  function provokeGarrison(): void {
    let woke = false;
    hostiles.forEach((h) => {
      if (h.guard && h.passive) {
        h.passive = false;
        woke = true;
      }
    });
    if (woke) {
      showBanner("Station guards are returning fire");
      updateEngineButtons();
    }
  }

  function spawnAmbientGroup(): void {
    const tierKey = Math.min(4, Math.max(1, currentSystem.dangerTier));
    const [min, max] = AMBIENT_GROUP_SIZE[tierKey] ?? [1, 1];
    const groupSize = min + Math.floor(Math.random() * (max - min + 1));
    const hullTier = `T${tierKey}` as NpcHullTier;
    const pool = allUnits.filter((u) => u.faction === "Pirate" && u.hullTier === hullTier);
    if (pool.length === 0) return;
    const baseAngle = Math.random() * Math.PI * 2;
    for (let i = 0; i < groupSize; i++) {
      const unit = pool[Math.floor(Math.random() * pool.length)];
      const angle = baseAngle + (i / groupSize) * Math.PI * 2;
      ambientSpawnCounter++;
      hostiles.push({
        id: `ambient${ambientSpawnCounter}`,
        name: unit.name,
        faction: unit.faction,
        hullTier: unit.hullTier,
        x: player.x + Math.cos(angle) * 2200,
        y: player.y + Math.sin(angle) * 2200,
        shield: unit.shieldHp,
        shieldMax: unit.shieldHp,
        hull: unit.hullHp,
        hullMax: unit.hullHp,
        dps: unit.dps,
        nextFireAt: null,
        heading: 0,
        speed: 0,
        topSpeed: inLocationTopSpeed(unit.hullTierLabel) * 0.9,
        detectionRangeM: npcDetectionRangeM(unit.hullTier),
        weaponRangeM: NPC_WEAPON_RANGE_M[unit.hullTier],
        aggroTargetId: null
      });
    }
  }

  function updateAmbientContact(now: number): void {
    if (ambientStage === 0 || ambientNextAt === null || now < ambientNextAt) return;
    showBanner(AMBIENT_WARNING_TEXT[ambientStage - 1]);
    if (ambientStage < 4) {
      ambientStage++;
      ambientNextAt = now + AMBIENT_STAGE_GAP_MS;
    } else {
      spawnAmbientGroup();
      ambientStage = 0;
      ambientNextAt = null;
    }
  }

  // Loaner hull mutiny (16.73). Warnings escalate as the hull falls and
  // each fires exactly once -- `loanerWarningTierShown` only ever rises,
  // so repairing back up and taking damage again doesn't re-nag. The
  // mutiny itself is irreversible by design, which is exactly why the
  // warnings and the striped bar exist. Returns true if the captain was
  // ejected, so the caller can stop touching a ship that's now gone.
  let loanerWarningTierShown: 0 | 1 | 2 | 3 = 0;

  function checkLoanerHullMutiny(): boolean {
    if (!state.isLoanerShip) return false;
    const fraction = player.hullMax > 0 ? player.hull / player.hullMax : 0;
    const tier = loanerHullWarningTier(fraction);

    if (tier >= 3) {
      if (!runLoanerHullMutiny(state, fraction)) return false;
      showBanner("Mutiny! The crew warp the loaner home — you're left in your pod");
      triggerSave(ctx);
      // Straight to the Pod: there is no ship left to keep rendering, and
      // the captain stays in this same System/Location (16.73).
      ctx.navigate("escapePod");
      return true;
    }

    if (tier > loanerWarningTierShown && (tier === 1 || tier === 2)) {
      loanerWarningTierShown = tier;
      showBanner(loanerWarningMessage(tier));
    }
    return false;
  }

  function applyDamageToPlayer(damage: number): void {
    if (damage <= 0 || player.hull <= 0) return;
    let remaining = damage;
    if (player.shield > 0) {
      const absorbed = Math.min(player.shield, remaining);
      player.shield -= absorbed;
      remaining -= absorbed;
    }
    player.hull = Math.max(0, player.hull - remaining);
    // Loaner hull mutiny (16.58/16.73) -- checked on the real damage path
    // rather than per-frame, so the crew react the instant a hit lands.
    // Runs before the destruction check below: a loaner's crew bail at
    // 25% and warp the ship out, so it never reaches 0 hull at all.
    if (checkLoanerHullMutiny()) return;
    if (player.hull <= 0) {
      showBanner("Falcon destroyed — ship disabled");
      // Armory weapon upgrade levels reset on destruction (Don's
      // direction, 16.36) -- this specific ship instance's levels are
      // gone; a future replacement (or the same ship respawned) starts
      // back at level 1 on every weapon.
      if (state.currentShipInstanceId) {
        state.weaponUpgrades = resetShipUpgrades(state.weaponUpgrades, state.currentShipInstanceId);
        // A destroyed hull has no damage to carry, and leaving the record
        // behind would follow a replacement that reused the id (16.81).
        delete state.shipDamage[state.currentShipInstanceId];
      }
    }
  }

  // Section 10, LOCKED: "a hired escort's crew, while Patrolling, will
  // flee once hull drops to 10% remaining" -- applied here to every real
  // fleet ship, hired or owned, since the doc never distinguishes the two
  // for this specific rule and there's no reason an owned ship's crew
  // would be braver about it.
  function applyDamageToFleetShip(f: LiveFleetShip, damage: number): void {
    if (damage <= 0 || f.fled) return;
    let remaining = damage;
    if (f.shield > 0) {
      const absorbed = Math.min(f.shield, remaining);
      f.shield -= absorbed;
      remaining -= absorbed;
    }
    f.hull = Math.max(0, f.hull - remaining);
    if (f.hull / f.hullMax <= FLEET_SHIP_FLEE_HULL_FRACTION) {
      f.fled = true;
      clearReferencesTo(f.id);
      showBanner(`${f.name} is fleeing — hull critical`);
    }
  }

  // A drone has one flat EHP figure (DRONE_STATS), not a separate
  // shield/hull split -- damage just comes straight off hp. "Individual
  // drones are real, ownable, losable assets" (16.23): destroyed means
  // permanently removed, not knocked to 0 and left sitting there.
  function applyDamageToDrone(d: LiveDrone, damage: number): void {
    if (damage <= 0) return;
    d.hp = Math.max(0, d.hp - damage);
    if (d.hp <= 0) {
      const idx = drones.indexOf(d);
      if (idx !== -1) drones.splice(idx, 1);
      // A destroyed drone is a real, permanent loss (16.23/16.74) --
      // consume it from cargo so the bay stays short until the captain
      // buys a replacement at a Market.
      state.stats.dronesLost++;
      const carried = state.cargo[droneItemName] ?? 0;
      if (carried > 0) {
        const left = carried - 1;
        if (left > 0) state.cargo[droneItemName] = left;
        else delete state.cargo[droneItemName];
        triggerSave(ctx);
      }
      // A spare launches to fill the gap (16.87) -- that is what the spares
      // are FOR: the captain keeps fighting at full complement across a few
      // missions instead of degrading mid-fight and needing a Market trip.
      // Only while some of the complement is still out, so a full RECALL isn't
      // silently undone by a straggler dying.
      const replacement = drones.find((other) => !other.deployed && other.hp > 0);
      const stillFighting = drones.some((other) => other.deployed);
      if (replacement && stillFighting) {
        replacement.deployed = true;
        replacement.nextFireAt = null;
        showBanner("A drone was destroyed — a spare launched");
      } else {
        showBanner("A drone was destroyed");
      }
    }
  }

  // Shared by the per-cycle-completion repair (updateModules below) and
  // Shield's new immediate-on-activation repair (see the self-module tap
  // handler) so the two never diverge. `targetId` is "player" or any live
  // fleet ship's own id (16.43) -- Section 4a: "can target the flagship
  // or any other fleet ship."
  // Returns whether any HP was actually restored -- a Ferrite-starved hull
  // cycle must not fire the repair pulse/sound for a repair that never
  // happened.
  function applySelfModuleRepair(m: LiveSelfModule, targetId: string): boolean {
    const target = targetId === "player" ? player : fleetShipById(targetId);
    if (!target) return false;
    const perSecond =
      targetId === "player"
        ? m.key === "shield"
          ? shieldRegenPerS
          : hullRepairPerS
        : m.key === "shield"
          ? (target as LiveFleetShip).shieldRegenPerS
          : (target as LiveFleetShip).repairPerS;
    const amount = perSecond * (m.cycleDurationMs / 1000);
    if (m.key === "shield") {
      target.shield = Math.min(target.shieldMax, target.shield + amount);
      return true;
    }
    // Hull repair burns Ferrite Ore out of the hold (16.4/16.80); Shield
    // regen is powered, not material, and stays free. Only the HP actually
    // missing is billed, so topping off a nearly-full hull is cheap.
    const missing = Math.max(0, target.hullMax - target.hull);
    if (missing <= 0) return false;
    const draw = drawRepair(Math.min(amount, missing), state.cargo[REPAIR_ORE] ?? 0);
    if (draw.ferriteSpent > 0) {
      const left = (state.cargo[REPAIR_ORE] ?? 0) - draw.ferriteSpent;
      if (left > 0.0001) state.cargo[REPAIR_ORE] = left;
      else delete state.cargo[REPAIR_ORE];
      target.hull = Math.min(target.hullMax, target.hull + draw.hpRepaired);
    }
    if (draw.starved) {
      m.cycle = IDLE_SELF_MODULE;
      showBanner(`Out of ${REPAIR_ORE} — hull repair offline`);
    }
    return draw.hpRepaired > 0;
  }

  // 16.43: real pursuit AI, shared shape for both hostiles (aggroing onto
  // the player/a fleet ship) and fleet ships (aggroing onto a hostile).
  // Keeps the current target as long as it's still alive and still
  // within detection range, rather than re-picking every single frame --
  // otherwise a target sitting right at the range boundary would flicker.
  function pickNearestInRange(from: Point, candidates: NamedPoint[], rangeM: number): string | null {
    let bestId: string | null = null;
    let bestDist = rangeM;
    candidates.forEach((c) => {
      const d = Math.hypot(c.x - from.x, c.y - from.y);
      if (d <= bestDist) {
        bestDist = d;
        bestId = c.id;
      }
    });
    return bestId;
  }

  function shipCandidatePositions(): NamedPoint[] {
    const live: NamedPoint[] = [{ id: "player", name: "you", x: player.x, y: player.y }];
    fleetShips.forEach((f) => {
      if (!f.fled) live.push({ id: f.id, name: f.name, x: f.x, y: f.y });
    });
    return live;
  }

  function updateHostileAggro(): void {
    // Cloak (16.61): no hostile can acquire a new lock while active --
    // existing locks simply freeze as-is (see hostileMoveTargetPoint's
    // own frozenTargetPoint branch), so skip re-evaluation entirely.
    if (cloakActive(performance.now())) return;
    // 16.72: hostiles ignore a podded captain outright -- every existing
    // lock drops too, so anything already chasing loses interest rather
    // than merely failing to re-acquire.
    if (podIsImmune(state.inEscapePod)) {
      hostiles.forEach((h) => {
        h.aggroTargetId = null;
      });
      return;
    }
    const candidates = shipCandidatePositions();
    hostiles.forEach((h) => {
      // A passive station guard holds fire entirely (16.94) -- it acquires no
      // target at all rather than acquiring one and declining to shoot, so
      // nothing downstream (movement, fire cycles, the HUD) has to know about
      // the distinction.
      if (h.guard && h.passive) {
        h.aggroTargetId = null;
        return;
      }
      if (h.aggroTargetId) {
        const cur = candidates.find((c) => c.id === h.aggroTargetId);
        if (cur && Math.hypot(cur.x - h.x, cur.y - h.y) <= h.detectionRangeM) return;
      }
      h.aggroTargetId = pickNearestInRange(h, candidates, h.detectionRangeM);
    });
  }

  function updateFleetShipAggro(): void {
    fleetShips.forEach((f) => {
      if (f.fled) {
        f.aggroTargetId = null;
        return;
      }
      if (f.aggroTargetId) {
        const cur = hostiles.find((h) => h.id === f.aggroTargetId);
        if (cur && Math.hypot(cur.x - f.x, cur.y - f.y) <= f.detectionRangeM) return;
      }
      f.aggroTargetId = pickNearestInRange(f, hostiles, f.detectionRangeM);
    });
  }

  // Where a hostile should move this frame: toward whatever it's aggroed
  // onto (player or a fleet ship), or null (decelerate to a stop, per
  // stepTowardTarget's own null-target behavior) if nothing's aggroed.
  function hostileMoveTargetPoint(h: LiveHostile): Point | null {
    if (!h.aggroTargetId) return null;
    // Cloak (16.61): "continues in its last real heading" -- once a
    // frozenTargetPoint snapshot exists, keep steering at THAT point
    // (not the real, now-hidden live position) for as long as Cloak is
    // active, however the hostile got aggroed onto it.
    if (cloakActive(performance.now()) && h.frozenTargetPoint) {
      return h.frozenTargetPoint;
    }
    if (h.frozenTargetPoint) {
      h.frozenTargetPoint = undefined; // Cloak ended -- resume live tracking
    }
    if (h.aggroTargetId === "player") return { x: player.x, y: player.y };
    const f = fleetShipById(h.aggroTargetId);
    return f ? { x: f.x, y: f.y } : null;
  }

  // Where a fleet ship should move: toward whatever hostile it's aggroed
  // onto, or a loose formation position near the player by default --
  // the old escort's own "always chase the player" behavior, now just the
  // fallback rather than the only behavior.
  function fleetShipMoveTargetPoint(f: LiveFleetShip): Point {
    if (f.aggroTargetId) {
      const h = hostiles.find((x) => x.id === f.aggroTargetId);
      if (h) return { x: h.x, y: h.y };
    }
    return { x: player.x, y: player.y };
  }

  function applyDamageToShipCandidate(id: string, damage: number): void {
    if (id === "player") {
      applyDamageToPlayer(damage);
      return;
    }
    const f = fleetShipById(id);
    if (f) applyDamageToFleetShip(f, damage);
  }

  // Captain Skill XP accrual (16.31's "1 XP per second of active
  // engagement", wired in 16.69). Deliberately driven off the same live
  // module/ship state that drives real damage, extraction and movement --
  // never off a separate counter that could drift from what's actually
  // happening. Engagement means *doing the thing*: a weapon locked on an
  // in-range target, not merely owning one.
  //
  // Note this is real-frame dt, so it accrues only while the captain is
  // actually in the Expedition View with the game running -- consistent
  // with a skill system that "reflects how a captain spends their time"
  // (16.19), rather than idle wall-clock time.
  function accrueSkillXp(dt: number): void {
    const skills = state.captainSkills;
    const engaged = (w: LiveWeapon): boolean => {
      if (!w.cycle.targetId) return false;
      const target = resolveNamedPoint(w.cycle.targetId);
      return !!target && Math.hypot(target.x - player.x, target.y - player.y) <= w.range;
    };

    if (weapons.some((w) => w.key === "mining" && engaged(w))) {
      skills.Mining = addEngagementSeconds(skills.Mining, dt);
    }
    if (weapons.some((w) => w.key !== "mining" && engaged(w))) {
      skills.Combat = addEngagementSeconds(skills.Combat, dt);
    }
    if (selfModules.some((m) => m.cycle.active && m.cycle.targetId)) {
      skills.Repair = addEngagementSeconds(skills.Repair, dt);
    }
    if (player.speed > 0.5) {
      skills.Piloting = addEngagementSeconds(skills.Piloting, dt);
    }
    if (fixedObjects.some((f) => f.kind === "wreck" && f.tractored)) {
      skills.TractorBeam = addEngagementSeconds(skills.TractorBeam, dt);
    }
    // Command: real fleet operation -- an escort of yours actively
    // engaging something, which is the only fleet activity that exists
    // (16.43). This is what 16.19's Command skill had nothing to boost
    // against before 16.69 gave it the fleet-capacity gate.
    if (fleetShips.some((f) => !f.fled && f.aggroTargetId)) {
      skills.Command = addEngagementSeconds(skills.Command, dt);
    }
  }

  function updateModules(now: number, dt: number): void {
    checkWaveProgress(now);
    updateAmbientContact(now);
    updateRandomAttack(now);
    updatePlanetaryDefenses(now);
    updateEnvironmentalHazard(now);
    accrueSkillXp(dt);

    // 16.30's automatic wave-gap shield regen is GONE (16.81, Don's
    // direction: shields only come back via an activated module or a paid
    // repair). The gap itself is untouched and still does its real job --
    // it's a window with zero incoming damage in which the captain can
    // turn the Shield module on and let it cycle. What it no longer does
    // is hand back HP the captain never asked for.

    // Docked drones repair steadily, ALWAYS -- not only during a Wave
    // encounter's recovery gap the way 16.23/16.30 originally scoped it
    // (amended by 16.74). This is what makes recalling a damaged drone a
    // real decision: pull it in and it slowly comes back, leave it out
    // and it dies for good. Deployed drones never self-repair.
    if (hasDroneBay) {
      drones.forEach((d) => {
        if (!d.deployed && d.hp < d.maxHp) {
          d.hp = Math.min(d.maxHp, d.hp + (d.maxHp / DRONE_HEAL_SECONDS) * dt);
        }
      });
    }

    if (hasDroneBay) {
      updateDroneTargeting();
      const droneTarget = droneTargetId ? resolveHostile(droneTargetId) : undefined;
      drones.forEach((d) => {
        if (!d.deployed) {
          d.nextFireAt = null;
          return;
        }
        if (!droneTarget) {
          d.nextFireAt = null;
          return;
        }
        if (d.nextFireAt === null) {
          d.nextFireAt = now + DRONE_CYCLE_DURATION_MS;
        }
        if (now >= d.nextFireAt) {
          const damage = rollDamage(d.dps * (DRONE_CYCLE_DURATION_MS / 1000), Math.random);
          applyDamageToHostile(droneTarget, damage);
          d.nextFireAt = now + DRONE_CYCLE_DURATION_MS;
        }
      });
      const targetEl = wrap.querySelector<HTMLElement>("[data-drone-target]");
      if (targetEl) {
        targetEl.textContent = droneTarget ? droneTarget.name.toUpperCase() : "NO TARGET";
      }
    }

    weapons.forEach((w) => {
      const target = w.cycle.targetId ? resolveNamedPoint(w.cycle.targetId) : null;
      if (w.cycle.targetId && !target) {
        w.cycle = { ...w.cycle, targetId: null }; // target no longer exists
      }
      const inRange = !!target && Math.hypot(target.x - player.x, target.y - player.y) <= w.range;
      const result = tickWeaponCycle(w.cycle, now, w.cycleDurationMs, inRange);
      w.cycle = result.state;
      applyModuleVisual(w.key, result.visual, result.progress);

      // Fire exactly once per completed cycle -- no more separate "ready"
      // pause to wait out or tap into (the tap-bonus mechanic was removed,
      // see moduleCycle.ts's file header). Mining Laser is "explicitly not
      // a weapon" (Section 9's naming rationale) -- it extracts resources
      // from an asteroid instead of dealing HP damage; every other
      // Hardpoint type deals damage as before.
      if (result.justFired) {
        if (w.key === "mining") {
          const asteroid = w.cycle.targetId ? resolveAsteroid(w.cycle.targetId) : undefined;
          if (asteroid) {
            const hadPool = (asteroid.poolM3 ?? 0) > 0;
            extractFromAsteroid(asteroid, w.cycleDurationMs);
            maybeTriggerAmbientContact();
            maybeTriggerEnvironmentalHazard();
            // Resource-collection particles (Don's direction): a few bits
            // of ore visibly drift from the rock back to the ship on each
            // real extraction tick -- skipped if the node was already
            // empty (no extraction actually happened this tick).
            if (hadPool) {
              for (let i = 0; i < 4; i++) {
                spawnParticle("mining", asteroid, player, now, 700 + Math.random() * 300);
              }
              playSound("mining");
            }
          }
        } else {
          const hostileTarget = w.cycle.targetId ? resolveHostile(w.cycle.targetId) : undefined;
          if (hostileTarget) {
            // Armory weapon upgrade levels (16.36) -- a flat multiplier on
            // top of the illustrative WEAPON_DPS base, per this specific
            // ship INSTANCE (not every ship of this name/variant).
            const multiplier = state.currentShipInstanceId
              ? weaponMultiplier(state.weaponUpgrades, state.currentShipInstanceId, w.key as WeaponKey)
              : 1;
            // Range-based hit chance (16.66): the exact curve for this
            // weapon's own type, evaluated at its real current distance to
            // target -- target is guaranteed non-null here since this is
            // inside justFired, which already required inRange this tick.
            const curve = HIT_CHANCE_CURVES[w.key as "laser" | "auto" | "missile"];
            const distanceM = Math.hypot(target!.x - player.x, target!.y - player.y);
            const hitChance = rangeHitChance(distanceM, w.range, curve);
            const damage = rollDamageWithHitChance(
              WEAPON_DPS[w.key] * multiplier * (w.cycleDurationMs / 1000),
              hitChance,
              Math.random
            );
            applyDamageToHostile(hostileTarget, damage);
            // Distinct fire visual per weapon type (Don's direction): a
            // laser is an instant beam flash, an auto-turret is a rapid
            // 3-round tracer burst, a missile is a slower homing
            // projectile that explodes on arrival -- spawned here (once
            // per completed cycle, same cadence the damage itself uses)
            // rather than trying to sub-divide the cycle into "real" shots.
            if (w.key === "laser") {
              spawnBeam("laser", "player", hostileTarget.id, now, 220);
            } else if (w.key === "auto") {
              for (let i = 0; i < 3; i++) {
                spawnBeam("auto", "player", hostileTarget.id, now, 160, i * 70);
              }
            } else if (w.key === "missile") {
              spawnProjectile("missile", player, hostileTarget, now, 650);
            }
            // Audio (16.69) -- spawned at the same real fire point as the
            // visual, so the two can never drift out of sync.
            playSound(w.key === "laser" ? "laser" : w.key === "auto" ? "auto" : "missile");
          }
        }
      }
    });

    selfModules.forEach((m) => {
      // Snapshot the target before ticking (item 4): a grace-period cycle
      // (deactivating=true) still fires its repair on completion before
      // actually going idle, and by then result.state.targetId has
      // already been cleared as part of that idle transition.
      const targetBeforeTick = m.cycle.targetId;
      const result = tickSelfModuleCycle(m.cycle, now, m.cycleDurationMs, SELF_MODULE_COOLDOWN_MS[m.key] ?? 0);
      m.cycle = result.state;
      applyModuleVisual(m.key, result.visual, result.progress);

      if (result.justFired && targetBeforeTick && applySelfModuleRepair(m, targetBeforeTick)) {
        // Shield/Hull repair pulse (Don's direction): a brief expanding
        // ring on the actual target the instant a repair tick lands --
        // reads as "a burst of healing energy," not a continuous glow,
        // since the repair itself is applied once per completed cycle.
        if (settings.showCombatFx) {
          effects.push({ kind: "pulse", weaponKey: m.key, targetId: targetBeforeTick, spawnedAt: now, durationMs: 500 });
        }
        playSound(m.key === "shield" ? "shieldRepair" : "hullRepair");
      }
    });

    // Hostile counter-fire (16.43, revised) -- illustrative cadence (see
    // file header), same real hit-chance/variance formula as the player's
    // own weapons. Only fires at whatever it's currently aggroed onto
    // (updateHostileAggro, driven by real pursuit/detection range now,
    // stepped in frame()) and only once actually within its own real
    // weaponRangeM of that target -- replaces the old unconditional-range,
    // random player-vs-escort split from when hostiles never moved at all.
    // Ship-vs-drone still uses 16.23's real DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT.
    hostiles.forEach((h) => {
      // Cloak (16.61): no hostile can attack the captain or fleet while
      // active -- the whole fire cycle simply pauses, resuming right
      // after Cloak ends rather than banking up a shot.
      if (cloakActive(now)) return;
      if (h.nextFireAt === null) {
        h.nextFireAt = now + HOSTILE_CYCLE_DURATION_MS;
      }
      if (now < h.nextFireAt) return;
      h.nextFireAt = now + HOSTILE_CYCLE_DURATION_MS;
      const targetPoint = hostileMoveTargetPoint(h);
      const distanceM = targetPoint ? Math.hypot(targetPoint.x - h.x, targetPoint.y - h.y) : Infinity;
      const inWeaponRange = !!h.aggroTargetId && distanceM <= h.weaponRangeM;
      if (!inWeaponRange) return;
      // Range-based hit chance (16.66), symmetric with the player's own
      // weapons (16.14: "no side is exempt") -- curve picked by bucketing
      // this hostile's real weaponRangeM onto the nearest turret band.
      const hitChance = rangeHitChance(distanceM, h.weaponRangeM, hitChanceCurveForRangeM(h.weaponRangeM));
      const damage = rollDamageWithHitChance(h.dps * (HOSTILE_CYCLE_DURATION_MS / 1000), hitChance, Math.random);
      const deployedDrones = drones.filter((d) => d.deployed && d.hp > 0);
      const targetsDrone = deployedDrones.length > 0 && Math.random() >= DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT;
      if (targetsDrone) {
        const chosen = deployedDrones[Math.floor(Math.random() * deployedDrones.length)];
        applyDamageToDrone(chosen, damage);
      } else {
        applyDamageToShipCandidate(h.aggroTargetId!, damage);
        // Incoming-fire visual (Don's direction: NPCs attacking us need an
        // animation too) -- a reddish tracer distinct from any of the
        // player's own outgoing weapon colors, so a hit reads as "coming
        // from them," not just more of the same beam.
        spawnBeam("hostile", h.id, h.aggroTargetId!, now, 200);
        playSound("hostileFire");
      }
    });

    // Fleet ships auto-attack whatever hostile they're aggroed onto
    // (16.43) -- same cadence/damage-roll convention as hostile
    // counter-fire above.
    //
    // 16.43's always-on passive shield/hull regen for escorts is GONE
    // (16.81): nothing heals for free any more, and an escort quietly
    // topping itself up every tick was the biggest free-healing path left
    // in the game. Escorts are repaired the same three ways the flagship
    // is -- the player's own Shield/Hull module retargeted onto them
    // (Section 4a already allows exactly that, and the Hull module bills
    // Ferrite for it), a Station drydock, or a Base repair bay.
    fleetShips.forEach((f) => {
      if (f.fled) return;
      if (!f.aggroTargetId) return;
      const target = hostiles.find((h) => h.id === f.aggroTargetId);
      if (!target) {
        f.aggroTargetId = null;
        return;
      }
      if (f.nextFireAt === null) f.nextFireAt = now + HOSTILE_CYCLE_DURATION_MS;
      if (now < f.nextFireAt) return;
      f.nextFireAt = now + HOSTILE_CYCLE_DURATION_MS;
      const fleetDistanceM = Math.hypot(target.x - f.x, target.y - f.y);
      if (fleetDistanceM > f.weaponRangeM) return;
      const fleetHitChance = rangeHitChance(fleetDistanceM, f.weaponRangeM, hitChanceCurveForRangeM(f.weaponRangeM));
      const damage = rollDamageWithHitChance(f.dps * (HOSTILE_CYCLE_DURATION_MS / 1000), fleetHitChance, Math.random);
      applyDamageToHostile(target, damage);
      spawnBeam("fleet", f.id, target.id, now, 200);
    });
  }

  // Target Bar (Section 4a/4b): one card per target that currently has at
  // least one weapon locked on it -- live distance, real HP%, and every
  // locked module's real IN RANGE/OUT OF RANGE status against it.
  function updateTargetBar(): void {
    const bar = wrap.querySelector<HTMLElement>("[data-target-bar]")!;
    const lockedIds = [...new Set(weapons.filter((w) => w.cycle.targetId).map((w) => w.cycle.targetId as string))];
    // 16.95: whatever is SELECTED appears here too, whether or not anything is
    // locked on it. Selection used to be visible only as a ring on the canvas,
    // so the captain could not see what they had picked without hunting for it
    // among the ships they were already fighting -- and the natural move,
    // "select this one, now fire that module at it", had no confirmation step
    // between the two halves.
    //
    // Appended rather than prepended so an existing engagement does not shuffle
    // sideways every time the captain looks at something else; a new selection
    // arrives at the end and stays put once a weapon locks it.
    const ids = [...lockedIds];
    if (selected && !ids.includes(selected.id)) ids.push(selected.id);
    bar.innerHTML = ids
      .map((id) => {
        const target = resolveNamedPoint(id);
        if (!target) return "";
        const distance = Math.hypot(target.x - player.x, target.y - player.y);
        const mods = weapons
          .filter((w) => w.cycle.targetId === id)
          .map((w) => ({ label: w.label, inRange: distance <= w.range }));
        const hostile = resolveHostile(id);
        const asteroid = resolveAsteroid(id);
        const hpPercent = hostile
          ? ((hostile.shield + hostile.hull) / (hostile.shieldMax + hostile.hullMax)) * 100
          : asteroid
            ? ((asteroid.poolM3 ?? 0) / (asteroid.poolMaxM3 ?? 1)) * 100
            : 100;
        // "More Info" only makes sense for an actual ship (hostile or
        // fleet ship) -- a planet, wreck or gate has no blueprint.
        const isShip = !!hostile || !!fleetShipById(id);
        return targetSlotHtml(target.name, `${Math.round(distance)}m`, hpPercent, mods.length > 0 ? mods : null, id === selected?.id, isShip ? id : null);
      })
      .join("");
    bar.querySelectorAll<HTMLButtonElement>("[data-bp-target]").forEach((btn) => {
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        const spec = blueprintSpecForTargetId(btn.dataset.bpTarget!);
        if (spec) openBlueprint(spec);
      });
    });
  }

  // Builds the Blueprint View spec for whatever is currently in a Target
  // Bar slot -- a hostile resolves through the same npcSubtypeKey the
  // Trophy Room/canvas silhouette already use, a fleet ship (or the
  // player's own hull, though it never appears as a target) through its
  // real role/hullTier, so the drawing can never disagree with the
  // silhouette already on screen for the same object.
  function blueprintSpecForTargetId(id: string): BlueprintSpec | null {
    const hostile = resolveHostile(id);
    if (hostile) {
      const key = npcSubtypeKey(hostile.name, hostile.faction);
      return {
        key,
        faction: hostile.faction,
        displayName: hostile.name,
        tier: hostile.hullTier,
        drawingNo: `SF-${key.slice(0, 3).toUpperCase()}-${hostile.hullTier}`,
        stats: [
          { label: "Hull HP", value: `${Math.round(hostile.hull)} / ${Math.round(hostile.hullMax)}` },
          { label: "Shield HP", value: `${Math.round(hostile.shield)} / ${Math.round(hostile.shieldMax)}` },
          { label: "DPS", value: hostile.dps.toFixed(1) }
        ]
      };
    }
    const fleetShip = fleetShipById(id);
    if (fleetShip) {
      return {
        key: fleetShip.role,
        faction: "Player",
        displayName: fleetShip.name,
        tier: fleetShip.hullTier,
        drawingNo: `SF-${fleetShip.role.slice(0, 3).toUpperCase()}-${fleetShip.hullTier}`,
        stats: [
          { label: "Hull HP", value: `${Math.round(fleetShip.hull)} / ${Math.round(fleetShip.hullMax)}` },
          { label: "Shield HP", value: `${Math.round(fleetShip.shield)} / ${Math.round(fleetShip.shieldMax)}` },
          { label: "Repair", value: fleetShip.repairPerS ? `${fleetShip.repairPerS.toFixed(1)}/s` : "none" },
          { label: "DPS", value: fleetShip.dps.toFixed(1) },
          { label: "Cargo", value: `${fleetShip.cargoM3.toLocaleString()} m³` }
        ]
      };
    }
    return null;
  }

  function updateHealthBars(): void {
    // Player row is always the first .xv-ship-row (fleet rows are
    // generated after it in the template) -- kept positional for this one
    // row since it's the only one that's always exactly one.
    const playerRow = wrap.querySelector<HTMLElement>(".xv-ship-row");
    if (playerRow) {
      const [shieldTrack, hullTrack] = playerRow.querySelectorAll<HTMLElement>(".xv-bar-fill");
      shieldTrack.style.width = `${(player.shield / player.shieldMax) * 100}%`;
      const hullFraction = player.hull / player.hullMax;
      hullTrack.style.width = `${hullFraction * 100}%`;
      // Loaner ships get a striped hull bar once inside the last band
      // before the crew walk (16.73) -- the mutiny is irreversible, so the
      // point of no return has to be visible before it's crossed, not
      // announced after.
      const atRisk = state.isLoanerShip && loanerHullAtRisk(hullFraction);
      hullTrack.className = `xv-bar-fill ${hullColorClass(hullFraction)}${atRisk ? " loaner-at-risk" : ""}`;
    }

    // One row per fleet ship (16.43), zipped by data-fleet-id rather than
    // the old fixed [playerRow, escortRow] position -- N rows now, not 2.
    fleetShips.forEach((f) => {
      const row = wrap.querySelector<HTMLElement>(`[data-fleet-row][data-fleet-id="${f.id}"]`);
      if (!row) return;
      const [shieldTrack, hullTrack] = row.querySelectorAll<HTMLElement>(".xv-bar-fill");
      if (f.fled) {
        shieldTrack.style.width = "0%";
        hullTrack.style.width = "0%";
      } else {
        shieldTrack.style.width = `${(f.shield / f.shieldMax) * 100}%`;
        hullTrack.style.width = `${(f.hull / f.hullMax) * 100}%`;
        hullTrack.className = `xv-bar-fill ${hullColorClass(f.hull / f.hullMax)}`;
      }
      const nameLine = row.querySelector<HTMLElement>(".xv-ship-name-line span");
      if (nameLine) nameLine.textContent = f.aggroTargetId ? "ENGAGING" : "NO TARGET";
    });

    if (hasDroneBay) {
      const nameLine = wrap.querySelector<HTMLElement>("[data-drone-row] .xv-ship-name-line b");
      const droneHpTrack = wrap.querySelector<HTMLElement>("[data-drone-hp]");
      const totalHp = drones.reduce((sum, d) => sum + d.hp, 0);
      const totalMaxHp = drones.reduce((sum, d) => sum + d.maxHp, 0);
      // Count drones IN THE BAY, not deployed ones (16.74). This read
      // backwards before: launching your drones showed the bay filling up.
      // The bay is a container -- drones out doing their job are not in it.
      // Denominator is the hull's real capacity, so a bay short of
      // replacements visibly reads as e.g. "2/10", not "2/2".
      const dockedCount = drones.filter((d) => !d.deployed).length;
      if (nameLine) nameLine.textContent = `Drone Bay (${dockedCount}/${droneBayCapacity(droneRole)})`;
      if (droneHpTrack) {
        const pct = totalMaxHp > 0 ? (totalHp / totalMaxHp) * 100 : 0;
        droneHpTrack.style.width = `${pct}%`;
        droneHpTrack.className = `xv-bar-fill ${hullColorClass(totalMaxHp > 0 ? totalHp / totalMaxHp : 0)}`;
      }
    }

    // Docked rack's "minimum truths" HP ratios (Section 4a) -- initially a
    // placeholder (initialStatText) since it's rendered before this real
    // player data exists; kept live here instead.
    const shieldStat = wrap.querySelector(".xv-mod-type-shield .xv-mod-stat");
    if (shieldStat) shieldStat.textContent = `${Math.round(player.shield)}/${player.shieldMax}`;
    const hullStat = wrap.querySelector(".xv-mod-type-hull .xv-mod-stat");
    if (hullStat) hullStat.textContent = `${Math.round(player.hull)}/${player.hullMax}`;
  }

  // Section 10's hull-bar percentage thresholds (ui-tokens.css's
  // --sf-hull-color-* values).
  function hullColorClass(pct: number): string {
    if (pct <= 0.1) return "hull-flee";
    if (pct <= 0.3) return "hull-critical";
    if (pct <= 0.6) return "hull-caution";
    return "hull-healthy";
  }

  /**
   * Provisions in the Fleet Health Box (16.104, Don's direction).
   *
   * 16.103 flagged the gap: Fuel had just gained a readout on the Engine module,
   * but Provisions appeared nowhere in the Expedition View at all -- and running
   * out of them is the *harsher* of the two consequences. A stranded ship waits
   * for a rescue; a starving crew mutinies (16.62). The captain was flying with
   * no sight of the countdown.
   *
   * It sits in the Fleet Health Box because that box is already the answer to
   * "how is my ship doing", and a crew with six hours of food left is exactly
   * that. Shown as TIME rather than as a quantity: 54.5 provisions means nothing
   * on its own, and "23 h" is the same number in the unit the decision is made in.
   *
   * Hidden entirely on a Loaner Ship, which carries no Provisions pool of its own
   * (16.63) -- a zero there would read as starvation rather than as absence.
   */
  function updateProvisionsLine(): void {
    const line = wrap.querySelector<HTMLElement>("[data-provisions-line]");
    if (!line) return;
    if (!state.currentShip || state.isLoanerShip) {
      line.textContent = "";
      return;
    }
    const prov = provisionsSummary(state.currentShip.crew, state.provisionsOnBoard, state.provisionsCapacity);
    line.textContent = provisionsLineText();
    // Zero provisions is not "low", it is the mutiny countdown already running --
    // a different state, and it must not look like the same warning. One rule,
    // read by both the first paint and every frame after it.
    const cls = provisionsLineClass();
    line.classList.toggle("low", cls === "low");
    line.classList.toggle("empty", cls === "empty");
    line.title = `${prov.perHour.toFixed(1)} provisions/hour for ${prov.crew} crew · ${prov.onBoard.toFixed(1)} of ${prov.capacity.toFixed(1)} aboard`;
  }

  function updateReadouts(): void {
    const speedLine = wrap.querySelector(".xv-speed-line")!;
    const headingDeg = Math.round((((player.heading * 180) / Math.PI) + 360) % 360);
    speedLine.textContent = `Speed: ${Math.round(player.speed)} m/s   |   Heading: ${headingDeg}°`;
    updateProvisionsLine();

    const engineStat = wrap.querySelector(".xv-mod-type-engine .xv-mod-stat");
    if (engineStat) {
      engineStat.textContent = `${Math.round(player.speed)} m/s`;
    }

    // Solid green ring whenever the ship actually has speed, independent
    // of moduleOff/activeMoveMode -- e.g. still coasting to a stop right
    // after Move To/Orbit/cruise was cancelled shows this the same as
    // while actively under engine power.
    moduleCircleEls.get("engine")?.circle.classList.toggle("moving", player.speed > 0.5);
  }

  // Total sequence duration once "exiting" begins, ported from the
  // mockup's stepFlee (a fixed wall-clock window for the ship to visibly
  // fly out of frame before the new Location loads).
  const FLEE_EXIT_DURATION_S = 2.5;

  function completeFlee(): void {
    fleeing = false;
    cameraLocked = true;
    const overlay = wrap.querySelector<HTMLElement>("[data-depart-overlay]")!;
    const text = wrap.querySelector<HTMLElement>("[data-depart-text]")!;
    text.textContent = fleeDestName ? `Arrived at ${fleeDestName} — Fast Travel complete.` : "Disengaged safely — Fast Travel complete.";
    setTimeout(() => overlay.classList.remove("show"), 1600);

    // A real WARP (jumpToSystemId set) actually changes state.currentSystemId
    // and persists it, re-deriving the belt's resource/pool from the new
    // real System -- an ordinary same-System Fast Travel leaves all of
    // this untouched, since it's still the same System.
    if (fleeJumpToSystemId) {
      const next = getSystem(fleeJumpToSystemId);
      if (next) {
        // Hidden-system discovery bonus (16.67): a one-time credit reward
        // the very first time this captain ever reaches one of the 10
        // hiddenUntilScanned systems -- checked before markSystemVisited
        // below records it as visited, since that's the only way to tell
        // "first time" from "already been here."
        if (isHiddenSystem(next.id) && !state.visitedSystemIds.includes(next.id)) {
          const bonus = hiddenSystemDiscoveryBonusCr(next.dangerTier);
          state.credits += bonus;
          showBanner(`First to reach ${next.name} -- discovery bonus: ${bonus.toLocaleString()} CR`);
          // 16.82: and the reason this system was worth hiding. Held back
          // until the arrival beat has cleared, so the captain isn't
          // reading through a black screen.
          setTimeout(() => showDiscoveryLore(next.id), 2200);
        }
        currentSystem = next;
        state.currentSystemId = next.id;
        markSystemVisited(next.id);
        asteroidResource = next.resources[0] ?? "Ferrite Ore";
        asteroidPoolMaxM3 =
          nodePoolM3(next.dangerTier, isHiddenSystem(next.id) ? "Exceptional" : "Standard") *
          dangerTierRichnessMultiplier(next.dangerTier);
        triggerSave(ctx);
      }
      fleeJumpToSystemId = null;
    }

    // On arrival, Shield/Hull switch off and both weapon locks clear
    // (Section 4b: the previous Location's targets no longer exist), and
    // Move To/Orbit go idle for the same reason.
    player.x = 0;
    player.y = 0;
    player.heading = -Math.PI / 2;
    player.speed = 0;
    // Fleet ship HP/fled status is NOT reset here -- a ship that fled has
    // left the mission (Section 10), not just stepped out of frame; a
    // still-alive ship's damage persists the same way the player ship's
    // own does. Position/aggro resets so nothing is left stranded
    // off-camera aggroed onto a hostile that no longer exists in the new
    // Location.
    fleetShips.forEach((f, i) => {
      f.x = -500 - i * 300;
      f.y = 600;
      f.heading = 0;
      f.speed = 0;
      f.aggroTargetId = null;
      f.nextFireAt = null;
    });
    selected = null;
    activeMoveMode = null;
    moveTarget = null;
    orbit = null;
    braking = false;
    pendingFastTravelLocationId = null;
    // A real bug, not a design gap: an in-progress ambient-contact warning
    // (Section 8's staged sequence) previously survived Fast Travel --
    // fleeing paused it (updateAmbientContact only runs while !fleeing)
    // but never cancelled it, so it just resumed on arrival and could
    // still spawn hostiles in the new Location/System. Fleeing is
    // supposed to be a real escape from exactly this ("fleeing always
    // feels viable," Section 5) -- a successful Fast Travel now always
    // cancels any pending warning outright.
    ambientStage = 0;
    ambientNextAt = null;
    weapons.forEach((w) => {
      w.cycle = IDLE_WEAPON;
    });
    selfModules.forEach((m) => {
      m.cycle = IDLE_SELF_MODULE;
    });
    // Lands in the real destination Location (rebuildLocationScene
    // regenerates fixedObjects/hostiles to match whatever that Location
    // actually is -- a fresh scene either way, matching 16.25's "must
    // node-hop" model, but now scoped to a real Location identity rather
    // than always the same single shared scene).
    rebuildLocationScene(fleeTargetLocationId);
    // Surviving drones redeploy fresh at the new Location (their HP still
    // persists, same as the player/escort) -- destroyed ones are gone for
    // good, same "permanent loss" rule as everything else here.
    drones.forEach((d) => {
      d.deployed = true;
      d.nextFireAt = null;
    });
    droneMode = "auto";
    droneTargetId = null;
    updateDroneModeButton();
    const recallBtn = wrap.querySelector<HTMLButtonElement>("[data-drone-recall]");
    if (recallBtn) recallBtn.textContent = "RECALL";
    renderScannerList();
    updateEngineButtons();

    playArrivalBeat();
  }

  function stepFleeSequence(dt: number): void {
    const overlay = wrap.querySelector<HTMLElement>("[data-depart-text]")!;
    const result = stepFlee(player, dt, fleeStage, fleeHeadingTarget, player.topSpeed);
    Object.assign(player, result.ship);

    if (result.stage !== fleeStage) {
      fleeStage = result.stage;
      if (fleeStage === "accelerating") {
        overlay.textContent = "Accelerating to top speed…";
      }
    }
    if (result.justReachedTopSpeed) {
      cameraLocked = false;
      frozenCam = { x: player.x, y: player.y };
      overlay.textContent = "Departing…";
    }

    if (fleeStage === "exiting") {
      fleeElapsedS += dt;
      if (fleeElapsedS > FLEE_EXIT_DURATION_S) {
        completeFlee();
      }
    }
  }

  // Real bug fix, not a style choice: renderScannerList() replaces
  // [data-sc-list]'s entire innerHTML, destroying and recreating every
  // row element. Doing that on every single animation frame (60/s) meant
  // a real human mouse click (mousedown...mouseup spans far more than one
  // frame) very often had its target row removed from the document
  // between press and release -- browsers don't fire "click" when that
  // happens, so Scanner UI selection appeared to silently do nothing.
  // Synthetic .click() calls (used during earlier testing) fire a single
  // event with no press/release lifecycle, so they never exposed this.
  // Refreshing "live" distances a few times a second instead of 60 is
  // imperceptible to a human reading a distance readout, so this trades
  // nothing real away.
  let nextScannerRefreshAt = 0;
  const SCANNER_REFRESH_INTERVAL_MS = 350;

  let lastT = performance.now();
  // 16.82: the discovery reveal. Marks the system's paragraph as seen the
  // moment it's shown (not when dismissed) so a captain who navigates away
  // mid-read doesn't get it again on the next arrival -- it's permanently
  // in the Trophy Room by then either way.
  function showDiscoveryLore(systemId: string): void {
    const lore = loreForSystem(systemId);
    if (!lore || !wrap.isConnected) return;
    if (!state.loreSeenSystemIds.includes(systemId)) {
      state.loreSeenSystemIds.push(systemId);
      triggerSave(ctx);
    }
    const overlay = wrap.querySelector<HTMLElement>("[data-discovery]");
    if (!overlay) return;
    wrap.querySelector<HTMLElement>("[data-discovery-eyebrow]")!.textContent =
      `${lore.name} — mystery ${state.loreSeenSystemIds.length} of ${HIDDEN_SYSTEM_LORE.length}`;
    wrap.querySelector<HTMLElement>("[data-discovery-title]")!.textContent = lore.title;
    wrap.querySelector<HTMLElement>("[data-discovery-body]")!.textContent = lore.body;
    overlay.classList.add("show");
  }

  wrap.querySelector<HTMLElement>("[data-discovery-close]")?.addEventListener("click", () => {
    wrap.querySelector<HTMLElement>("[data-discovery]")?.classList.remove("show");
  });

  // 16.82: every hostile currently in the scene counts as met. Swept from
  // the live array once per frame rather than hooked into each of the four
  // separate spawn paths (wave, ambient, guardian, planetary-defense
  // retarget) -- one sweep can't miss a path, and a future fifth spawn
  // path gets recorded for free. `sightedHostileIds` dedupes per hostile,
  // so one ship is one sighting no matter how many frames it survives.
  const sightedHostileIds = new Set<string>();
  function noteHostileSightings(): void {
    hostiles.forEach((h) => {
      if (sightedHostileIds.has(h.id)) return;
      sightedHostileIds.add(h.id);
      recordSighting(state.bestiary, h.name, h.hullTier);
    });
  }

  // 16.81: the Expedition View works in live HP, everything persisted
  // works in damage taken -- this is the single place the two meet. A
  // ship that fled is deliberately still recorded: it left the fight with
  // real damage and shouldn't arrive home mysteriously whole.
  function persistShipDamage(): void {
    recordCurrentHp(state.shipDamage, state.currentShipInstanceId, player);
    fleetShips.forEach((f) => recordCurrentHp(state.shipDamage, f.id, f));
  }

  function frame(now: number): void {
    if (!wrap.isConnected) {
      return; // screen navigated away -- stop the loop rather than leak it
    }
    const dt = Math.min(0.1, (now - lastT) / 1000);
    lastT = now;

    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;

    if (fleeing) {
      stepFleeSequence(dt);
    } else if (braking) {
      Object.assign(player, stepBrake(player, dt, player.topSpeed));
      if (player.speed <= 0) {
        braking = false;
      }
    } else if (activeMoveMode === "cruise") {
      Object.assign(player, stepCruise(player, dt, player.topSpeed));
    } else {
      if (orbit) {
        orbit = stepOrbitPoint(orbit, dt, player.topSpeed);
      }
      const target = resolveMoveTarget();
      // 16.101: stop at a solid object's real surface, not a flat 150m. The
      // separation pass will not let the ship inside SHIP_TO_ASTEROID_CLEARANCE_M
      // anyway, so approaching a rock with the old flat figure meant arriving,
      // being shoved back out, and re-approaching forever. Stations, planets and
      // wrecks are not solid (Don) and keep the flat distance, which is what
      // docking and looting are already tuned against.
      const playerStopDistance = orbit ? 0 : stopDistanceFor(moveTarget);
      Object.assign(player, stepTowardTarget(player, dt, playerStopDistance, player.topSpeed, target));
    }
    // 16.43: real pursuit AI, aggro-driven -- both hostiles and non-flown
    // fleet ships pick their move target off whatever they're currently
    // aggroed onto (updateHostileAggro/updateFleetShipAggro, based on real
    // detection range), rather than the old escort's single hardcoded
    // "always chase the player" line. Movement continues even while
    // fleeing (matching the old escort's own unconditional step) --
    // combat itself pauses during the flee sequence via updateModules's
    // own `if (!fleeing)` gate below.
    updateHostileAggro();
    updateFleetShipAggro();
    fleetShips.forEach((f) => {
      if (f.fled) return;
      const moveTarget = fleetShipMoveTargetPoint(f);
      const stopDist = f.aggroTargetId ? f.weaponRangeM * 0.8 : 400;
      Object.assign(f, stepTowardTarget(f, dt, stopDist, f.topSpeed, moveTarget));
      const point = fleetShipPoints.find((p) => p.id === f.id)!;
      point.x = f.x;
      point.y = f.y;
    });
    hostiles.forEach((h) => {
      const moveTarget = hostileMoveTargetPoint(h);
      const stopDist = h.weaponRangeM * 0.8;
      Object.assign(h, stepTowardTarget(h, dt, stopDist, h.topSpeed, moveTarget));
    });

    // Nothing flies through anything (16.100, Don). Runs after every body has
    // moved, so pursuit, orbiting and stop distances are untouched -- this only
    // un-overlaps what actually intersected this frame. Ships and asteroids
    // only: stations, planets and wrecks read as background a ship passes over,
    // and wrecks may overlap each other, per Don.
    separateBodies();

    // Tractor Beam (16.61): steadily pulls every `tractored` wreck toward
    // the player until it's within loot range, then stops pulling it (the
    // flag clears) -- a real QoL convenience, not a targeting/aggro
    // mechanic, so it runs unconditionally, even while fleeing.
    fixedObjects.forEach((f) => {
      if (f.kind !== "wreck" || !f.tractored) return;
      const dist = Math.hypot(player.x - f.x, player.y - f.y);
      if (dist <= player.stopDistance) {
        f.tractored = false;
        return;
      }
      const step = Math.min(dist, TRACTOR_BEAM_SPEED_MPS * dt);
      f.x += ((player.x - f.x) / dist) * step;
      f.y += ((player.y - f.y) / dist) * step;
    });

    // Marker cleanup on arrival (per Don's direction) -- a dropped
    // marker is scratch scaffolding for a one-off Move To, not a
    // permanent object; once the ship actually reaches it there's
    // nothing left to look at, so it's removed rather than sitting on
    // the viewport/mini-map forever.
    if (!fleeing && marker && moveTarget?.id === "marker" && activeMoveMode === "moveto") {
      if (Math.hypot(marker.x - player.x, marker.y - player.y) <= player.stopDistance) {
        marker = null;
        selected = null;
        moveTarget = null;
        activeMoveMode = null;
        updateEngineButtons();
      }
    }

    if (!fleeing) {
      updateModules(now, dt);
      updateTargetBar();
      updateHealthBars();
      updateExpandedStats();
      // Throttled, not per-frame -- see nextScannerRefreshAt's comment.
      if (now >= nextScannerRefreshAt) {
        renderScannerList(); // live distances (Section 4b: "refreshed live")
        nextScannerRefreshAt = now + SCANNER_REFRESH_INTERVAL_MS;
        const beforeMarkup = state.distressSignal.markup;
        state.distressSignal = tickDistressSignal(state.distressSignal, effectiveNow(state.clock, Date.now()));
        if (state.distressSignal.markup !== null && beforeMarkup === null) {
          showBanner("A rescuer answers your Distress Signal.");
        }
        renderDistressPanel();
      }
      updateEngineButtons(); // Loot's in-range gate changes continuously as the ship moves
      checkAutoEngage();
    }

    // Runs even while fleeing -- a failed save shouldn't be swallowed just
    // because it landed mid-Fast-Travel.
    drainGlobalEvent();

    // Mirror live HP back into the persistent damage record (16.81) every
    // frame, rather than only on exit: the captain can leave this screen
    // by a dozen different paths (nav icons, Dock, mutiny, the Escape Pod,
    // closing the tab), and any one of them missing a save-on-exit hook
    // would silently hand back a free repair. Writing two numbers per ship
    // per frame is far cheaper than auditing every exit. This only updates
    // in-memory state -- the real save is still driven by triggerSave.
    persistShipDamage();
    persistCombatSnapshot(now);
    noteHostileSightings();

    // Advance/cull combat-visual effects (particles carry real velocity;
    // beam/projectile/pulse resolve their live endpoints in draw() itself,
    // nothing to step here beyond expiry).
    effects.forEach((e) => {
      if (e.kind === "particle" && e.x !== undefined && e.y !== undefined) {
        e.x += (e.vx ?? 0) * dt;
        e.y += (e.vy ?? 0) * dt;
      }
    });
    effects = effects.filter((e) => now - e.spawnedAt < e.durationMs);

    draw();
    drawMiniMap(minimapCanvas, false);
    if (minimapFull.classList.contains("show")) {
      drawMiniMap(minimapFullCanvas, true);
    }
    updateReadouts();
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
  playArrivalBeat();
}

// Real per-System Location roster (Don's direction, superseding the
// earlier single-scene-per-System model): "a System should contain
// multiple Locations... we have to travel to the right Location in a
// System, and then Move To the correct Gate, and then activate the Gate
// with a new WARP button." No per-system Location list exists anywhere
// in the real game data (Section 6 only gives qualitative rules -- every
// System has >=1 belt, exactly one baseline buildable planet, a Station
// only on hub-tier Systems, outbound Jump Gates = that System's own
// `connections`) -- this generates a deterministic, always-the-same-for-
// a-given-System list from those rules: one Location per real planet
// (system.planets, 1-3 -- Section 6, 16.35), one Belt, one Station (if
// hasStation), one Mining/one Combat encounter Location (illustrative,
// flavored with the System's real resources), and one dedicated Gate
// Location per real connection. Each real Jump Gate now lives ONLY in its
// own Location -- it no longer also appears as a physical object in every
// other Location the way the single-scene model put it, which is exactly
// the double-listing Don flagged.
export type LocationKind =
  | "planet"
  | "belt"
  | "station"
  | "mining"
  | "combat"
  | "gate"
  | "missionCombat"
  | "missionMining";

export interface LocationDef {
  id: string;
  name: string;
  kind: LocationKind;
  jumpToSystemId?: string; // gate kind only
  missionId?: string; // missionCombat/missionMining kind only
  planetId?: string; // planet kind only -- which of system.planets this is
}

export function buildSystemLocations(system: StarSystem): LocationDef[] {
  const locations: LocationDef[] = system.planets.map((planet) => ({
    id: planet.id,
    name: planet.name,
    kind: "planet" as const,
    planetId: planet.id
  }));
  // Same reasoning as the gates (16.95): "Outer Belt" is unambiguous inside
  // the system it belongs to.
  locations.push({ id: "belt", name: "Outer Belt", kind: "belt" });
  if (system.hasStation) {
    locations.push({ id: "station", name: `${system.name} Station`, kind: "station" });
  }
  const flavorResource = system.resources[0] ?? "Ore";
  locations.push(
    { id: "mining", name: `Mining Contract — ${flavorResource}`, kind: "mining" },
    { id: "combat", name: "Bounty — Target Sighted", kind: "combat" }
  );

  // Real per-mission Locations (Don's direction: "a Mission will create a
  // new Location in that system which shows up when we arrive so we can
  // go and perform the mission requirements") -- purely derived from
  // state.acceptedMissions on every call, not stored separately, so a
  // completed/abandoned mission's Location simply stops appearing the
  // next time this is computed. Only Combat/Mining get one: Hauling/VIP
  // already resolve on plain System arrival (16.34), no Location needed.
  const missionEncounters = loadEncounters();
  state.acceptedMissions.forEach((m) => {
    if (m.type === "Combat" && m.systemId === system.id) {
      const encounterName = missionEncounters.find((e) => e.id === m.encounterId)?.name ?? m.encounterId;
      locations.push({ id: `mission-${m.id}`, name: `Mission: ${encounterName}`, kind: "missionCombat", missionId: m.id });
    } else if (m.type === "Mining" && m.systemId === system.id) {
      locations.push({ id: `mission-${m.id}`, name: `Mission: ${m.resource} Deposit`, kind: "missionMining", missionId: m.id });
    }
  });

  system.connections.forEach((destId) => {
    const dest = getSystem(destId);
    locations.push({
      id: `gate-${destId}`,
      // 16.95: no current-system prefix. Every gate in the list is in the
      // system the captain is standing in, so repeating its name on all of
      // them spent the widest part of each row on the one fact already known,
      // and pushed the DESTINATION -- the only thing that differs -- to the
      // right where it wrapped. The system name now appears once, in the
      // section header.
      name: `Gate — ${dest?.name ?? destId}`,
      kind: "gate",
      jumpToSystemId: destId
    });
  });
  return locations;
}

function locationMeta(kind: LocationKind): string {
  switch (kind) {
    case "planet":
      return "Planet";
    case "belt":
      return "Asteroid Belt";
    case "station":
      return "Faction Station";
    case "mining":
      return "Mining Encounter";
    case "combat":
      return "Combat Encounter";
    case "gate":
      return "Jump Gate";
    case "missionCombat":
      return "Mission — Combat";
    case "missionMining":
      return "Mission — Mining";
  }
}

interface ScanRow {
  name: string;
  meta: string;
  unknown?: boolean;
  system?: boolean; // Fast Travel (System-scope) entries get an accent border
  distanceValue: number;
  locationId?: string; // set only for Fast Travel rows -- which Location this leads to
  objId?: string; // set only for "In This Location" rows -- the real fixedObjects id to select
}

// "Fast Travel -- This System" -- every OTHER real Location in the
// current System (the one you're standing in is never listed -- you're
// already there). Illustrative AU spacing only (Section 2a: "not a real
// measured quantity").
function buildFastTravelRows(system: StarSystem, currentLocationId: string): ScanRow[] {
  return buildSystemLocations(system)
    .filter((loc) => loc.id !== currentLocationId)
    .map((loc, i) => ({
      name: loc.name,
      meta: locationMeta(loc.kind),
      system: true,
      distanceValue: 1.1 + i * 1.3,
      locationId: loc.id
    }));
}

function scanItemHtml(row: ScanRow, metaText: string, isSelected = false, isWaypointNext = false): string {
  const classes = ["xv-sc-item"];
  if (row.unknown) classes.push("unknown");
  if (row.system) classes.push("system");
  if (row.objId) classes.push("selectable");
  if (isSelected) classes.push("selected");
  if (isWaypointNext) classes.push("waypoint-next");
  const nameAttr = row.system ? ` data-name="${row.name}"` : "";
  const locAttr = row.locationId ? ` data-location-id="${row.locationId}"` : "";
  const objAttr = row.objId ? ` data-obj-id="${row.objId}"` : "";
  return `
    <div class="${classes.join(" ")}"${nameAttr}${locAttr}${objAttr}>
      <span class="xv-sc-name">${row.name}</span>
      <span class="xv-sc-meta">${row.unknown ? row.meta : metaText}</span>
    </div>
  `;
}
